// HOW TO knowledge base - authored from the LSM 2026 training manual.
//
// Each entry below is a self-contained training topic for NJRC Legal
// Services Coordinators. The pattern is deliberately Q&A-style ("How do
// I X?") so staff can scan + drill in.
//
// Source attribution lives on each topic so a reader can trace back to
// the specific module / doc when they need the full underlying text.
//
// Adding a topic: just append a Topic object below. The list page +
// search index update automatically.

import type { Category, Topic } from "./howToTypes";

// ─── Categories ──────────────────────────────────────────────────────

// Color per category - drives the category icon tint AND the dot on
// each topic card (replaces the previous difficulty-coded dot). Picked
// to spread evenly around the hue wheel without leaving the bordeaux/
// slate palette family. Each color is a foreground tint at ~60% lift,
// designed to read clearly on the dark slate body but never compete
// with the cream text or the bordeaux hero.
export const CATEGORY_COLOR: Record<Category["id"], string> = {
  "getting-started":     "#D6BE7A", // cream-gold (the entry point)
  "legal-assessment":    "#7CC2FF", // sky blue (foundational doc)
  "mccs":                "#E2A658", // copper (NJ courts)
  "time-served":         "#5FB8AA", // teal (clock / passage of time)
  "court-letters":       "#9DA8FF", // periwinkle (paper / formal)
  "license-restoration": "#E27A4E", // burnt orange (driver / road)
  "social-security":     "#86C495", // sage (federal / stable)
  "birth-certificates":  "#E48BB6", // rose (life / birth)
  "state-id":            "#B89BFF", // lavender (official ID)
  "expungement":         "#7BD495", // emerald (clean slate)
  "operations":          "#B8B0C2", // warm slate (ops / neutral)
};

export const CATEGORIES: Category[] = [
  {
    id: "getting-started",
    title: "Getting Started",
    blurb: "What an LSC does, the weekly model, and the resources you'll use every day.",
    iconPath:
      "M12 2L2 7v10l10 5 10-5V7L12 2zM12 22V12M2 7l10 5 10-5",
  },
  {
    id: "legal-assessment",
    title: "Legal Assessment",
    blurb: "The foundational document - what's in it, how to build it, how to keep it current.",
    iconPath:
      "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM14 2v6h6M9 13h6M9 17h4",
  },
  {
    id: "mccs",
    title: "Municipal Court Case Search",
    blurb: "How to search NJ municipal-court records, read results, and capture them for the assessment.",
    iconPath:
      "M11 19a8 8 0 1 1 0-16 8 8 0 0 1 0 16zM21 21l-4.3-4.3",
  },
  {
    id: "time-served",
    title: "Time Served",
    blurb: "Use a participant's prior incarceration to convert outstanding municipal fines.",
    iconPath:
      "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 6v6l4 2",
  },
  {
    id: "court-letters",
    title: "Court Letters",
    blurb: "Time-served, warrant-recall, and MVC-correction letters - when, why, how, and the checklist.",
    iconPath:
      "M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM22 6l-10 7L2 6",
  },
  {
    id: "license-restoration",
    title: "License Restoration",
    blurb: "Driver license + non-driver ID + MVC surcharges. The longest, most labor-intensive workflow.",
    iconPath:
      "M3 7h18l-1.5 12.5a2 2 0 0 1-2 1.5h-11a2 2 0 0 1-2-1.5L3 7zM8 7V4a4 4 0 0 1 8 0v3",
  },
  {
    id: "social-security",
    title: "Social Security Cards",
    blurb: "Replacement and corrected SS cards - paper application, supporting docs, where to file.",
    iconPath:
      "M3 6h18v12H3zM7 10h2M7 14h6M16 10h1M16 14h1",
  },
  {
    id: "birth-certificates",
    title: "Birth Certificates",
    blurb: "NJ in-state, NYC, Puerto Rico, VitalChek, paper-only states, and 190+ international countries.",
    iconPath:
      "M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11",
  },
  {
    id: "state-id",
    title: "State ID & 6 Points",
    blurb: "First-time IDs, renewals, duplicates, change of address, and the 6-Point ID system.",
    iconPath:
      "M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6zM7 12h.01M11 12h6M7 16h10",
  },
  {
    id: "expungement",
    title: "Expungement",
    blurb: "Regular, Clean Slate, marijuana decrim - eligibility, timing, and where to refer.",
    iconPath:
      "M9 11l3 3 8-8M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11",
  },
  {
    id: "operations",
    title: "NJRC Operations",
    blurb: "Salesforce, model week, contacts, and other things that keep the wheels turning.",
    iconPath:
      "M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83",
  },
];

// ─── Topics ──────────────────────────────────────────────────────────

export const TOPICS: Topic[] = [
  // ─── GETTING STARTED ──────────────────────────────────────────────
  {
    id: "what-does-an-lsc-do",
    category: "getting-started",
    title: "What does a Legal Services Coordinator (LSC) do?",
    summary:
      "Prepare each participant's written Legal Assessment, then use it to navigate four common obstacles: NJ Municipal Courts, ID retrieval, Driver License restoration, and attorney referrals.",
    source: "LSM 2026 · Module 1",
    estimatedTime: "5 min read",
    difficulty: "easy",
    keywords: ["overview", "role", "responsibilities", "lsc", "what we do"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "The LSC's primary deliverable is the **written Legal Assessment** - the foundational document that tracks every legal barrier to a participant's successful reentry.",
          "**The Legal Assessment, as applicable, includes:** (1) a New Jersey Municipal Court case search; (2) a PROMIS/Gavel criminal history; (3) NJ DOC offender details; (4) county jail commitment verifications / inmate-finder results; (5) a driver-license / registration restoration-fee search; (6) an NJ MVC surcharge search; and (7) attorney referral information (e.g., regarding child support matters or restraining orders).",
          "**Four common obstacles** the LSC helps navigate: **NJ Municipal Courts** (warrant recalls, fine→time-served conversion, fine waivers, license-suspension recalls, marijuana-decrim expungements), **ID retrieval** (birth certificates, NJ State IDs, out-of-state IDs, Social Security cards - post-release only), **Driver License Restoration** (action plans built from restoration-fee + surcharge searches + License Restoration Requirements Reports), and **Attorney Referrals** (LSNJ, VLJ, or NJRC's pro bono pool).",
          "**NJRC currently has funding to pay** NJ MVC driver-license + registration restoration fees.",
          "**Social Security cards are post-release only.**",
          "Driver License Restoration is the **lengthiest, most labor-intensive** workflow; the LSC develops and carries out an action plan based on the searches.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "The Legal Services Coordinator prepares a **written Legal Assessment** to support NJRC's reentry services. As applicable, the Legal Assessment includes: **(1)** a New Jersey Municipal Court case search; **(2)** a PROMIS/Gavel criminal history; **(3)** NJ DOC offender details; **(4)** county jail commitment verifications / inmate-finder search results; **(5)** a driver license / registration restoration-fee search; **(6)** an NJ MVC surcharge search; and **(7)** attorney referral information (e.g., regarding child-support matters or restraining orders). The Legal Assessment is the LSC's primary tool for helping participants navigate the following common obstacles:",
      },
      {
        kind: "kv",
        heading: "Common LSC obstacles to remove",
        items: [
          {
            label: "New Jersey Municipal Courts",
            value:
              "Routine help with municipal-court matters: seeking warrant recalls, requesting conversion of fines to time served, pursuing fine waivers when appropriate, requesting driver's license suspension recalls, and assisting with Marijuana Decriminalization expungements.",
          },
          {
            label: "Identification retrieval",
            value:
              "Help collecting documents and obtaining identification - including birth certificates (New Jersey, out-of-state for most states, and international across multiple countries), New Jersey State IDs, out-of-state IDs (depending on the state and the availability of online services), and Social Security cards (**post-release only**).",
          },
          {
            label: "Driver License Restoration",
            value:
              "Restoring NJ driver's licenses (and registrations) can be lengthy and labor-intensive. LSCs develop and carry out action plans based on the restoration-fee search, surcharge search, and **License Restoration Requirements Reports** obtained from NJ MVC. NJRC currently has funding to pay NJ MVC driver-license and registration restoration fees.",
          },
          {
            label: "Attorney Referrals",
            value:
              "When a participant requires legal representation or legal advice, the LSC can refer to one of two primary pro bono legal-aid organizations - **Legal Services of New Jersey (LSNJ)** and **Volunteer Lawyers for Justice (VLJ)** - or to attorneys in **NJRC's pro bono attorney pool**, which is supported by NJRC's certification by the New Jersey Supreme Court as a pro bono provider under Court Rule 1:21-11.",
          },
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "The Legal Assessment is the heart of it all",
        text:
          "Every other LSC task - letter, search, ID order, restoration - funnels back into the Assessment. Keep it accurate and up-to-date and the rest of the workflow follows naturally.",
      },
      {
        kind: "links",
        heading: "Read next",
        items: [
          { label: "What's in the Legal Assessment", href: "/how-to/whats-in-the-legal-assessment", description: "The four starting pages and what you add as the case progresses" },
          { label: "How do I run an MCCS search", href: "/how-to/run-an-mccs-search", description: "The first search you run on every new Participant" },
          { label: "How do I write a court letter", href: "/how-to/when-to-write-a-court-letter", description: "Time served / warrant recall / MVC correction" },
          { label: "When do I refer to LSNJ vs. VLJ vs. Public Defender vs. our Pro Bono Pool?", href: "/how-to/referrals-attorney-pool" },
        ],
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/lsc-process-cheat-sheet.png",
        caption: "**LSC Process Cheat Sheet** (LSM 2026 / LSC New Hire Packet, rev 062722) - the one-page summary of every search, every form, and every decision-point an LSC owns. Print it and pin it.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/njrc-legal-services-bullets.png",
        caption: "**NJRC Legal Services bullets** (rev 06.18.25) - the bullet-form summary of the LSC's portfolio, used in onboarding briefs and external decks.",
      },
    ],
  },

  {
    id: "the-njrc-program-flow",
    category: "getting-started",
    title: "What's the full NJRC program flow, end-to-end?",
    summary:
      "Participants go through a 4-day orientation, then engage with five NJRC staff roles (Intake, Case Manager, Social Worker, Employment Specialist, LSC) toward stable employment. The LSC's work spans months, well past Orientation Week.",
    source: "LSM 2026 · Module 1 + Module 0",
    estimatedTime: "5 min read",
    difficulty: "easy",
    keywords: ["overview", "intake", "orientation", "case manager", "social worker", "flow", "njrc"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**NJRC's mission:** remove barriers to employment for people returning from prison or jail, individuals leaving addiction treatment, and veterans returning from combat.",
          "**To receive NJRC's full range of services, participants must:** (1) attend a **four-day orientation**, (2) remain engaged with NJRC staff (Intake, Case Manager, LSC, Employment Specialist, Social Worker), and (3) comply with NJRC guidelines.",
          "**Five staff roles** each participant interacts with - Intake, Case Manager, Social Worker, Employment Specialist, and **the LSC**.",
          "**The LSC's work is ongoing** - typically months long, well past Orientation Week.",
          "**NJRC's four program-level goals:** (i) increase employability + earnings via training + credentials, (ii) reduce employment barriers for the target population, (iii) enhance social/emotional well-being via mentoring + peer support + community engagement, (iv) improve public safety by reducing recidivism + unemployment.",
          "**Beyond eight reentry facilities,** NJRC operates two facilities in Kearny: a NJ State Parole Board-contracted Community Resource Center, and the **Training Center** (HVAC, Electrical, Construction, Forklift, Solar, CCNA, Culinary, Welding, Peer Recovery, Phlebotomy, plus life-skills classes).",
        ],
      },
      {
        kind: "paragraph",
        text:
          "NJRC is a nonprofit with a **social mission: remove barriers to employment** for people returning from prison or jail, individuals leaving addiction treatment, and veterans returning from combat. NJRC delivers comprehensive, wraparound services through: **(i)** individualized assessments and treatment plans addressing critical needs; **(ii)** connections to mental and medical health care, legal services (birth certificates, identification, driver's licenses, pro bono legal referrals), and access to health insurance and other public benefits upon release; **(iii)** education, training, and career/employment services (work-based learning, apprenticeship programs, industry-recognized credentials); and **(iv)** a strong statewide network of training vendors, service providers, employers, community organizations, and government partners.",
      },
      {
        kind: "paragraph",
        text:
          "**To receive NJRC's full range of services and benefits, participants must:** **(1)** attend a **four-day orientation**; **(2)** remain engaged with NJRC staff - including an Intake Coordinator, Case Manager, Legal Services Coordinator, Employment Specialist, and Social Worker; and **(3)** comply with NJRC guidelines.",
      },
      {
        kind: "steps",
        heading: "The five staff roles + what each owns",
        items: [
          { title: "Intake Coordinator", body: "**First point of contact** at an NJRC reentry facility. Explains the intake process, helps the participant complete intake paperwork, creates a Salesforce profile (entering intake info and uploading available documents - IDs, medical history, custodial release info), schedules appointments with other facility team members, and manages telephone-based and walk-in inquiries." },
          { title: "Case Manager", body: "Applies for and secures benefits (Food Stamps, cash assistance, health insurance), provides sober-living referrals, assists with voter registration. Also helps participants obtain or replace key ID documents (Social Security card, birth certificate, county/municipal ID) and provides ongoing case management - including check-in calls **at least monthly**." },
          { title: "Social Worker", body: "Conducts a **biopsychosocial assessment (BSP)** to develop an **individualized service plan (ISP)**. The ISP may include housing, addiction treatment, and/or mental health care. Many NJRC participants have a history of alcohol or opioid addiction; others have co-occurring mental health conditions. Social Workers follow an **18-month treatment framework** that incorporates the BSP and supports services across the continuum of care (detox, residential treatment, intensive outpatient, induction + maintenance of medication-assisted treatment (MAT), harm reduction). Through CBT and counseling, NJRC provides trauma-informed behavioral health care and psychotherapy." },
          { title: "Employment Specialist", body: "Builds an **Individualized Employment Plan (IEP)**. Three categories of work: **(1) assessing + strengthening employability** (résumé, interview prep, navigating a criminal record, identifying background-friendly employers); **(2) connecting participants with employment** (Honest Jobs profiles, job applications, direct connections to employers like Sansone Auto Group and Wakefern Food Corp., job fairs, temp agencies); **(3) tracking + supporting outcomes** - 30/60/90-day, 6-month, and 1-year employment checks; collection of pay stubs; bus tickets through the first paycheck. Support continues through preparation, search, secured employment, and at least one year into the job. If a participant loses a position, they can return to develop a new plan." },
          { title: "Legal Services Coordinator (you)", body: "Prepares the written Legal Assessment (MCCS, PROMIS/Gavel, DOC, county jail, restoration-fee + surcharge searches, attorney referrals). Helps with municipal-court matters, ID retrieval, driver-license restoration, and attorney referrals. See [[what-does-an-lsc-do]]." },
        ],
      },
      {
        kind: "paragraph",
        text:
          "**Beyond NJRC's eight reentry facilities, NJRC operates two facilities in Kearny:** a New Jersey State Parole Board–contracted **Community Resource Center**, and the **Training Center**. The Training Center is a self-contained, multidisciplinary, multipurpose facility that also provides wellness services, dental screenings, medical screenings, computer facilities, medication-assisted treatment, and veterans' assistance. NJRC provides van transportation to and from the Training Center from anywhere in the North region, so participants can access post-release skill-building.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/program-flow-chart.png",
        caption: "**The official NJRC Program Flow Chart** (LSM 2026 / Module 0). Maps every staff role across Orientation Week → Follow-Up / Ongoing → Successful Employment, including the LSC's lane and how it intersects with the Case Manager, Social Worker, and Employment Specialist tracks. Print-out-and-pin-to-your-wall material.",
      },
      {
        kind: "kv",
        heading: "Training Center courses",
        items: [
          { label: "HVAC I & II", value: "Includes OSHA30 + EPA. Certificate of Completion from National Career Institute." },
          { label: "Electrical Assistant", value: "Includes OSHA30. Certificate from NCI." },
          { label: "Construction", value: "OSHA30, Flagger, Scaffold, Fall Prevention, Drug & Alcohol Awareness." },
          { label: "Forklift", value: "General Industry OSHA30, Forklift Operator, Scissor Lift Operator, Lockout/Tagout." },
          { label: "Solar Panel Installation", value: "Solar PV Installer + Green Construction." },
          { label: "CISCO", value: "Cisco Certified Network Associate (CCNA)." },
          { label: "Culinary Arts", value: "Hot Food Proficiency Certificate from Hudson County Community College + ServSafe." },
          { label: "Welding", value: "AWS Certification - Structural Steel." },
          { label: "Peer Recovery", value: "Ethical Considerations, Recovery Coach Academy, CCAR." },
          { label: "Phlebotomy", value: "Phlebotomy Technician Certification from HCCC." },
          { label: "Life-skill classes", value: "Google Digital Literacy · Financial Literacy through M&T Bank and TD Bank · Introduction to Computers through JPMorgan Chase · Workforce Readiness through USDOL and 1 Huddle." },
        ],
      },
      {
        kind: "kv",
        heading: "NJRC's four goals (program-level)",
        items: [
          { label: "(i) Earnings", value: "Increase employability and earnings potential through occupational skills training, industry-recognized credentials, and work experience in critical-skill and in-demand industries." },
          { label: "(ii) Barriers", value: "Reduce barriers to stable employment faced by the target population." },
          { label: "(iii) Well-being", value: "Enhance social and emotional well-being through mentoring, peer support services, and community engagement." },
          { label: "(iv) Public safety", value: "Improve public safety by reducing recidivism and unemployment." },
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "The LSC's work is ongoing",
        text:
          "Unlike Orientation Week activities, your work for a participant typically spans months - court letters wait for responses, license restoration depends on surcharge payments over time, expungement timelines are measured in years.",
      },
    ],
  },

  {
    id: "the-lsc-model-week",
    category: "getting-started",
    title: "What does an LSC's typical week look like?",
    summary:
      "Monday Legal Services Presentation, Tuesday-Wednesday assembly, Thursday participant meetings, Friday weekly report. Court letters, expungements, LSPM service requests, and notarized BC applications run as a daily background task every weekday.",
    source: "LSM 2026 · Module 1 (LSC Model Week)",
    estimatedTime: "3 min read",
    difficulty: "easy",
    keywords: ["weekly", "cadence", "model week", "schedule", "routine"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**Daily background tasks** - every weekday includes Municipal Court Letters, Expungements, LSPM Services Requests (MVC; VitalChek), and Notarized Birth Certificate Applications routed to the assigned LSC.",
          "**Monday:** Legal Services Presentation. Office Hours 1:30–3:00 PM.",
          "**Tuesday + Wednesday:** Legal Assessment Assembly + Salesforce.",
          "**Thursday:** One-on-One Participant Meetings + Salesforce. Track Municipal Activity. Office Hours 1:30–3:00 PM.",
          "**Friday:** Salesforce. Track Municipal Activity. **Prepare Weekly Report for LSPM** (numbers + special projects).",
          "There's no rigid schedule, but if you find yourself behind on any one of these recurring activities, that's the first place to look.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "There's no rigid LSC schedule, but the model week below is the rhythm most coordinators settle into. **Adapt to your site, but if you find yourself behind on any one of these recurring activities, that's the first place to look.** Five tasks run in the background every weekday - Municipal Court Letters, Expungements, LSPM Services Requests (MVC and VitalChek), Notarized Birth Certificate Applications routed to the assigned LSC, and ongoing Salesforce hygiene. The day-by-day below adds the major focus on top of those.",
      },
      {
        kind: "table",
        heading: "Weekly cadence (LSM 2026 · LSC Model Week 06/18/25)",
        columns: ["Day", "Major focus"],
        rows: [
          ["**Monday**", "**Legal Services Presentation.** Office Hours 1:30 – 3:00."],
          ["**Tuesday**", "**Legal Assessment Assembly + Salesforce.**"],
          ["**Wednesday**", "**Legal Assessment Assembly + Salesforce.**"],
          ["**Thursday**", "**One-on-One Participant Meetings + Salesforce.** Track Municipal Activity. Office Hours 1:30 – 3:00."],
          ["**Friday**", "**Salesforce.** Track Municipal Activity. **Prepare Weekly Report for LSPM** (numbers + special projects)."],
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "Daily background tasks (every weekday)",
        text:
          "On top of the day's major focus, every weekday includes: **Municipal Court Letters** · **Expungements** · **LSPM Services Requests (MVC; VitalChek)** · **Notarized Birth Certificate Applications routed to the assigned LSC**. Don't let them pile - the daily rhythm is what keeps the queue from overflowing.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/lsc-model-week-poster.png",
        caption: "**The original LSC Model Week chart** (LSM 2026 / LSC New Hire Packet, rev 100422) - the printable cadence reference NJRC distributes to new LSCs during orientation.",
      },
    ],
  },

  // ─── LEGAL ASSESSMENT ─────────────────────────────────────────────
  {
    id: "whats-in-the-legal-assessment",
    category: "legal-assessment",
    title: "What's in the Legal Assessment?",
    summary:
      "The Legal Assessment is the foundation of NJRC Legal Services. Every Assessment starts with four pages - and grows as the case progresses with court results, time-served docs, MVC results, and other supporting pages.",
    source: "LSM 2026 · Module 2",
    estimatedTime: "4 min read",
    difficulty: "easy",
    keywords: ["assessment", "intake", "what's in it", "structure", "anatomy"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**The Legal Assessment is the foundation of NJRC Legal Services.** Every participant who enrolls in the Reentry Program gets one.",
          "**The LSC creates it and reviews it with the participant during the first legal meeting** - typically during Orientation Week.",
          "**A new Legal Assessment may also be completed** if a participant has been away from the program for an extended period, or if the LSC expects the participant's legal status to have changed significantly since the prior assessment.",
          "**It identifies barriers** related to NJ MVC and municipal courts - and **documents a plan** (what the participant will do; what the LSC can support).",
          "**It also flags missing primary documents** the participant may need to apply for employment, benefits, a bank account, or a driver license.",
          "**Every Assessment starts with four pages:** (1) Initial Assessment page, (2) MVC Suspension Line call sheet, (3) MVC Notes page, (4) Acceptable 6 Points Documentation page.",
          "**As the case progresses**, the LSC adds: municipal court results and summaries, applicable time-served documentation (when available), MVC search results, and other related documents.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "**For NJRC, the Legal Assessment is the foundation of Legal Services.** When a participant enrolls in the Reentry Program, the LSC creates the Legal Assessment and reviews it with the participant during their first legal meeting - typically during Orientation Week. **A new Legal Assessment may also be completed** if a participant has been away from the program for an extended period, or if the LSC expects the participant's legal status to have changed significantly since the prior assessment.",
      },
      {
        kind: "paragraph",
        text:
          "The Legal Assessment **helps identify barriers** to a participant's reentry related to the **New Jersey Motor Vehicle Commission (MVC) and municipal courts**. It also **documents a plan** - what the participant will do and what the LSC can support - so staff can track progress, revisit goals, and measure outcomes over time.",
      },
      {
        kind: "paragraph",
        text:
          "It **also flags missing primary documents** the participant may need to apply for employment, benefits, a bank account, or a driver license.",
      },
      {
        kind: "checklist",
        heading: "Every Assessment starts with these four pages",
        items: [
          "**(1) Initial Assessment page** - participant info, intake notes, MCCS/MVC checkboxes + summaries (see [[assessment-cover-page-fields]] for the full field-by-field walkthrough)",
          "**(2) MVC Suspension Line call sheet** - script + Q&A captured during the [[mvc-suspension-line-script]] call",
          "**(3) MVC Notes page** - free-form notes from the call",
          "**(4) Acceptable 6 Points Documentation page** - the current MVC-accepted-documents reference (see [[six-points-of-id]])",
        ],
      },
      {
        kind: "paragraph",
        text:
          "**As the case progresses, the LSC adds supporting pages as needed**, including:",
      },
      {
        kind: "checklist",
        heading: "Supporting pages added over time",
        items: [
          "Municipal court results and summaries (MCCS Additional Details printouts)",
          "Applicable time-served documentation (when available) - DOC Offender Info, PROMIS/Gavel sentence pages, JOC, drug-program completion letters",
          "MVC search results - surcharge, restoration fee, LRRR pages",
          "Copies of identification documents you've helped obtain",
          "Copies of court letters sent + responses received",
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Use the Notes section liberally",
        text:
          "**For consistency and easier review, document MCCS findings, time-served information, MVC search results, and planned follow-up steps in the Notes section of the Initial Assessment page.** It's the single most-referenced spot in the entire document.",
      },
    ],
  },

  {
    id: "create-a-new-legal-assessment",
    category: "legal-assessment",
    title: "How do I create a new Legal Assessment?",
    summary:
      "Each week, use the Orientation intake info to pre-build each Legal Assessment. Start with the Initial Assessment header, run MCCS, run both MVC searches, request restoration fees if eligible, then scan + upload to Salesforce + Case Note.",
    source: "LSM 2026 · Module 2",
    estimatedTime: "30-45 min per Participant",
    difficulty: "medium",
    keywords: ["assessment", "new", "create", "build", "intake", "orientation"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**At the start of each week**, the LSC receives intake information for participants scheduled for Orientation. Use that information to prepare each Legal Assessment **in advance** of the first legal meeting.",
          "**Initial Assessment page header fields:** participant's first + last name (with suffix) · DOB · SS# (if available) · name of staff member completing the Assessment · most recent conviction date, release date, and SBI# (when available) · NJ ID/DL# · ZIP code.",
          "**Partial info is OK.** Even if some intake info is missing: MCCS runs on **name + DOB** alone (or the NJ ID# alone). Both MVC searches need **NJ ID# + DOB + ZIP code**. The Restoration Fee Search asks for SS# but **DOB substitutes** if SS# unavailable.",
          "**If the participant has lost their ID or only has an out-of-state ID**, instruct them to call the **[[mvc-suspension-line-call]]** to request their NJ ID# and the ZIP code on file.",
          "**Even if the participant has never held an NJ ID**, they may still have an **MVC ID#** if they've ever received tickets or charges in New Jersey - always check.",
          "**Leave the SS Card / Birth Certificate / MVC ID checkboxes blank** until the legal meeting (unless documentation is provided in advance).",
          "**If the participant appears eligible for a restoration fee**, submit a request to SDLS **before** the legal meeting and provide the receipt later.",
          "**Document MCCS findings, time-served information, MVC search results, and planned follow-up steps in the Notes section** of the Initial Assessment page - for consistency and easier review.",
          "**After completing:** scan and upload to the participant's Salesforce profile · enter details in the **Legal/Info** and **DL Restoration** tabs · add a **Legal Assessment Case Note** · make copies to distribute during the legal meeting.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "**At the start of each week, the LSC receives intake information for participants scheduled for Orientation.** Use that information to prepare each Legal Assessment in advance - by the time the participant sits down for their legal meeting, you should already know most of their picture.",
      },
      {
        kind: "steps",
        heading: "Build the Assessment",
        items: [
          { title: "Complete the Initial Assessment page header", body: "**Participant's first and last name (including any suffix)** · **DOB** · **SS#** (if available) · **Name of staff member completing the Assessment** · Most recent **conviction date, release date, and SBI#** (when available) · **NJ ID/DL#** · **ZIP code**." },
          { title: "Leave ID checkboxes blank", body: "**Leave the checkboxes for SS Card, Birth Certificate, and MVC IDs blank until the legal meeting** with the participant - unless the participant provides documentation in advance." },
          { title: "Conduct a Municipal Court Case Search (MCCS)", body: "Use the participant's name and DOB. **Note any active cases, ticket/fine amounts, Closed to MVC statuses, driver license suspensions, and outstanding warrants.** Use the corresponding checkboxes and the Notes section at the bottom of the Initial Assessment page to summarize. See [[run-an-mccs-search]]." },
          { title: "Run the MVC Restoration Fee Search and Surcharge Violation System Search", body: "Using the participant's **NJ ID#, ZIP code, and date of birth**. The Restoration Fee Search **also requests the participant's SS#**, but if the SS# is unavailable, **you may substitute the date of birth**. Record the results. See [[restoration-fee-search]] and [[surcharge-violation-search]]." },
          { title: "If restoration-fee-eligible, request the fee", body: "**Submit a restoration-fee request to SDLS BEFORE the legal meeting** and provide the receipt to the participant later." },
          { title: "Scan + upload + Salesforce", body: "**Scan the Assessment, upload to the participant's Salesforce profile**, enter the corresponding details in the **Legal/Info** and **DL Restoration** tabs, add a **Legal Assessment Case Note**." },
          { title: "Make copies for the meeting", body: "**Make copies of the Assessment to distribute to the participant during their legal meeting.**" },
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "If the participant has lost their ID - or only has an out-of-state ID",
        text:
          "**Instruct the participant to call the [[mvc-suspension-line-call]]** to request their NJ ID# and the ZIP code on file. Without those, both MVC searches are blocked. The MVC ID# alone can also be used to run an MCCS search.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "MVC IDs without an NJ ID card",
        text:
          "**Even if a participant has never held an NJ ID, they may still have an MVC ID#** if they have received tickets or charges in New Jersey. Always check before assuming there's no MVC record.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Notes section is your friend",
        text:
          "**For consistency and easier review, document MCCS findings, time-served information, MVC search results, and planned follow-up steps in the Notes section** of the Initial Assessment page. The next LSC who picks up this file will read the Notes first.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/lsc-participant-assessment-form.png",
        caption: "**The blank LSC Participant Assessment cover sheet** (LSM 2026 / LSC New Hire Packet, rev 07.25.24) - the literal form you fill in for every participant. SSN / BC checkboxes, MVC ID + DL number fields, surcharge / restoration / time-served sections, and the 6-Point ID checklist all on one page.",
      },
    ],
  },

  // ─── MCCS ─────────────────────────────────────────────────────────
  {
    id: "run-an-mccs-search",
    category: "mccs",
    title: "How do I run a Municipal Court Case Search (MCCS)?",
    summary:
      "MCCS is the LSC's primary tool for identifying and tracking municipal-court matters. Four search methods, two status filters. Always start with Name + DOB; fall back to DL number when needed.",
    source: "LSM 2026 · Module 3",
    estimatedTime: "5-10 min per Participant",
    difficulty: "easy",
    keywords: ["mccs", "search", "municipal court", "njcourts", "name", "ticket", "case number", "DL"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**MCCS is the Legal Services Team's primary tool** for helping participants identify and track municipal-court matters. It's a publicly-available search tool provided by the New Jersey Courts.",
          "**The LSC uses MCCS** when completing a participant's Legal Assessment, tracking and updating relief requests, and for other case-related tasks.",
          "**When preparing a Legal Assessment**, run an MCCS search to capture any outstanding municipal-court cases at the time of the search. Record the results on the participant's Legal Assessment.",
          "**Four search methods:** (1) Ticket Number · (2) Complaint Number · (3) Driver's License Number · (4) Name (First, Middle, Last) + DOB.",
          "**Two status filters:** **Outstanding** = active cases, warrants, and unpaid tickets/fines. **All** = everything, including disposed and transferred matters.",
          "**In most cases, search by Name + DOB.** If no results appear under either Outstanding or All - or if the spelling of the participant's name is uncertain - use the **driver's license number** to confirm.",
          "**You must accept the privacy disclaimer** before each search session.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "**Municipal Court Case Search (MCCS) is the Legal Services Team's primary tool** for helping participants identify and track municipal-court matters. MCCS is a publicly available search tool provided by the New Jersey Courts that allows users to look up tickets and complaints in several ways. The LSC uses MCCS when **completing a participant's Legal Assessment, tracking and updating relief requests, and for other case-related tasks**.",
      },
      {
        kind: "paragraph",
        text:
          "**When preparing a participant's Legal Assessment, run an MCCS search to capture any outstanding municipal court cases at the time of the search.** Record the results on the participant's Legal Assessment.",
      },
      {
        kind: "steps",
        heading: "Executing an MCCS search",
        items: [
          { title: "Open the search portal + accept the privacy disclaimer", body: "[portal.njcourts.gov/webe41/MPAWeb/](https://portal.njcourts.gov/webe41/MPAWeb/index.jsp). The disclaimer is the first page; it gates access to the search." },
          { title: "Pick one of four search methods", body: "**(1)** Search by **Ticket Number** · **(2)** Search by **Complaint Number** · **(3)** Search by **Driver's License Number** · **(4)** Search by **Name** (First, Middle, Last) + **DOB**. See [[mccs-by-ticket-or-complaint]] for the alternate methods." },
          { title: "Pick a status filter", body: "**Set Status to Outstanding** to view active cases, warrants, and unpaid tickets/fines for the participant. **Set Status to All** to view all cases and tickets associated with the participant - including active, disposed, and transferred matters." },
          { title: "If results don't match, switch methods", body: "**In most cases, the LSC will search using the participant's name and DOB.** If no results appear under either Outstanding or All - or if the spelling of the participant's name is uncertain - **use the driver's license number** (when available) to confirm." },
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Always start with Name + DOB",
        text:
          "It's the most common and most reliable search. Save the DL-based search for when name search fails or returns ambiguous results.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/mccs-flow-chart.png",
        caption: "**MCCS Flow Chart** (LSM 2026 / LSC New Hire Packet) - the canonical decision tree for an MCCS run. Outstanding-vs-All filter, 'No Cases Found' fallback path, the 'View Additional Details → Time Payment / Bail' branches, and exactly which pages to print at each step.",
      },
      {
        kind: "links",
        heading: "Resources & contacts",
        items: [
          { label: "MCCS - Municipal Court Case Search", href: "https://portal.njcourts.gov/webe41/MPAWeb/index.jsp", description: "The public search portal." },
          { label: "MCCS Disclaimer / accept page", href: "https://portal.njcourts.gov/webe41/MPAWeb/jsp/common/Disclaimer.faces", description: "Direct link to the disclaimer that gates the search." },
          { label: "NJ Courts self-help", href: "https://njcourts.gov/selfhelp/index.html", description: "Public-facing court resources for participants." },
        ],
      },
    ],
  },

  {
    id: "read-mccs-results",
    category: "mccs",
    title: "How do I read MCCS results?",
    summary:
      "MCCS results show 8 columns of case data. Click View Summary for fines, then See Additional Details for the full record. Tabs at the bottom split warrant, bail, involved persons, and time payment views.",
    source: "LSM 2026 · Module 3",
    estimatedTime: "3 min read",
    difficulty: "easy",
    keywords: ["mccs", "results", "read", "interpret", "columns", "additional details"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**Eight result columns** to understand: Court Name · Case/Ticket Number · Offense Code · Offense Description · Offense Date · Court Date / Pay-by Date · Case Status · Warrant Status.",
          "**Drill in:** click a case row → **View Summary** (bottom-right; shows fine + ticket amounts) → **See Additional Details** (bottom-right of summary; opens the full Details page).",
          "**Additional Details tabs** (at the bottom of the page): **Warrant** (warrant history including issue and recall dates) · **Bail** (bail amount for a warrant) · **Involved Persons** (arresting officer, prosecutors, public defenders) · **Time Payment** (assessed fine total, payments made, remaining balance).",
          "**Warrant Status** appears only if a warrant was ever issued. **Blank** = no warrant ever. **Outstanding** = active warrant. **Recalled** = prior warrant exists but no longer active.",
          "**For court-letter prints:** select the **Bail** tab when printing a warrant matter. Select the **Time Payment** tab when printing a fine or ticket matter.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "After executing an MCCS search, results will vary by participant. Review the **Results page** for the following information:",
      },
      {
        kind: "kv",
        heading: "What each result column tells you",
        items: [
          { label: "Court Name", value: "Which municipal court the offense is filed in" },
          { label: "Case/Ticket Number", value: "The court's identifier for this matter", mono: true },
          { label: "Offense Code", value: "The statute (e.g. 39:4-50)", mono: true },
          { label: "Offense Description", value: "Plain-English description of the charge" },
          { label: "Offense Date", value: "When the charge was filed" },
          { label: "Court Date / Pay-by Date", value: "Upcoming hearing date OR payment deadline" },
          { label: "Case Status", value: "Court's current action - see status guide" },
          { label: "Warrant Status", value: "Outstanding, Recalled, or blank if no warrant ever" },
        ],
      },
      {
        kind: "steps",
        heading: "Drill into a case",
        items: [
          { title: "Select the case in the results list", body: "Click any case row." },
          { title: "Click 'View Summary' (bottom-right)", body: "**The summary displays fine and ticket amounts.**" },
          { title: "Click 'See Additional Details' (bottom-right of summary)", body: "**Opens the full Details page**, which includes all available case information - fines owed, payment history, and bottom-tab views." },
          { title: "Use the bottom tabs", body: "**Warrant** (warrant history including issue + recall dates) · **Bail** (bail amount for a warrant) · **Involved Persons** (arresting officer, prosecutors, public defenders) · **Time Payment** (assessed fine total, payments made toward the fine, remaining balance due)." },
        ],
      },
      {
        kind: "kv",
        heading: "What each Case Status means (common codes)",
        items: [
          { label: "Time Payment", value: "Participant on a payment plan with the court. Indicates unpaid fines/tickets." },
          { label: "C2MVC", value: "Closed to MVC. The court has handed the matter to MVC for collection." },
          { label: "DL Suspended", value: "Court has reported a driver-license suspension to MVC." },
          { label: "Non-traffic T-Pay", value: "Time-payment plan for a non-traffic offense." },
          { label: "Proposed Suspension Notice", value: "Suspension is pending; not yet active." },
          { label: "Failure to Appear", value: "Participant missed a court date - often paired with a warrant." },
          { label: "Warrant", value: "Active arrest warrant - highest priority." },
        ],
      },
      {
        kind: "kv",
        heading: "Warrant Status values",
        items: [
          { label: "(blank)", value: "No warrant was ever issued on the case." },
          { label: "Outstanding", value: "Active warrant - the participant could be arrested at any traffic stop." },
          { label: "Recalled", value: "A prior warrant exists but is no longer active." },
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Print preference for letters",
        text:
          "**When printing or scanning an Additional Details page for a Legal Assessment or Relief Letter, select the Bail tab when printing a warrant** - and **select the Time Payment tab when printing a fine or ticket**. The default tab can omit the very info the court needs to act.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/mccs-case-list-live.png",
        caption: "Live MCCS Case List for a real participant (defendant name redacted) - showing case status, warrant status, offense codes, and dates across 8 cases in 4 different municipal courts.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/mccs-ticket-detail-dl-suspended.png",
        caption: "MCCS Ticket Detail page for a 'DL Suspended' case - bail tab, fine breakdown, time payment summary, and warrant history. This is what you screenshot for a court letter.",
      },
    ],
  },

  {
    id: "mccs-case-status-codes",
    category: "mccs",
    title: "What do MCCS Case Status codes mean?",
    summary:
      "Time Payment, C2MVC, DL Suspended, Failure to Appear, Warrant - and what each tells you to do.",
    source: "LSM 2026 · Module 3",
    estimatedTime: "3 min read",
    difficulty: "easy",
    keywords: ["mccs", "status", "codes", "c2mvc", "DL Suspended", "warrant", "time payment"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**Case Status** in MCCS gives a quick snapshot of what's happening with a case or ticket.",
          "**Common statuses:** Time Payment · C2MVC · DL Suspended · Non-traffic T-Pay · Proposed Suspension Notice · Failure to Appear · Warrant.",
          "These statuses can indicate **unpaid fines/tickets, an MVC-reported suspension, a missed court date, or an outstanding warrant**.",
          "**Active warrants take absolute priority** - they're a same-day risk to the participant.",
          "**Warrant Status** is a **separate column** from Case Status - see [[read-mccs-results]] for blank/Outstanding/Recalled meanings.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "**Case Status gives a quick snapshot of what is happening with a case or ticket.** Common statuses include: Time Payment, C2MVC, DL Suspended, Non-traffic T-Pay, Proposed Suspension Notice, Failure to Appear, and Warrant. These statuses can indicate **unpaid fines/tickets, an MVC-reported suspension, a missed court date, or an outstanding warrant**.",
      },
      {
        kind: "table",
        heading: "Common Case Status codes",
        columns: ["Status", "Meaning", "What it tells you to do"],
        rows: [
          ["Time Payment", "Participant on a payment plan with the court", "Confirm they're current; if behind, this could become a problem"],
          ["C2MVC", "Closed to MVC - the court has handed it over to MVC", "Will block license restoration; needs court letter to lift"],
          ["DL Suspended", "Driver license has been suspended via this case", "Same as C2MVC - court letter required to lift"],
          ["Non-traffic T-Pay", "Time payment plan for a non-traffic offense", "Same monitoring as Time Payment"],
          ["Proposed Suspension Notice", "Suspension is pending; not yet active", "Resolve the matter before the suspension takes effect"],
          ["Failure to Appear", "Participant missed a court date", "Often paired with a warrant - see Warrant Status column"],
          ["Warrant", "Active arrest warrant", "Highest priority - Warrant Recall letter immediately"],
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "Warrants take priority over everything",
        text:
          "If MCCS shows ANY active warrant, that's the first thing you address - before fines, before license restoration, before BC orders. A warrant means the Participant could be arrested at any traffic stop.",
      },
    ],
  },

  // ─── TIME SERVED ──────────────────────────────────────────────────
  {
    id: "what-is-time-served",
    category: "time-served",
    title: "What is Time Served and when can I use it?",
    summary:
      "Days a participant spent incarcerated (county jail, NJ state prison, federal prison) - used to ask Municipal Court judges to vacate, forgive, or reduce fines. Two strict requirements, plus rules about what counts and what doesn't.",
    source: "LSM 2026 · Module 4",
    estimatedTime: "4 min read",
    difficulty: "medium",
    keywords: ["time served", "credit", "fine waiver", "vacate", "incarceration"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**Time Served** refers to the number of days a participant has been incarcerated in a county jail, state prison, or federal prison. **It must be verified through an official document or database search** that shows where the participant was incarcerated and the dates of confinement.",
          "**A Time Served Relief Request** asks a Municipal Court judge to **vacate, forgive, or reduce a participant's fines** by crediting time the participant has spent incarcerated in New Jersey.",
          "**Two strict requirements** every Time-Served request must meet: **(1) Time served must be AFTER the Offense Date** for the municipal matter (Time Served only moves backward in time). **(2) The monetary value must meet or exceed the outstanding fine** - many courts apply **$50 × days incarcerated**. Not a hard-and-fast rule; courts may show leniency.",
          "**What counts:** NJ county jail · NJ state prison · **federal prison** (yes, surprises some). Some courts will accept **drug-treatment program** or **halfway house** time.",
          "**What does NOT count:** **out-of-state jail or prison** · **ankle monitor** · **probation** · **parole**. Most courts will not accept these.",
          "**LSCs conduct Jail Time Searches for two purposes:** (1) determine whether Time Served is available, and (2) **obtain the participant's SBI number** if not previously recorded in Salesforce.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "**Time Served** refers to the number of days a participant has been incarcerated in a county jail, state prison, or federal prison. **This time must be verified through an official document or database search** that shows where the participant was incarcerated and the dates of confinement.",
      },
      {
        kind: "paragraph",
        text:
          "**A key part of NJRC's legal services is advocating for relief from tickets and fines in New Jersey Municipal Courts.** One of the primary ways the LSC does this is through **Time Served Relief Requests** - a Time Served Relief Request asks a Municipal Court judge to **vacate, forgive, or reduce a participant's fines** by crediting time the participant has spent incarcerated in New Jersey.",
      },
      {
        kind: "checklist",
        heading: "The two requirements every Time Served request must meet",
        items: [
          "**Time served must be AFTER the Offense Date** for the municipal matter - **Time Served only moves backward in time**. *Example:* if the participant received a ticket on **December 31, 2023**, time served in **2024 or 2025** may be applied to that ticket.",
          "**The monetary value of the Time Served must meet or exceed the outstanding fine amount.** Many courts apply **$50 × days incarcerated**. **This is not a hard-and-fast rule, and courts may show leniency.**",
        ],
      },
      {
        kind: "paragraph",
        text:
          "Participants may ask whether time served in an **out-of-state jail or prison** can be used. **The answer is no.** Participants may also ask whether **time served in federal prison** counts. **The answer is yes.** **Some courts will accept time spent in a drug-treatment program or halfway house. Most courts will not accept time spent on an ankle monitor, probation, or parole as Time Served credit.**",
      },
      {
        kind: "table",
        heading: "What counts vs. what doesn't",
        columns: ["Type of time", "Counts?", "Notes"],
        rows: [
          ["NJ county jail", "✅ Yes", "The most common"],
          ["NJ state prison", "✅ Yes", "Most common after county jail"],
          ["Federal prison", "✅ Yes", "Less common but accepted"],
          ["Out-of-state jail or prison", "❌ No", "Even if it was for the same conduct"],
          ["Drug treatment program", "🟡 Sometimes", "Some courts accept; varies"],
          ["Halfway house", "🟡 Sometimes", "Same as drug treatment - court-by-court"],
          ["Ankle monitor", "❌ Usually no", "Not custodial"],
          ["Probation / parole", "❌ No", "Not custodial"],
        ],
      },
    ],
  },

  {
    id: "promis-gavel-search",
    category: "time-served",
    title: "How do I run a PROMIS/Gavel search?",
    summary:
      "NJ's public criminal-conviction database - your primary source for Sentence Date and Jail Days when supporting a Time Served Relief Request, and the place to confirm a participant's SBI number.",
    source: "LSM 2026 · Module 4",
    estimatedTime: "10 min per Participant",
    difficulty: "medium",
    keywords: ["promis", "gavel", "criminal", "convictions", "SBI", "sentence date"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**PROMIS/Gavel** is NJ's publicly-accessible database of **criminal convictions**. It's the LSC's primary source for **Sentence Date and Jail Days** when supporting a Time Served Relief Request - and it's where you confirm the participant's **SBI number** when not already in Salesforce.",
          "**Search method:** the LSC will **almost always use the participant's last and first name** to search. Other methods exist: Indictment/Accusation # and CDR#.",
          "**Results list everyone with the same/similar name** - use **birth year** + **SBI number** to identify the right person. **It's not uncommon** for the same name + birth year to appear multiple times; **compare SBI numbers**. Matching SBIs likely mean multi-county records for the same person - clicking any of those entries should bring you to the same information.",
          "**Pop-up + captcha required.** Resolve the captcha and **enable pop-ups** if your browser blocks them - search results open in a pop-up window.",
          "**Case List shows up to 2 cases per page**, with a **maximum of 6 pages retrievable**.",
          "**Look for a Sentence Date AFTER the municipal Offense Date** - that's the case you'll cite. Sentence Date is on the **bottom-right of each case row**.",
          "**Defendant Detail Page** shows Sentence Date + **Jail Days** - record both on the Legal Assessment.",
          "**Print formats:** **for the Assessment** → print the Defendant Detail Page (printer icon, top-left of the pop-up). **For a Time Served Relief Letter** → print the **Sentence Details Page** instead. From the Defendant Detail Page, click the **Sentence** option in the dark blue boxes on the top left.",
          "**Don't use the browser back button.** PROMIS/Gavel is an older website - use the on-page icons only. Browser back will lose your session.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "**PROMIS/Gavel is NJ's publicly-accessible database of criminal convictions.** It's the LSC's primary source for Sentence Date and Jail Days when supporting a Time Served Relief Request, and it's also where you confirm a participant's SBI number when it's not already in Salesforce.",
      },
      {
        kind: "steps",
        heading: "Execute a PROMIS/Gavel search",
        items: [
          { title: "Open the search portal + resolve the captcha", body: "**Enable permissions to open a pop-up window if you haven't previously done so.** A pop-up window will appear with the search options." },
          { title: "Choose a search method", body: "Options: **Last/First Name**, **Indictment/Accusations #**, **CDR#**. **The LSC will almost always use the participant's last and first name** to search PROMIS/Gavel." },
          { title: "Identify the correct person in results", body: "Results list **all individuals with the same or similar name**, along with **birth year**, **SBI number(s)**, and the **county or counties** where convictions occurred. **Match name + birth year.** It's not uncommon for the same name and birth year to appear multiple times - in that case, **compare the SBI numbers**. If the SBI numbers match, the entries likely refer to the same person and may reflect matters in multiple counties. Clicking any of those entries should bring the LSC to the same information." },
          { title: "Confirm the right person on the demographics page", body: "After selecting the desired subject, the LSC will see a **short information sheet with the participant's basic demographics**. Review this page to confirm you selected the correct person." },
          { title: "Open Case List", body: "Click **Case List** at the top to view the subject's case history. **PROMIS/Gavel will show up to two cases per page, with a maximum of six pages retrievable.**" },
          { title: "Find a case with an appropriate Sentence Date", body: "When reviewing cases, **pay attention to the Sentence Date (bottom right of the case information)** and open a case for more details when you find an appropriate sentence date - i.e., **a date after the municipal Offense Date** you're trying to relieve." },
          { title: "Open the Defendant Detail Page", body: "Click into the case. The page shows the participant's relevant information for the selected case. **Pay special attention to the Sentence Date and Jail Days; record them on the Legal Assessment** and use them for Time Served Relief Requests." },
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "Don't use the browser back button",
        text:
          "**PROMIS/Gavel is an older website**, and the only way to navigate it is to use the **icons on each page** - for example, **the browser back button will not work**. Use the printer icon, on-page links, and the dark-blue navigation boxes only.",
      },
      {
        kind: "steps",
        heading: "Print for the Legal Assessment vs. for a Relief Letter",
        items: [
          { title: "For the Legal Assessment", body: "**Print the Defendant Detail Page** - click the small **printer icon near the top left of the pop-up**." },
          { title: "For a Time Served Relief Letter", body: "**Print the Sentence Details Page** instead. From the Defendant Detail Page, **click the Sentence option in the dark blue boxes on the top left of the page** first." },
        ],
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/promis-complaint-detail.png",
        caption: "PROMIS/Gavel Complaint Detail page (defendant info redacted) - this is what you're looking at when you've drilled into a case.",
      },
      {
        kind: "links",
        heading: "Resources & contacts",
        items: [
          { label: "PROMIS/Gavel Public Access search", href: "https://portal.njcourts.gov/webe41/ExternalPGPA/", description: "The actual search portal - captcha required." },
          { label: "NJ Courts main portal", href: "https://njcourts.gov", description: "Self-help, e-filing, and ancillary services." },
          { label: "NJ Courts MVC liaison (Alexandro Marin)", href: "mailto:alexandro.marin@njcourts.gov", description: "When MCCS / PROMIS data needs human follow-up." },
        ],
      },
    ],
  },

  {
    id: "doc-inmate-finder",
    category: "time-served",
    title: "How do I use the NJ DOC Inmate Finder?",
    summary:
      "Search the NJ Department of Corrections database for current or recently-released state-prison incarceration history. Use when PROMIS/Gavel returns nothing AND the participant reports a NJ state-prison release.",
    source: "LSM 2026 · Module 4",
    estimatedTime: "5 min",
    difficulty: "easy",
    keywords: ["doc", "inmate", "finder", "state prison", "njdoc", "incarceration history"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**The NJ DOC Inmate Finder** can be used to search for any individual who is **currently incarcerated** in a NJ state prison, or **released within the past year**.",
          "**(9.15.25 update from corpus):** The DOC search has been **returning results for individuals released more than a year ago** as well - so it's worth trying for older releases too.",
          "**Use this search when PROMIS/Gavel returns no results AND the participant reports having been released from a NJ state prison.**",
          "**Search by first + last name.** Results return photos + identifying info (DOB, sex, race) for matching individuals - useful for confirming identity if there are multiple results.",
          "**The Offender Details Page** shows the **Incarceration History** (near the bottom left), detailing **dates the subject has been in and out of custody**. **These dates are used for Time Served Relief Requests.**",
          "**If the participant is currently in custody**, the Incarceration History will report **\"Currently in Custody\"** with the date they were committed.",
          "**For Time Served Relief Letters: print the FULL Offender Details Page(s)** - all pages.",
          "**If no results match, the search returns** \"no records found that match your search criteria.\"",
        ],
      },
      {
        kind: "paragraph",
        text:
          "**New Jersey's Department of Corrections (DOC) Inmate Finder** can be used to search for any individual who is **currently incarcerated in a New Jersey state prison**, or who has been **released within the past year**. Individuals who have been released for longer than a year may not appear in the search results - though a **9/15/25 update notes that DOC search has been returning results for individuals released more than a year ago**, so it's worth trying even for older releases.",
      },
      {
        kind: "paragraph",
        text:
          "**Use this search if no results are found in PROMIS/Gavel and the participant reports having been released from a New Jersey state prison.**",
      },
      {
        kind: "steps",
        heading: "Run the search",
        items: [
          { title: "Open the search portal + accept the disclaimer", body: "[www-doc.state.nj.us/DOC_Inmate/inmatefinder](https://www-doc.state.nj.us/DOC_Inmate/inmatefinder?i=I)." },
          { title: "Search by first + last name", body: "Results return **pictures of incarcerated/released individuals**, as well as **identifying information (DOB, sex, race, etc.)** - used to identify the correct participant if there are multiple results." },
          { title: "Open the Offender Details Page", body: "Pick the matching individual from the results." },
          { title: "Scroll to the Incarceration History (near the bottom left)", body: "**Lists the dates the subject has been in and out of custody.** These are the dates you cite for Time Served Relief Requests. If the subject is **currently in custody**, the Incarceration History will report **\"Currently in Custody\"** with the date they were committed." },
          { title: "Print the full Offender Details Page(s)", body: "**All pages** - this is your evidence for the relief letter." },
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "If there are no results",
        text:
          "If there are no individuals currently incarcerated in New Jersey or recently released matching the participant, the search will report that it found **\"no records matching the search criteria.\"** That's when you fall back to county-jail finders or [[time-served-when-databases-fail]].",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/doc-offender-details-full.png",
        caption: "DOC Offender Details page (anonymized) - the full single-page printout you attach to a Time Served Relief letter. Note the Current Prison Sentence + Incarceration History sections.",
      },
      {
        kind: "links",
        heading: "Resources & contacts",
        items: [
          { label: "NJ DOC Inmate Finder", href: "https://www-doc.state.nj.us/DOC_Inmate/inmatefinder?i=I", description: "The public search itself." },
          { label: "DOC Offender Information liaison", href: "mailto:Joseph.G.Ratajczak@doc.nj.gov", description: "For records-request follow-up when public search doesn't yield enough." },
          { label: "Federal BOP Inmate Locator", href: "https://www.bop.gov/mobile/find_inmate/byname.jsp", description: "If the participant did federal time, this is the equivalent." },
          { label: "Bergen County jail inmate finder", href: "https://iml.bcsd.us:8443/IML", description: "When PROMIS+DOC aren't enough - try the relevant county jail." },
          { label: "Essex County corrections inmate finder", href: "http://www.eccorrections.org/inmatelookup", description: "Note: was reportedly broken July 2025; verify before relying." },
        ],
      },
    ],
  },

  {
    id: "judgment-of-conviction",
    category: "time-served",
    title: "How do I get a Judgment of Conviction (JOC)?",
    summary:
      "NJ Courts Public Access search for Superior Court Judgments of Conviction - useful for Time Credits when PROMIS/Gavel and DOC don't show enough.",
    source: "LSM 2026 · Module 4",
    estimatedTime: "10 min",
    difficulty: "medium",
    keywords: ["joc", "judgment of conviction", "superior court", "time credits"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**JOC Public Access Search locates Judgments of Conviction in Superior Court** for participants. Useful for **Time Credits** when PROMIS/Gavel and DOC don't show enough detail.",
          "**Hard date cutoff:** **JOC searches only return judgments dated AFTER June 30, 2017**. Judgments dated before June 30, 2017 are **not available in this database**.",
          "**Search by County + Name.** Always scroll to the bottom of the County dropdown and **select ALL COUNTIES** - don't filter to one.",
          "**Each result lists:** defendant's name · county · Indictment/Accusation # · PROMIS/Gavel # · SBI # · sentence + finalized dates · presiding judge.",
          "**The downloadable PDF** of the Judgment of Conviction includes a **Time Credits section** - commonly **at the top of page 3** of the JOC. This section details time spent in custody and is what you cite for Time Served Relief.",
          "**Always upload the JOC to Salesforce first**, then print and attach to the relief letter.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "**The JOC Public Access Search locates Judgments of Conviction in Superior Court** for participants. JOC searches will only return results for **JOCs dated after June 30, 2017**. **Judgments dated before June 30, 2017 are not available in this database.**",
      },
      {
        kind: "callout",
        tone: "warn",
        title: "Hard date cutoff: post-June 30, 2017 only",
        text:
          "The JOC Public Access Search only returns judgments dated **AFTER June 30, 2017**. Older judgments aren't available in this database - you'll need to fall back to county-jail records, probation/parole letters, or the participant directly. See [[time-served-when-databases-fail]].",
      },
      {
        kind: "steps",
        heading: "Execute a JOC search",
        items: [
          { title: "Open the JOC Public Access portal + resolve the captcha", body: "[portal.njcourts.gov/webe41/jocpa_internet](https://portal.njcourts.gov/webe41/jocpa_internet/jsp_public/home_public.jsp)." },
          { title: "Search by County and the participant's Name", body: "**Always scroll to the bottom of the County dropdown and select ALL COUNTIES.**" },
          { title: "Read the results list", body: "**Results list all judgments matching the query, organized by date and court.** If no results match, the search returns: \"no records found that match your search criteria.\"" },
          { title: "Confirm the right judgment", body: "Each result shows defendant info: **name, county, Indictment/Accusation #, PROMIS/Gavel #, SBI #, sentence + finalized dates, presiding judge.**" },
          { title: "Download the JOC PDF", body: "Once the desired case is found, **select it** - a new pop-up window opens where a **PDF of the Judgment of Conviction can be downloaded**." },
          { title: "Find the Time Credits section", body: "**Commonly found at the top of page 3 of the JOC.** This section details any time spent in custody - which the LSC can use for Time Served Relief Requests." },
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Always upload to Salesforce first",
        text:
          "**When using a Judgment of Conviction for a relief request, first download the judgment and upload it to the participant's Salesforce profile. Then print the full judgment and attach it to the relief request.** Don't skip the Salesforce step - institutional memory.",
      },
      {
        kind: "links",
        heading: "Resources & contacts",
        items: [
          { label: "JOC Public Access search", href: "https://portal.njcourts.gov/webe41/jocpa_internet/jsp_public/home_public.jsp", description: "NJ Courts Judgment of Conviction search (post-June 2017 only)." },
          { label: "NJ Courts MVC liaison (Alexandro Marin)", href: "mailto:alexandro.marin@njcourts.gov", description: "For municipal-court coordination questions." },
        ],
      },
    ],
  },

  // ─── COURT LETTERS ────────────────────────────────────────────────
  {
    id: "when-to-write-a-court-letter",
    category: "court-letters",
    title: "When do I write a court letter, and what kind?",
    summary:
      "The three letter types - Time Served / Warrant Recall / MVC Suspension Correction - what each one asks the court to do, what backup goes with it, and how to read MCCS to choose.",
    source: "LSM 2026 · Module 5",
    estimatedTime: "8 min read",
    difficulty: "medium",
    keywords: ["letters", "when", "types", "time served", "warrant recall", "mvc correction"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "Court letters are how the LSC actually moves the needle on a participant's municipal-court issues. They're not generic form-letters - they're targeted requests addressed to a specific Court Administrator that ask for a specific outcome (vacate this fine, recall this warrant, fix this suspension on MVC's records). Almost every legal-services breakthrough you'll deliver runs through one of three letter types below. **Read this topic before you draft your first letter** - the choice of letter type is what separates a request that gets granted from one that gets ignored.",
      },
      {
        kind: "paragraph",
        text:
          "**The signal comes from MCCS.** When you run a Municipal Court Case Search and see warrants, fines, suspensions, or proposed suspensions, MCCS is essentially telling you which letter to write. The three common types map cleanly onto the three most common MCCS findings.",
      },
      {
        kind: "table",
        heading: "The three common letter types",
        columns: ["Letter type", "When to use it", "What you're asking the court to do", "Backup attached"],
        rows: [
          [
            "**Conversion of Fines to Time Served**",
            "Participant has prior jail/prison/drug-program time AFTER the offense date AND fines are still outstanding",
            "Grant time-served credit; vacate the fines and dismiss the matter",
            "DOC Offender Info → PROMIS/Gavel sentence detail → JOC → drug program completion (if any). Order matters.",
          ],
          [
            "**Warrant Recall Request**",
            "Participant has one or more **active** municipal-court warrants (MCCS Warrant column = Outstanding)",
            "Recall the warrant; revoke any bail; calendar a new hearing date",
            "**Usually no backup**. Exception: if the letter also seeks fine relief OR there's time-served available to dismiss the warrant matter, attach the time-served chain.",
          ],
          [
            "**MVC Suspension Correction**",
            "MCCS shows disposed/transferred (or no record at all) but the participant's MVC report still shows a suspension tied to that case",
            "Communicate with MVC to resolve the suspension OR provide an official disposition the LSC can transmit to MVC",
            "Pages from a [License Restoration Requirements Report](/how-to/license-restoration-requirements-report) (if you have one) + the relevant MCCS Additional Details pages.",
          ],
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "Less common - but worth knowing",
        text:
          "**Fine waiver / indigency** letters argue that a participant cannot pay even on a payment plan. Used sparingly; SDLS keeps a template. **Hearing rescheduling** letters request a different court date when the participant has a documented conflict (work, treatment program, transportation barrier). **Records-clarification** letters ask the court to confirm a disposition the LSC can't otherwise verify.",
      },
      {
        kind: "decisionTree",
        heading: "Quick decision tree",
        root: {
          kind: "decision",
          question: "Does MCCS show an active warrant?",
          yes: {
            kind: "decision",
            question: "Does the participant ALSO have outstanding fines on the same case + time served available?",
            yes: { kind: "result", tone: "tip", label: "Combined Warrant Recall + Time-Served Letter (one letter, two asks)" },
            no: { kind: "result", tone: "tip", label: "Warrant Recall Letter (no backup needed in most cases)" },
          },
          no: {
            kind: "decision",
            question: "Does MCCS show outstanding fines or DL Suspended / Closed to MVC?",
            yes: {
              kind: "decision",
              question: "Does the participant have time served AFTER the offense date?",
              yes: { kind: "result", tone: "tip", label: "Time-Served Letter (backup chain required)" },
              no: { kind: "result", tone: "warn", label: "Talk to SDLS - fine waiver or payment-plan path may apply" },
            },
            no: {
              kind: "decision",
              question: "Does MVC show a suspension that MCCS doesn't reflect?",
              yes: { kind: "result", tone: "info", label: "MVC Suspension Correction Letter" },
              no: { kind: "result", tone: "tip", label: "No letter needed - confirm with participant + close the issue" },
            },
          },
        },
      },
      {
        kind: "callout",
        tone: "danger",
        title: "Don't write letters from scratch",
        text:
          "SDLS maintains a comprehensive set of letter forms - one for each scenario, periodically updated with current letterhead. **Always start from a SDLS-provided form.** If you encounter a scenario no existing form covers, don't invent your own - talk to SDLS and you'll collaborate on a new one. The LSC who builds bespoke letters is the LSC whose work is one inconsistency away from a courtroom problem.",
      },
      {
        kind: "checklist",
        heading: "Before drafting any letter",
        items: [
          "Run a fresh **MCCS** to confirm the case is still in the state you think (warrant still active, fine still owed, etc.)",
          "If preparing a Time-Served letter: confirm the participant's custody dates are **after** the offense date",
          "Pull the **court Administrator's name + credentials** + correct fax / email from the [LSC Court Directory](/lookup) (the live Google Sheet - don't use a 6-month-old letter as your source for contact info)",
          "Pull every needed **backup document** - DOC, PROMIS/Gavel, JOC, drug-program completion certificates",
          "Identify **all case numbers** that should appear in the Re: line (look across the participant's full MCCS results)",
          "Have **NJRC's most current letterhead** open - CLO distributes updates periodically",
        ],
      },
      {
        kind: "links",
        heading: "Related topics",
        items: [
          { label: "The court-letter pre-send checklist", href: "/how-to/court-letter-checklist", description: "Sixteen things to verify before signing in blue ink" },
          { label: "What attachments go behind each kind of court letter?", href: "/how-to/letter-backup-materials", description: "Time-served vs. warrant-recall vs. MVC backup chains" },
          { label: "Write a Time-Served Letter", href: "/how-to/write-a-time-served-letter" },
          { label: "Write a Warrant-Recall Letter", href: "/how-to/write-a-warrant-recall-letter" },
          { label: "Write an MVC Correction Letter", href: "/how-to/write-a-mvc-correction-letter" },
          { label: "When and how do I send: fax vs. email vs. certified mail?", href: "/how-to/certified-mail-protocol" },
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Set the participant's expectations on timing",
        text:
          "Most courts respond within 2-3 weeks. But \"respond\" can mean: granting the request, scheduling a hearing, or asking for more documentation. Tell the participant up front: \"This will take a few weeks. If we don't hear back in 21 days, I'll follow up with the Administrator. If we get a hearing date, you'll need to be there.\"",
      },
      {
        kind: "divider",
        label: "What success looks like",
      },
      {
        kind: "paragraph",
        text:
          "Below: a real time-served letter NJRC sent to the Cherry Hill Municipal Court (some details redacted), and the actual court response - Judge Shamey vacated **$291** in time-payment fines after reviewing the letter. **This is the deliverable.** Court letters work; this is what \"success\" looks like in your inbox.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/letter-time-served-example.png",
        caption: "Real NJRC time-served letter to Cherry Hill Municipal Court (LSM 2026 · LSC New Hire Packet · participant name redacted). Built on SDLS's template.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/court-response-vacated-fine.png",
        caption: "🎉 The court's response - Readington Township MCA confirms Judge Shamey vacated the $291 time-payment after reviewing the NJRC letter. This is what success looks like.",
      },
    ],
  },

  {
    id: "court-letter-checklist",
    category: "court-letters",
    title: "The court-letter pre-send checklist",
    summary:
      "Sixteen items the LSM 2026 Module 5 says you must verify before sending any court letter. Skip one and the letter can cost the participant a court date - or worse.",
    source: "LSM 2026 · Module 5",
    estimatedTime: "5 min per letter",
    difficulty: "medium",
    keywords: ["letters", "checklist", "verify", "pre-send", "qa"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**The corpus marks this checklist:** *\"DO NOT SEND ANY LETTER THAT RUNS AFOUL OF ANY ITEM IN THIS CHECKLIST.\"* Treat that literally.",
          "Run the checklist on **every letter, every time** - a senior LSC running the same letter type they've sent dozens of times. Familiarity is when mistakes slip in.",
          "**When in doubt about ANY aspect of an unsent letter - NO MATTER WHAT IT IS - reach out to SDLS.** Better to delay one day than to send something wrong.",
          "**Periodically, the CLO will distribute updated letterhead.** Always use the **most up-to-date letterhead.**",
        ],
      },
      {
        kind: "callout",
        tone: "danger",
        title: "Do not send any letter that fails this checklist",
        text:
          "Every line below has cost a Participant a court date or worse at some point. Run this checklist on EVERY letter before it leaves the office. The corpus literally says: *\"DO NOT SEND ANY LETTER THAT RUNS AFOUL OF ANY ITEM IN THIS CHECKLIST.\"*",
      },
      {
        kind: "checklist",
        heading: "Verify before sending",
        items: [
          "DATE is correct",
          "Transmission method is correctly labeled (BY FAX, BY EMAIL)",
          "COURT NAME and ADDRESS are correct",
          "SPELLING of the COURT ADMINISTRATOR'S NAME is correct",
          "Court Administrator's credentials are included if applicable (usually CMCA - Certified Municipal Court Administrator)",
          "ALL CASE NUMBERS appear after the Participant's name in the Re: line",
          "Attached Additional Details pages are in the SAME ORDER as the case numbers in the Re: line",
          "Correct GENDER courtesy title for the Court Administrator (Dear Mr./Ms. ___:)",
          "Find/replace the PARTICIPANT'S NAME so it's correct and consistent throughout",
          "Use the most complete name available in Salesforce (FIRST MIDDLE LAST) when first identifying the Participant + in the CC line",
          "Use SINGULAR if there's one matter, PLURAL if more than one",
          "VERB TENSES are consistent with the singular/plural choice",
          "ALL ATTACHMENTS are included",
          "SIGN IN BLUE INK (not black)",
          "CC the Participant BY EMAIL",
          "Use the most up-to-date letterhead (CLO distributes updates periodically)",
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "When in doubt, ask SDLS",
        text:
          "Better to delay a letter by a day to verify with SDLS than to send something wrong.",
      },
    ],
  },

  {
    id: "write-a-time-served-letter",
    category: "court-letters",
    title: "How do I write a Time Served / Fine Waiver letter?",
    summary:
      "Three-stage process per LSM 2026 Module 5: Frame the Request → Assemble the Backup Materials in order → Finalize and Send. Backup chain: DOC → PROMIS/Gavel → JOC → drug program completion → participant fallback.",
    source: "LSM 2026 · Module 5",
    estimatedTime: "30 min per letter",
    difficulty: "medium",
    keywords: ["time served", "fine waiver", "letter", "court", "vacate"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**Purpose** - the participant has served prison, jail, or drug-program time **after the date the fine accrued**. The letter respectfully requests that the court grant time-served credit to cover the existing fines. **Desired result:** court vacates the fines and dismisses the matter(s).",
          "**Three stages:** (1) **Frame the Request** - pull case numbers from MCCS, prepare letter from SDLS-provided form, run against the [[court-letter-checklist]]. (2) **Assemble Backup Materials in order.** (3) **Finalize and Send.**",
          "**Backup chain (in order):** DOC Offender Info → PROMIS/Gavel sentence pages → relevant Judgment(s) of Conviction → drug-program completion letter/certificate. **If none of those are available**, ask the participant for jail face sheet, probation discharge paperwork, or incarceration-confirmation letter from probation/parole officer. **If still nothing**, consult SDLS.",
          "**Don't draft from scratch.** Always start from the SDLS-provided form. If no existing form covers the scenario, work with SDLS to create one.",
          "**Always run the [[court-letter-checklist]]** before sending. **SIGN IN BLUE INK. CC the Participant by email.**",
          "**Address to the Court Administrator** - use the [LSC Municipal Court Information Google Sheet](/lookup) (live data) or the court website if not in the Sheet. Same source for fax and email.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "**Conversion of Fines to Time Served** is one of the three most common municipal-court letter types. Use it when **the participant has served prison, jail, or drug-program time AFTER the date on which the fine accrued**. The letter respectfully requests that the court **grant time-served credit to cover the existing fines**. The desired result: **the court vacates the fines and dismisses the matter(s)**.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Use the in-app Fine Waive Letter generator",
        text:
          "The Floating Dock on every Person page has a 'Fine Waive Letter' action that auto-fills today's date, participant name, case numbers, and address from the NJ court directory. Use that as your starting point - then attach the backup materials below.",
      },
      {
        kind: "steps",
        heading: "Stage 1 - Frame the Request",
        items: [
          { title: "Identify all case numbers from MCCS", body: "Every case at this court that you're asking to vacate. The Re: line lists each one in the same order as the attached Additional Details pages." },
          { title: "Prepare letter from the SDLS-provided form", body: "Don't draft from scratch. SDLS maintains a comprehensive set of forms covering a broad spectrum of issues. If no existing form fits the scenario, **the LSC and SDLS will collaborate to create a new one**." },
          { title: "Review letter against the [[court-letter-checklist]]", body: "All 16 items. The corpus literally says: \"DO NOT SEND ANY LETTER THAT RUNS AFOUL OF ANY ITEM IN THIS CHECKLIST.\"" },
        ],
      },
      {
        kind: "steps",
        heading: "Stage 2 - Assemble the Backup Materials (IN THIS ORDER)",
        items: [
          { title: "1. NJ DOC Offender Info", body: "If available/appropriate. See [[doc-inmate-finder]]. Print full Offender Details Page(s)." },
          { title: "2. Sentence information detail page(s) from PROMIS/Gavel", body: "If available/appropriate. One per relevant sentence. See [[promis-gavel-search]]. For relief letters, print the **Sentence Details Page**, not the Defendant Detail page." },
          { title: "3. Relevant Judgment of Conviction(s)", body: "If available/appropriate. See [[judgment-of-conviction]]. Note: JOC search returns only judgments dated AFTER June 30, 2017." },
          { title: "4. Drug program completion letter(s) / certificate(s)", body: "If applicable and available." },
          { title: "Fall-back: obtain from the participant", body: "If none of the above are available, obtain from the participant any of the following - **jail face sheet**, **probation discharge paperwork**, or an **incarceration-confirmation letter from a probation or parole officer**. If still none of these are available, **consult SDLS**." },
        ],
      },
      {
        kind: "steps",
        heading: "Stage 3 - Finalize and Send",
        items: [
          { title: "Address letter to the Court Administrator", body: "Use the LSC Municipal Court Information Google Sheet (live data, surfaced at [/lookup](/lookup)) - or the court website if not in the Sheet." },
          { title: "Identify court fax number + all applicable email addresses", body: "Same source - Sheet first, court website if not in the Sheet." },
          { title: "Sign in blue ink", body: "**Always.** Black ink can read as a photocopy and creates ambiguity." },
          { title: "CC the Participant by email", body: "**Always.** Use the most complete name available in Salesforce (FIRST MIDDLE LAST) in the CC line." },
          { title: "Use the most up-to-date letterhead", body: "Periodically, the CLO will distribute updated letterhead featuring changes - make sure you're on the current version." },
        ],
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/letter-time-served-example.png",
        caption: "Anonymized real Time Served letter NJRC sent to Cherry Hill Municipal Court - built on SDLS's template, with case number, time-served narrative, and signed in blue ink by the LSC.",
      },
    ],
  },

  {
    id: "write-a-warrant-recall-letter",
    category: "court-letters",
    title: "How do I write a Warrant Recall letter?",
    summary:
      "Same three-stage Frame / Assemble / Finalize structure as a Time-Served letter - but warrant recalls usually need NO backup materials. Two exceptions trigger the full backup chain.",
    source: "LSM 2026 · Module 5",
    estimatedTime: "20 min per letter",
    difficulty: "medium",
    keywords: ["warrant", "recall", "letter", "court", "expedited hearing"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**Purpose** - the participant is the subject of one or more **active Municipal Court warrants**. The letter respectfully requests that the court **recall the warrant and calendar a hearing**. **Desired result:** court recalls the warrant, **revokes any bail**, and sets a date for a hearing.",
          "**Same three stages:** Frame the Request → (No backup, usually) → Finalize and Send.",
          "**No backup materials, EXCEPT in two cases:** (1) the letter seeks relief from **BOTH fines AND warrants**, OR (2) there is **time served available to support dismissal of the warrant matter**. In those cases, attach the **same materials as a Time-Served letter** ([[write-a-time-served-letter]]).",
          "**Address to the Court Administrator** - same as Time-Served: live Google Sheet first, court website if not in the Sheet.",
          "**SIGN IN BLUE INK. CC the Participant by email.** Run the [[court-letter-checklist]] before sending.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "**Warrant Recall Request** is the second of the three most common municipal-court letter types. Use it when **the participant is the subject of one or more active Municipal Court warrants**. The letter **respectfully requests that the court recall the warrant and calendar a hearing**. The desired result: **the court recalls the warrant, revokes any bail, and sets a date for a hearing.**",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Use the in-app Warrant Letter generator",
        text:
          "The Floating Dock has a 'Warrant Letter' action that auto-fills date, participant name, and case numbers. Backup materials are usually not needed.",
      },
      {
        kind: "steps",
        heading: "Stage 1 - Frame the Request",
        items: [
          { title: "Identify all case numbers from MCCS", body: "Every case at this court with an active warrant. Re: line lists each one." },
          { title: "Prepare letter from the SDLS-provided form", body: "Don't draft from scratch." },
          { title: "Review letter against the [[court-letter-checklist]]", body: "All 16 items." },
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "Stage 2 - Backup Materials (USUALLY not required for warrants)",
        text:
          "**EXCEPT** when **(1)** the letter is seeking relief from **both fines AND warrants**, OR **(2)** there is **time served available to support a dismissal of the warrant matter**. In those cases, attach **the same materials as a Time-Served letter** ([[write-a-time-served-letter]]) - DOC Offender Info → PROMIS/Gavel sentence page → relevant JOC → drug-program completion → participant fallback.",
      },
      {
        kind: "steps",
        heading: "Stage 3 - Finalize and Send",
        items: [
          { title: "Address letter to the Court Administrator", body: "Use the LSC Municipal Court Information Google Sheet - or the court website if not in the Sheet." },
          { title: "Identify court fax number + all email addresses", body: "Same source - Sheet first, website if not." },
          { title: "Sign in blue ink, CC the participant by email", body: "Always. Use the most complete name available in Salesforce (FIRST MIDDLE LAST) in the CC line." },
          { title: "Use the most up-to-date letterhead", body: "" },
        ],
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/letter-warrant-recall-example.png",
        caption: "Anonymized real NJRC letter to Deptford Municipal Court (LSM 2026 · LSC New Hire Packet) - combined warrant-recall + time-served request format.",
      },
    ],
  },

  {
    id: "write-a-mvc-correction-letter",
    category: "court-letters",
    title: "How do I write an MVC Suspension Correction letter?",
    summary:
      "When MCCS says the matter was disposed (or doesn't show it at all) but MVC insists there's still a suspension. Asks the court to either resolve the suspension with MVC directly OR provide a written disposition NJRC can transmit to MVC's Suspension Department.",
    source: "LSM 2026 · Module 5",
    estimatedTime: "30-45 min per letter",
    difficulty: "hard",
    keywords: ["mvc correction", "suspension", "lrrr", "license restoration"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**Purpose** - the participant obtained information from MVC (or the LSC obtained it via a [[license-restoration-requirements-report]]) about cases that **MCCS indicated had been disposed** OR that **did not appear in MCCS at all**, yet MVC still shows them as suspending the license.",
          "**The ask:** the letter respectfully requests that the court **either resolve the suspension with MVC directly, OR provide an official written disposition** that NJRC can transmit to MVC's Suspension Department.",
          "**Desired result:** the court provides an **official written disposition** OR confirms that **it will resolve the matter with MVC**.",
          "**Stage 1 - Frame the Request:** identify case numbers from MCCS **(if any)** · **copy pages from the LRRR if available** · prepare letter from SDLS form · run [[court-letter-checklist]].",
          "**Stage 2 - Assemble Backup Materials in order** (same chain as Time Served): DOC Offender Info → PROMIS/Gavel sentence pages → JOC → drug-program completion → participant fallback (jail face sheet, probation paperwork, PO letter, or escalate to SDLS).",
          "**Stage 3 - Finalize and Send:** address to the **Court Administrator (as identified on court website)** - MVC Correction letters often go to courts the live Sheet may not have current info for. Rely on the court website. Same blue-ink-signature, CC-the-participant-by-email rules.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "**MVC Suspension Correction** is the third of the three most common municipal-court letter types. Use it when **the participant obtained information from MVC** - or the LSC obtained the information in a **License Restoration Requirements Report** - regarding **cases that MCCS indicated had been disposed or that did not appear in MCCS at all**. The letter respectfully requests that the **court communicate with MVC** to resolve the suspension, or **provide a disposition** that the LSC can transmit to MVC's suspension department. The desired result: **the court either provides an official written disposition or confirms that it will resolve the matter with MVC.**",
      },
      {
        kind: "steps",
        heading: "Stage 1 - Frame the Request",
        items: [
          { title: "Identify all case numbers from MCCS (if any)", body: "There may be no MCCS hits if the case is too old or too obscure to appear. That's part of why this letter exists." },
          { title: "Copy pages from the LRRR if available", body: "If you have a License Restoration Requirements Report, the relevant pages are powerful documentation that MVC's records show the suspension. Attach them." },
          { title: "Prepare letter from the SDLS-provided form", body: "Don't draft from scratch." },
          { title: "Review letter against the [[court-letter-checklist]]", body: "All 16 items." },
        ],
      },
      {
        kind: "steps",
        heading: "Stage 2 - Assemble the Backup Materials (IN THIS ORDER)",
        items: [
          { title: "1. NJ DOC Offender Info", body: "If available/appropriate. See [[doc-inmate-finder]]." },
          { title: "2. PROMIS/Gavel sentence detail page(s)", body: "If available/appropriate. See [[promis-gavel-search]]." },
          { title: "3. Relevant Judgment of Conviction(s)", body: "If available/appropriate. See [[judgment-of-conviction]]." },
          { title: "4. Drug program completion letter(s) / certificate(s)", body: "If applicable and available." },
          { title: "Fall-back: ask the participant", body: "If none of the above are available, obtain from the participant: jail face sheet, probation discharge paperwork, incarceration confirmation letter from probation/parole officer. **Consult SDLS if none of these are available.**" },
        ],
      },
      {
        kind: "steps",
        heading: "Stage 3 - Finalize and Send",
        items: [
          { title: "Address letter to the Court Administrator - as identified on the court website", body: "MVC Correction letters often go to courts where the live Sheet may not have current Administrator info. **Rely on the court website** for these." },
          { title: "Identify court fax number + all email addresses", body: "Sheet first, court website if not." },
          { title: "Sign in blue ink, CC the participant by email", body: "" },
          { title: "Use the most up-to-date letterhead", body: "" },
        ],
      },
    ],
  },

  // ─── LICENSE RESTORATION ──────────────────────────────────────────
  {
    id: "license-restoration-decision-tree",
    category: "license-restoration",
    title: "What's the License Restoration decision tree?",
    summary:
      "Four questions about the participant's license status drive the entire restoration path. Plus the order of barriers MVC will check.",
    source: "LSM 2026 · Module 6",
    estimatedTime: "3 min read",
    difficulty: "easy",
    keywords: ["license", "restoration", "decision tree", "permit", "suspended"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**NJRC will pay for one permit and one Driver License or Non-Driver ID per participant per calendar year.** If they need a replacement after NJRC has already paid this year, they bring a prepaid debit/credit card or wait until the next calendar year.",
          "**Four questions** drive the restoration path: current license status · expiration · MCCS suspensions · surcharges/restoration fees.",
          "**Barriers come in a fixed order:** court suspensions (DL Suspended / Closed to MVC) → surcharges → restoration fees. MVC won't issue a license until all three are resolved.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "In addition to State IDs, NJRC's Legal Services Team assists participants with **applying for, recovering, and restoring Driver Licenses**. NJRC will pay for **one permit and one Driver License (or Non-Driver ID) per participant per calendar year**. If a participant needs a replacement after NJRC has already purchased a license that year, the participant may bring a prepaid debit or credit card to an NJRC office and staff can help them order a duplicate license online - or they can wait until the next calendar year.",
      },
      {
        kind: "checklist",
        heading: "Ask these four questions first",
        items: [
          "**Does the participant currently have a Driver License (valid or otherwise)?** If not, has the participant ever had a Driver License?",
          "**Is the participant's Driver License expired?** If so, how long ago did it expire?",
          "**Are there any 'DL Suspended' and/or 'Closed to MVC' cases or tickets in MCCS?**",
          "**Does the participant owe surcharges or restoration fees?**",
        ],
      },
      {
        kind: "paragraph",
        text:
          "These answers determine the next steps the LSC takes to restore the participant's Driver License.",
      },
      {
        kind: "table",
        heading: "What the answers tell you",
        columns: ["Situation", "Path"],
        rows: [
          ["Never had a license **OR** expired > 3 years", "Start with an [[first-time-driver-license]]: Exam Permit → Knowledge Test → Road Test → first-time Driver License (Probationary)"],
          ["Expired ≤ 1 year (or up to 90 days before)", "Online renewal eligible - see [[renew-driver-license]]"],
          ["Expired 1-3 years", "Must renew in person at an MVC Center"],
          ["Has DL Suspended / Closed to MVC cases in MCCS", "Resolve those FIRST via court letters ([[when-to-write-a-court-letter]]) before MVC will issue a license"],
          ["Owes surcharges", "Run [[surcharge-violation-search]]; participant pays or files [[surcharge-hardship-letter]]"],
          ["Owes restoration fee", "$100 each for DL + Registration; [[restoration-fee-search]]; NJRC pays AFTER other suspensions clear"],
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "Order matters",
        text:
          "MVC checks barriers in a fixed order. Court suspensions block surcharge resolution; surcharges block restoration fees; restoration fees block license issuance. **Don't pay restoration fees until everything else is clear** - at best, you waste NJRC funding; at worst, you get the participant's hopes up before the actual barrier surfaces.",
      },
      {
        kind: "callout",
        tone: "info",
        title: "When the search tools don't add up",
        text:
          "If your databases (MCCS + NJSVS + Restoration Fee Search) don't tell you why restoration is blocked, escalate via the [[mvc-suspension-line-call]] or request an [[license-restoration-requirements-report]] (LRRR) from SDLS.",
      },
    ],
  },

  {
    id: "first-time-driver-license",
    category: "license-restoration",
    title: "How do I help a Participant get a first-time Driver License?",
    summary:
      "Exam Permit → Knowledge Test → Road Test → Probationary License → full Driver License after 1 year. Same path applies to anyone whose license expired more than 3 years ago.",
    source: "LSM 2026 · Module 6",
    estimatedTime: "Multiple visits over weeks",
    difficulty: "medium",
    keywords: ["first time", "driver license", "permit", "knowledge test", "road test", "probationary"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**Applies to first-time drivers AND anyone whose Driver License has been expired more than 3 years.**",
          "**Step 1 is an Exam Permit** - valid for up to **6 months**, lets the participant take the Knowledge Test.",
          "**An Exam Permit ≠ a Learner Permit.** An Exam Permit does NOT allow the participant to drive. It only authorizes the Knowledge Test.",
          "**Knowledge Test pass threshold: ≥ 80%.** Can be retaken as many times as needed within the 6-month permit window.",
          "Passing the Knowledge Test → **5-digit code is written on the Exam Permit**, used to schedule the Road Test.",
          "**NJRC staff may assist with transporting** the participant to MVC during regular MVC trips. **Road Tests are not available at all MVC locations** - confirm before scheduling.",
          "LSCs can help schedule MVC appointments via the **tele.gov portal**.",
          "Once the Road Test is scheduled, the **participant is responsible** for going to the designated location and **providing a vehicle**.",
          "**First-time drivers receive a Probationary License**, upgradable to a full Driver License after 1 year.",
          "**Pre-existing drivers** (previously held a license, now expired > 3 years) go through the same exam steps but get a **full Driver License immediately** on passing - no Probationary period.",
          "If NJRC helped obtain the permit, **scan + upload to Salesforce** and email SDLS for a profile update.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "For a first-time driver - or for a driver whose Driver License has been expired for **more than three years** - the participant must first obtain an **Exam Permit**. In New Jersey, an Exam Permit is valid for up to **six months** and allows the permit holder to take the Knowledge Test. **An Exam Permit is different from a Learner Permit because it does not allow the participant to drive.** During the six-month period, the permit holder may take the Knowledge Test as many times as needed until they pass with a score of **at least 80%**.",
      },
      {
        kind: "paragraph",
        text:
          "After the Knowledge Test is passed, the permit holder receives a **five-digit code** (written on the Exam Permit) that is used to schedule the Road Test. **NJRC staff may assist with transporting the participant to MVC** to obtain the permit and take the Knowledge Test during regular MVC trips. *(Note: Road Tests are not available at all MVC locations.)* LSCs can also assist participants with scheduling MVC appointments through the **tele.gov portal**. Once the Road Test is scheduled, **the participant is responsible** for going to the designated location and **providing a vehicle for the test**.",
      },
      {
        kind: "paragraph",
        text:
          "After passing the Road Test, the participant is eligible to obtain a Driver License. **First-time drivers receive a Probationary License**, which can be upgraded to a full Driver License **after one year**. **Pre-existing drivers do not receive a Probationary License** upon passing the Road Test and may obtain a full Driver License immediately.",
      },
      {
        kind: "steps",
        heading: "The full path, in order",
        items: [
          { title: "Obtain Exam Permit at MVC", body: "NJRC staff can transport the participant during regular MVC trips. Permit is valid up to 6 months." },
          { title: "Pass the Knowledge Test (≥ 80%)", body: "Can be taken as many times as needed during the 6-month permit window." },
          { title: "Get the 5-digit Road Test code", body: "Written on the Exam Permit after passing the Knowledge Test." },
          { title: "Schedule the Road Test", body: "LSC can help schedule via the tele.gov portal. Confirm the chosen MVC location actually offers Road Tests - not all do." },
          { title: "Take the Road Test", body: "Participant goes to the location and provides their own vehicle for the test." },
          { title: "Receive license", body: "**First-time drivers** → Probationary License (upgradable to full DL after 1 year). **Pre-existing drivers** → full Driver License immediately." },
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "After permit acquisition: scan + Salesforce update",
        text:
          "If the permit was obtained with NJRC assistance, **scan it, upload it to the participant's Salesforce profile, and email SDLS to request a Salesforce update**. Don't wait - capture it the same day.",
      },
    ],
  },

  {
    id: "renew-driver-license",
    category: "license-restoration",
    title: "How do I renew a Driver License?",
    summary:
      "Online if expired ≤ 1 year (or up to 3 months before). In-person at an MVC Center if expired 1-3 years. Full first-time re-test if expired more than 3 years. Address must be current before the portal will work.",
    source: "LSM 2026 · Module 6",
    estimatedTime: "20 min for online renewal",
    difficulty: "medium",
    keywords: ["renew", "renewal", "driver license", "online", "expired"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**Online renewal window: up to 3 months BEFORE expiration through up to 1 year AFTER expiration.**",
          "**Expired 1-3 years** → must renew **in person at an MVC Center**.",
          "**Expired more than 3 years** → treated as first-time - full Exam Permit / Knowledge Test / Road Test process. See [[first-time-driver-license]].",
          "**Required online inputs:** ZIP Code · NJ ID number · SSN or DOB · first 6 letters of last name.",
          "**Confirm address before proceeding.** If the address is incorrect or the participant can't receive mail there, complete a [[change-of-address-mvc]] FIRST. The renewal portal becomes accessible almost immediately after the Change of Address is submitted.",
          "**Confirm no suspensions** in NJ or out of state before requesting the renewal.",
          "If the LSC can navigate to the payment page → the renewal is eligible. **Email an ID/License Renewal Request to SDLS** rather than paying directly.",
          "A duplicate Driver License is ordered the same way - see [[duplicate-driver-license]].",
          "**MVC's online portal does NOT distinguish between Driver Licenses and State IDs.** If the participant has both, the portal will order whichever credential was **issued most recently**. If the participant needs the DL but recently received a State ID, **they must go in person to an MVC Center** for the DL.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "As with State ID renewals in New Jersey, a participant's Driver License may be renewed online **up to three months before its expiration date and up to one year after**. If a Driver License has been **expired for more than one year but fewer than three years**, it must be renewed **in person at an MVC Center**. If it has been expired **more than three years**, the participant goes through the full first-time process (see [[first-time-driver-license]]).",
      },
      {
        kind: "paragraph",
        text:
          "Using the participant's **ZIP Code, NJ ID number, SSN/DOB, and the first six letters of the participant's last name**, the LSC can access the Driver License renewal portal. **Confirm the participant's address information before proceeding.** If the address is incorrect - or the participant cannot receive mail there - complete a [[change-of-address-mvc]] first (using the similar online portal). After a Change of Address is submitted, the LSC should be able to access the Renewal portal almost immediately.",
      },
      {
        kind: "paragraph",
        text:
          "In the renewal portal, **confirm that the participant has no suspensions in New Jersey or out of state**. As with State ID renewal, **if the LSC can navigate to the payment page**, the Driver License is eligible for renewal and the LSC can **email a License Renewal Request to SDLS** instead of paying directly. A duplicate Driver License can be ordered in the same way a duplicate State ID is ordered.",
      },
      {
        kind: "steps",
        heading: "Online renewal - step by step",
        items: [
          { title: "Gather the required inputs", body: "ZIP code on file with MVC · NJ ID number · SSN or DOB · first 6 letters of the participant's last name." },
          { title: "Open the Driver License renewal portal", body: "On NJ MVC's website. Same family of portals as State ID renewals." },
          { title: "Confirm the address on file", body: "If wrong or the participant can't receive mail there, **complete a Change of Address first** ([[change-of-address-mvc]]). After Change of Address is submitted, the Renewal portal becomes accessible almost immediately." },
          { title: "Confirm no suspensions", body: "Cross-check NJ and out-of-state suspensions before proceeding." },
          { title: "Navigate to the payment page (do NOT pay through the LSC's session)", body: "If you reach the payment page, the renewal is eligible. Close the portal - don't pay. Email SDLS a License Renewal Request instead." },
          { title: "SDLS handles payment + Salesforce update", body: "SDLS will email you the receipt; print or forward to the participant." },
        ],
      },
      {
        kind: "callout",
        tone: "danger",
        title: "Important: MVC's online portal can't tell DLs from State IDs apart",
        text:
          "**The MVC online portals for duplicates and renewals do not distinguish between State IDs and Driver Licenses.** If a participant has both, the portal will order **whichever credential was issued most recently**. If the participant needs to renew or replace a Driver License but recently ordered a State ID, **the participant must go in person to an MVC Center** to obtain the Driver License. See [[mvc-portal-duplicate-trap]] for the full diagnosis.",
      },
    ],
  },

  {
    id: "duplicate-driver-license",
    category: "license-restoration",
    title: "How do I order a Duplicate Driver License?",
    summary:
      "Same workflow as a renewal - through MVC's online portal - for a license that's lost or damaged. Confirm the address first; mind the DL/State-ID portal trap.",
    source: "LSM 2026 · Module 6",
    estimatedTime: "15 min",
    difficulty: "easy",
    keywords: ["duplicate", "lost", "damaged", "driver license", "replacement"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**Same workflow as Driver License renewal** - through MVC's online duplicate/renewal portal. See [[renew-driver-license]] for the full input list.",
          "**Confirm address first.** If the participant's address on file is outdated or they can't receive mail there, complete a [[change-of-address-mvc]] before requesting the duplicate.",
          "**The DL/State-ID portal trap applies.** The portal will issue whichever credential was issued most recently - see [[mvc-portal-duplicate-trap]].",
          "If the LSC reaches the **payment page**, the duplicate is viable - close the portal and **email SDLS a Duplicate DL Request**.",
          "Subject to NJRC's **one DL or Non-Driver ID per participant per calendar year** funding limit.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "A duplicate Driver License is ordered **the same way a duplicate State ID is ordered** - through MVC's online portal. The same restrictions apply: confirm the participant's address before proceeding, complete a [[change-of-address-mvc]] first if needed, and beware the [[mvc-portal-duplicate-trap]] (the portal cannot distinguish between DLs and State IDs and will issue whichever was most recently ordered).",
      },
      {
        kind: "steps",
        heading: "Steps",
        items: [
          { title: "Confirm the participant's address is current with MVC", body: "If outdated or the participant can't receive mail there, run [[change-of-address-mvc]] first. After Change of Address, the duplicate portal is accessible almost immediately." },
          { title: "Enter participant info into the MVC duplicate portal", body: "Same inputs as renewal: ZIP, NJ ID#, SSN/DOB, first 6 of last name." },
          { title: "If you reach the payment page, the duplicate is viable", body: "Close the portal - don't pay through the LSC's session. Email SDLS a Duplicate DL Request." },
          { title: "SDLS pays + sends receipt; LSC scans + uploads to Salesforce", body: "Forward the receipt to the participant; ID arrives by mail in ~20 calendar days." },
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "If the participant has both a State ID and a DL",
        text:
          "Verify which was issued most recently before submitting. If the State ID is the most recent issuance and the participant needs a duplicate of the DL, the **only** path is **in-person at an MVC Center**. The online portal will not let you order the right credential.",
      },
    ],
  },

  {
    id: "surcharge-violation-search",
    category: "license-restoration",
    title: "How do I run a Surcharge Violation System search?",
    summary:
      "Surcharges are fines owed directly to MVC - separate from court fines, not forgivable, and a common barrier to license restoration. NJSVS is how you check the balance and the suspension status.",
    source: "LSM 2026 · Module 6",
    estimatedTime: "5 min",
    difficulty: "easy",
    keywords: ["surcharge", "mvc", "violation", "search", "installment", "restoration", "njsvs"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**Surcharges are MVC fines**, not court fines - owed directly to MVC.",
          "They arise from **too many points on a Driver License** OR from **documentation-related tickets** (driving without a license, without insurance, without registration).",
          "**Search inputs:** participant's **NJ ID number** + **date of birth**.",
          "**Result page** shows the participant's total surcharges owed and (when applicable) a monthly installment plan.",
          "If the result shows a **Surcharge Restoration Amount** instead of an installment plan, the participant's license is usually **suspended for non-payment**. Paying the Restoration Amount converts it back to a monthly plan and lifts the suspension.",
          "Surcharges are **NOT forgivable or negotiable**. NJRC does not have funding to pay them.",
          "Participant **does not need to pay the full balance** to restore - just needs to be in **good standing** = Surcharge Restoration Amount paid.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "Another common barrier to license restoration is the MVC Surcharge. Surcharges are fines owed directly to MVC - separate from court fines. They typically arise when a participant accumulates too many points on their Driver License, or receives certain documentation-related tickets (driving without a license, insurance, or registration).",
      },
      {
        kind: "steps",
        heading: "Run the search",
        items: [
          { title: "Open the NJSVS portal", body: "[NJ Surcharge Violation System](http://www.state.nj.us/mvc/Violations/Surcharges.htm)." },
          { title: "Enter NJ ID number + DOB", body: "Both required. The NJ ID number is the participant's MVC-assigned identifier (same one used for all MVC searches)." },
          { title: "Read the result page", body: "Shows total surcharges owed; a monthly installment plan if the participant has been making minimum payments; OR a Surcharge Restoration Amount if they've fallen behind." },
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "Surcharge Restoration Amount = license-suspension flag",
        text:
          "If the result shows a Surcharge Restoration Amount in place of a monthly installment plan, that usually means the participant's license is suspended for non-payment. Once the Restoration Amount is paid, it converts back to a monthly installment plan, the surcharge suspension is lifted, and license restoration can proceed.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Good standing ≠ paid in full",
        text:
          "The participant does NOT need to pay the full surcharge balance to restore their Driver License. They only need to be in **good standing**, which means **the Surcharge Restoration Amount has been paid**. The remaining balance gets paid down on the monthly installment plan over time.",
      },
      {
        kind: "callout",
        tone: "danger",
        title: "Surcharges are not forgivable or negotiable",
        text:
          "Whatever surcharges MVC has on a participant's record, those balances stand. At the time of writing, **NJRC does not have funding to pay surcharge balances** - surcharge payment is the participant's responsibility. The one lever the LSC has is the [[surcharge-hardship-letter]] to reduce the monthly installment.",
      },
      {
        kind: "links",
        heading: "Related",
        items: [
          { label: "How do I write a Surcharge Hardship Letter?", href: "/how-to/surcharge-hardship-letter", description: "When the monthly installment is more than the Participant can afford." },
        ],
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/njsvs-landing.png",
        caption: "NJSVS landing page - start here. Pick the search type (DL # is most common), enter the participant's info.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/njsvs-payment.png",
        caption: "NJSVS payment screen - what you'll see when there's a surcharge balance. Total amount, restoration amount (license-suspension flag), monthly installment.",
      },
      {
        kind: "links",
        heading: "Resources & contacts",
        items: [
          { label: "NJSVS - NJ Surcharge Violation System", href: "http://www.state.nj.us/mvc/Violations/Surcharges.htm", description: "The portal for surcharge searches + payments." },
          { label: "MVC general inquiry email", href: "mailto:mvcinquiry@gilacorp.com" },
          { label: "Customer Service (Gila - surcharge billing)", href: "tel:+18444246829", description: "844-424-6829 - open Mon-Fri 8am-5pm." },
        ],
      },
    ],
  },

  {
    id: "surcharge-hardship-letter",
    category: "license-restoration",
    title: "How do I write a Surcharge Hardship Letter?",
    summary:
      "When MVC's monthly installment is more than the participant can afford, request a reduction via a formal hardship letter - coordinated through SDLS.",
    source: "LSM 2026 · Module 6",
    estimatedTime: "20-30 min",
    difficulty: "medium",
    keywords: ["surcharge", "hardship", "letter", "mvc", "reduce", "installment"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**Surcharges are NOT forgivable or negotiable.** MVC sets the balance and that balance must be paid by the participant.",
          "**NJRC does not have funding for surcharge balances.** Surcharge payments are the participant's responsibility.",
          "The participant does **not** need to pay the *full* surcharge balance to restore a Driver License. They only need to be in **good standing** - defined as **the Surcharge Restoration Amount paid**.",
          "The Surcharge Hardship Letter is a **formal request to MVC to reduce the monthly installment** based on financial hardship - it does not reduce the balance.",
          "MVC has historically agreed to reduce the monthly installment **only after** the participant makes a one-time payment equal to the previously required monthly amount.",
          "Always start from the **current SDLS-provided hardship letter form** - don't draft from scratch.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "Surcharges are fines owed directly to MVC (see [[surcharge-violation-search]]). They typically arise when a participant accumulates too many points on their Driver License or receives certain documentation-related tickets - driving without a license, insurance, or registration. Surcharges can be for any amount, and **MVC does not forgive or negotiate them**. NJRC, at the time of writing, does not have funding to pay surcharge balances; that is the participant's responsibility.",
      },
      {
        kind: "paragraph",
        text:
          "The participant **does not need to pay the full balance to restore their Driver License.** They only need to be in good standing with MVC, which means the **Surcharge Restoration Amount** has been paid. From there, the remaining balance can be paid down on a monthly installment plan.",
      },
      {
        kind: "paragraph",
        text:
          "When the standard monthly installment plan is more than the participant can afford, the LSC can help the participant submit a **Surcharge Hardship Letter** to MVC - a formal request to reduce the monthly payment based on financial hardship. The letter goes to the email address MVC currently uses for hardship requests; SDLS has the current address.",
      },
      {
        kind: "callout",
        tone: "info",
        title: "How MVC has historically responded",
        text:
          "MVC has generally agreed to reduce the monthly installment plan, but **only after** the participant makes a one-time payment equal to the previously required monthly amount. So if the participant has been on a $100/month plan, MVC's typical move is: pay $100 once now, future installments reduce.",
      },
      {
        kind: "steps",
        heading: "How to submit",
        items: [
          { title: "Get the current hardship letter form from SDLS", body: "The form is updated periodically. Don't draft from scratch." },
          { title: "Complete with the participant's financial information", body: "The form will tell you exactly what's required. Don't fill in fields the form doesn't ask for." },
          { title: "Send to MVC's hardship-request email address", body: "SDLS has the current address. CC SDLS on the email so they have the record." },
          { title: "Track + follow up", body: "MVC's response usually comes within 4-6 weeks. If the response is conditional (\"pay one-time amount of $X first\"), coach the participant through the payment + reply." },
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "Set expectations honestly with the participant",
        text:
          "The hardship letter reduces the **monthly burden**, not the balance. The participant is still on the hook for the full surcharge total - it just gets paid over a longer time. Make sure the participant understands this before you send the letter.",
      },
    ],
  },

  {
    id: "restoration-fee-search",
    category: "license-restoration",
    title: "How do I run a Restoration Fee search?",
    summary:
      "The final fee gate before MVC will issue a license - up to two restoration fees ($100 each). NJRC pays them, but only after all other suspension issues are resolved.",
    source: "LSM 2026 · Module 6",
    estimatedTime: "5 min",
    difficulty: "easy",
    keywords: ["restoration fee", "license", "registration", "search", "$100"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**Up to two restoration fees** may be owed at one time: a **Driver License Restoration Fee** AND a **Registration Restoration Fee**.",
          "Each is **always $100.00** - fixed amount, not negotiable.",
          "Both must be **paid before** a participant can apply for a new or renewed Driver License.",
          "Restoration fees are commonly owed **after DL suspensions**.",
          "**NJRC will pay** the participant's restoration fees - but **only after all other suspension issues are resolved** (court suspensions cleared, surcharges in good standing).",
          "Verify by entering the participant's info in the License Restoration portal - if you reach the payment page, the fee is owed and payable.",
          "Then email SDLS a **License/Registration Restoration Request**.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "The final obstacle that can prevent a participant from obtaining a Driver License is the **Restoration Fee**. Up to two restoration fees may be owed at one time: a **Driver License Restoration Fee** and a **Registration Restoration Fee**. These fees must be paid before a participant can apply for a new or renewed Driver License. Restoration fees are commonly owed after DL suspensions and are always **$100.00 each**.",
      },
      {
        kind: "paragraph",
        text:
          "At the time of writing, **NJRC will pay a participant's restoration fees** - but only **after all other suspension issues are resolved**. The order matters: clear court suspensions first ([[when-to-write-a-court-letter]]), get the participant's surcharges into good standing ([[surcharge-violation-search]]), then come to the restoration fee.",
      },
      {
        kind: "steps",
        heading: "How to verify + request payment",
        items: [
          { title: "Confirm all suspensions cleared", body: "Run a fresh MCCS - no DL Suspended, no Closed to MVC entries. Run NJSVS - surcharge balance is on a current installment plan." },
          { title: "Verify the participant's Driver License info in Salesforce", body: "Restoration Information section. NJ ID#, ZIP code on file with MVC, SSN if known." },
          { title: "Enter the info in the License Restoration portal", body: "If you can navigate to the payment page, the fee is owed and payable. The portal will show whether one or both fees are owed." },
          { title: "Email SDLS a License/Registration Restoration Request", body: "Include the participant's name, NJ ID#, and which fees are owed. SDLS pays from NJRC's restoration-fee account." },
          { title: "Upload SDLS's payment confirmation to Salesforce", body: "When SDLS sends back the receipt, scan + upload to the participant's profile. Add a Case Note." },
        ],
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/mymvc-login.png",
        caption: "MyMVC Login screen - the entry point. Needs ZIP, DL/ID number, SSN. The 'I do not have a Social Security Number' link handles SSN-less participants.",
      },
      {
        kind: "links",
        heading: "Resources & contacts",
        items: [
          { label: "MyMVC Restoration Fees portal", href: "https://mymvc.state.nj.us/restoration-fees/", description: "The actual portal you log into to check + pay restoration fees." },
          { label: "MyMVC login (alternate URL)", href: "https://mymvc.state.nj.us/restoration-fees/login.xhtml", description: "Direct link to the login form." },
          { label: "NJ MVC main portal", href: "https://www.state.nj.us/mvc/", description: "All other MVC services." },
          { label: "MVC Suspension Office", href: "tel:+16093252885", description: "(609) 325-2885 - call here when restoration is blocked and you can't tell why." },
        ],
      },
    ],
  },

  {
    id: "mvc-suspension-line-call",
    category: "license-restoration",
    title: "How do I call the MVC Suspension Line with a Participant?",
    summary:
      "When databases don't tell you what's blocking restoration - get it straight from MVC.",
    source: "LSM 2026 · Module 6",
    estimatedTime: "30-60 min on-call",
    difficulty: "medium",
    keywords: ["mvc", "suspension line", "call", "lrrr", "phone"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**MVC's Suspension Office** has **direct access to a participant's driving record + history**, and can provide the same info as a License Restoration Requirements Report - making it a valuable resource for restoration questions.",
          "**Use the Suspension Line when your databases can't tell you why restoration is blocked** - when MCCS, NJSVS, and the Restoration Fee Search don't add up to the participant's actual barriers.",
          "The **call instructions** live behind the front page of the Legal Assessment.",
          "Behind the call sheet is a **Notes Page** the participant uses to record what they hear during the call.",
          "Capture: **ticket numbers, municipalities, and any other reason for the suspension** - child support warrants, unpaid probation fines, alcohol interlock device fees, Intoxicated Driver Resource Center (IDRC) requirements, multiple ID numbers, default judgments in auto accident cases, etc.",
          "**If the participant can't complete the call** or doesn't record the info legibly: the LSC can submit a [[license-restoration-requirements-report]] (LRRR) request to SDLS, who forwards it to NJRC's MVC contact.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "The **MVC Suspension Office** has direct access to a participant's driving record and history - including the same information contained in a License Restoration Requirements Report. That makes it a valuable resource for restoration questions. **If the LSC cannot determine what is preventing restoration using available tools and databases, the participant should call the MVC Suspension Line to ask what is required to restore the license.**",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Use the Suspension Line call sheet",
        text:
          "The instructions for calling the MVC Suspension Line are **behind the front page of the Legal Assessment**. Behind that is a Notes Page where the participant captures the information they hear during the call.",
      },
      {
        kind: "checklist",
        heading: "What to capture during the call",
        items: [
          "**Ticket numbers** blocking restoration",
          "**Municipalities** those tickets are in",
          "**Any other reason for the suspension** - including:",
          "Child support warrants",
          "Unpaid probation fines",
          "Alcohol interlock device fees",
          "Intoxicated Driver Resource Center (IDRC) requirements",
          "Multiple ID numbers (MVC sometimes has duplicate records for one person)",
          "Default judgments in auto accident cases",
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "If the participant can't complete the call",
        text:
          "If the participant is unable to complete the call, or does not record the information in a legible and usable way, the LSC may submit a **License Restoration Requirements Report (LRRR) request to SDLS**. SDLS will then forward the LRRR to NJRC's contact at MVC. See [[license-restoration-requirements-report]] for the full process.",
      },
    ],
  },

  // ─── SOCIAL SECURITY ──────────────────────────────────────────────
  {
    id: "request-social-security-card",
    category: "social-security",
    title: "How do I help a Participant request a Social Security Card?",
    summary:
      "Confirm whether the participant has a card; help request a replacement (or correct one) via Form SS-5. No fee. Applications go in-person or by mail (when NJRC has an established understanding with the local SSA office). Online not recommended.",
    source: "LSM 2026 · Module 7",
    estimatedTime: "30 min in-meeting + 2-4 weeks turnaround",
    difficulty: "easy",
    keywords: ["social security", "ss card", "ss-5", "replacement", "ssa"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**The LSC will confirm whether each participant has a Social Security Card.** If not, the LSC can assist with **requesting a replacement**. The LSC can also help **correct information on the card** (for example, a misspelling).",
          "**There is no fee** to replace a Social Security Card.",
          "**Applications go in-person OR by mail.** Mail-in is available only **when the NJRC site has an established understanding with the local Social Security Administration (SSA) office**.",
          "**One supporting document is required.** Must be **original AND unexpired**: State-Issued ID · U.S. Passport · Certificate of Naturalization · Health Insurance Card · Military ID Card · OR a medical record signed in pen ink by a doctor.",
          "**Doctor's note fallback** if none of the above: must include the participant's name, DOB, date + time of visit, and the doctor's pen-ink signature dated within the past 30 days.",
          "**Online not recommended.** Even applying online still requires an in-person SSA appointment to present the ID documents - paper is more efficient until SSA changes that.",
          "**Turnaround: ~2-4 weeks** by mail after submission (varies by location).",
        ],
      },
      {
        kind: "paragraph",
        text:
          "**The LSC will confirm whether each participant has a Social Security Card.** If a participant does not have a card, the LSC can assist with **requesting a replacement**. The LSC can also help a participant **correct information on the card** (for example, a misspelling). Applications may be submitted in person, or **by mail when the NJRC Site has an established understanding with the local Social Security Administration (SSA) office**. **There is no fee to replace a Social Security Card.**",
      },
      {
        kind: "paragraph",
        text:
          "To apply for a Social Security Card (replacement or correction), **the participant must submit the application with one of the following documents. All documents must be original and unexpired:**",
      },
      {
        kind: "checklist",
        heading: "Required supporting document - must be original + unexpired",
        items: [
          "State-Issued ID",
          "U.S. Passport",
          "Certificate of Naturalization",
          "Health Insurance Card",
          "Military ID Card",
          "Medical record signed (pen ink) by a doctor",
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "Doctor's note alternative",
        text:
          "If none of the above are available: a doctor's note stating the Participant is under the doctor's care, including: Participant's name, DOB, date + time of the visit, and the doctor's pen-ink signature dated within the past 30 days.",
      },
      {
        kind: "links",
        items: [
          { label: "How do I complete the SS-5 paper application?", href: "/how-to/complete-ss5-application", description: "Box-by-box walkthrough" },
        ],
      },
      {
        kind: "links",
        heading: "Resources & contacts",
        items: [
          { label: "Find a Social Security office (online lookup)", href: "https://secure.ssa.gov/apps6z/FOLO/fo001.jsp", description: "By ZIP code; gives the local SSA office address + phone." },
          { label: "Form SS-5 (PDF) - official", href: "https://www.ssa.gov/forms/ss-5.pdf", description: "The Application for a Social Security Card. Page 5 of 5 is the only page that gets submitted." },
          { label: "SSA general information", href: "tel:+18007721213", description: "1-800-772-1213 - open Monday-Friday, 8am-7pm." },
          { label: "SSA TTY (deaf/hard of hearing)", href: "tel:+18003250778", description: "1-800-325-0778." },
        ],
      },
    ],
  },

  {
    id: "complete-ss5-application",
    category: "social-security",
    title: "How do I complete the SS-5 paper application?",
    summary:
      "Form SS-5 (12-2024) UF. Only page 5 of 5 is the application that gets submitted. Type the form, hand-sign Box 17 in pen ink, then either give it to the participant for in-person submission OR (when site has an SSA understanding) mail it.",
    source: "LSM 2026 · Module 7",
    estimatedTime: "20 min",
    difficulty: "easy",
    keywords: ["ss-5", "form", "application", "boxes", "social security"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**Use Form SS-5 (12-2024) UF.** **Only page 5 of 5 is the application** and **the only page that must be submitted**.",
          "**The form should be typed (PDF)** and must include a **written (pen-ink) signature in Box 17**.",
          "**Top of form fields:** participant's name, DOB, place of birth, SSN (if known), citizenship status, ethnicity, race, and gender.",
          "**Parents' names: required.** **Parents' SSNs are NOT required** - select \"Unknown.\"",
          "**Box 11:** if the participant is unsure whether they have applied for a Social Security Card before, **select \"Don't Know.\"**",
          "**Boxes 15 and 16 (phone + mailing address):** if the participant **does not have a usable mailing address**, the LSC may use the **phone number and mailing address for the NJRC office**.",
          "**Box 14:** date the application is prepared.",
          "**Box 17:** participant signs **in pen ink**.",
          "**Box 18:** check **\"Self.\"**",
          "**After completion:** scan + upload to Salesforce.",
          "**For mail submission (only when NJRC has an established SSA understanding):** complete Boxes 15 and 16 with the **LSC's phone number** and the **reentry site address**.",
          "**Online applications NOT recommended** - they still require an in-person SSA appointment to present ID documents.",
          "**Turnaround:** participant should receive their SS Card by mail within **2-4 weeks** of submission (timelines vary by location).",
        ],
      },
      {
        kind: "paragraph",
        text:
          "**To request a replacement Social Security Card, the LSC will complete Form SS-5 (12-2024) UF with the participant.** **Page 5 of 5 is the application and is the only page that must be submitted.** The form should be **typed (PDF)** and must include a **written (pen-ink) signature in Box 17**.",
      },
      {
        kind: "kv",
        heading: "Field-by-field breakdown",
        items: [
          { label: "Top of form", value: "Participant's name, **date of birth (DOB)**, place of birth, **Social Security number (SSN), if known**, citizenship status, ethnicity, race, and gender." },
          { label: "Parents' info", value: "**Both parents' names.** Parents' Social Security numbers **are not required** - select \"Unknown.\"" },
          { label: "Box 11", value: "If the participant is unsure whether they have applied for a Social Security Card before, **select \"Don't Know.\"**" },
          { label: "Boxes 15 and 16", value: "Phone + mailing address. **If the participant does not have a usable mailing address, the LSC may use the phone number and mailing address for the NJRC office.**" },
          { label: "Box 14", value: "**Date the application is prepared.**" },
          { label: "Box 17", value: "Participant's **pen-ink signature**." },
          { label: "Box 18", value: "Check **\"Self\"**." },
        ],
      },
      {
        kind: "paragraph",
        text:
          "**After the application is completed, the LSC will scan and upload it to the participant's Salesforce profile.**",
      },
      {
        kind: "steps",
        heading: "Applying In-Person OR By Mail",
        items: [
          { title: "Default: hand the application back to the participant", body: "**The LSC will return the completed application to the participant with instructions to bring it to their local (or nearest) Social Security Administration office, along with the required ID document or medical note.** The LSC or other NJRC staff can assist the participant with **scheduling an appointment** if needed. After the application is submitted, the participant should receive their Social Security Card by mail within **2-4 weeks** (timelines vary by location)." },
          { title: "If site has SSA understanding: mail-in", body: "**If the LSC or NJRC Site has an established understanding with the local Social Security Administration office, the LSC may place the completed application and supporting document(s) in a sealed envelope and mail it to the appropriate office.** When mailing the application, complete **Boxes 15 and 16 with the LSC's phone number and the reentry site address**." },
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "Why not online?",
        text:
          "**Applications may also be started online.** However, **at the time of writing, this is not recommended** because **the participant must create their own profile on SSA.gov, and online applicants are still required to schedule an in-person appointment to present the required ID documents.** Until SSA changes that, paper is more efficient.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/ss5-form-blank.png",
        caption: "**The blank Form SS-5 page that gets submitted** (the only page of the SS-5 packet that needs to be filled in). Box 17 (signature) goes pen-on-paper after the rest is typed.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/ssa-gov-instructions-1.png",
        caption: "SSA.gov screenshot - page-by-page reference for the SS-5 process (LSM 2026 · LSC New Hire Packet, page 1 of 3).",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/ssa-gov-instructions-2.png",
        caption: "SSA.gov screenshot - page 2 of 3.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/ssa-gov-instructions-3.png",
        caption: "SSA.gov screenshot - page 3 of 3.",
      },
    ],
  },

  // ─── BIRTH CERTIFICATES ───────────────────────────────────────────
  {
    id: "birth-certificate-decision-tree",
    category: "birth-certificates",
    title: "Which Birth Certificate process do I use?",
    summary:
      "The path depends on the place of birth: NJ, NYC, PA, Puerto Rico, VitalChek-supported state, paper-only state, or international.",
    source: "LSM 2026 · Module 8",
    estimatedTime: "2 min decision",
    difficulty: "easy",
    keywords: ["birth certificate", "decision", "place of birth", "vital records", "vitalchek"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "Birth Certificate procedure depends almost entirely on the **place of birth**. Confirm city, county, state, and country during the legal meeting before you do anything else.",
      },
      {
        kind: "table",
        heading: "Pick the right path",
        columns: ["Place of birth", "Process"],
        rows: [
          ["New Jersey", "NJ in-state - Designated Individual picks up at NJ Vital Records (Trenton)"],
          ["New York City (5 boroughs)", "NYC paper application + Attorney Protocol (NOT VitalChek)"],
          ["State of NY (outside NYC)", "VitalChek"],
          ["Pennsylvania", "Paper application - homeless-fee-waiver path for indigent participants"],
          ["Hawaii / Vermont / Wyoming", "Paper application (no VitalChek support)"],
          ["California / Michigan", "VitalChek with relaxed requirements (no security questions in some cases)"],
          ["Other US states", "VitalChek (default)"],
          ["Puerto Rico", "PR-specific paper application + Notary Packet"],
          ["US Virgin Islands", "VitalChek"],
          ["Born abroad to US citizen parents", "Consular Report of Birth Abroad (CRBA) via VitalChek"],
          ["Foreign-born", "Country-specific - see the international guide"],
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "NJRC policy: one BC per Participant per calendar year",
        text:
          "Only one copy may be ordered at a time. Plan accordingly.",
      },
      {
        kind: "links",
        heading: "Specific guides",
        items: [
          { label: "How do I order a NJ Birth Certificate?", href: "/how-to/order-nj-birth-certificate" },
          { label: "How do I order a NYC Birth Certificate?", href: "/how-to/order-nyc-birth-certificate" },
          { label: "How do I use VitalChek?", href: "/how-to/order-vitalchek-birth-certificate" },
          { label: "How do I order a Puerto Rico Birth Certificate?", href: "/how-to/order-pr-birth-certificate" },
          { label: "How do I order a PA Birth Certificate (homeless path)?", href: "/how-to/order-pa-birth-certificate" },
          { label: "🌎 How do I get a birth certificate from another country?", href: "/how-to/international-birth-certificates" },
        ],
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/bc-decision-flowchart.png",
        caption: "Birth Certificate decision flowchart (LSM 2026 · Module 8 BC Guide). Same logic as the table above, in the original visual format.",
      },
    ],
  },

  {
    id: "order-nj-birth-certificate",
    category: "birth-certificates",
    title: "How do I order a NJ Birth Certificate?",
    summary:
      "Two forms, a notarized Delegation of Authority, and the Designated Individual picks up in Trenton.",
    source: "LSM 2026 · Module 8",
    estimatedTime: "30 min in-meeting + 4-6 weeks turnaround",
    difficulty: "medium",
    keywords: ["nj birth certificate", "delegation", "trenton", "vital records", "notary", "REG-27a", "REG-2"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "For NJ Birth Certificates, a **Designated Individual** for the NJRC Legal Team makes regular trips to the Vital Records Office in Trenton, picks up certified copies, and distributes them to NJRC offices. You prepare the application + Delegation, get the Delegation notarized, send to the Designated Individual.",
      },
      {
        kind: "steps",
        heading: "If Participant is physically present",
        items: [
          { title: "Complete Form REG-27a - Non-Genealogical Certification", body: "PDF is fillable. Pre-filled with designated LSC's info. Only complete sections in green text." },
          { title: "Complete Form REG-2 - Delegation of Authority", body: "Authorizes the Designated Individual to pick up the BC on the Participant's behalf." },
          { title: "Notarize the Delegation in front of a Notary Public", body: "Participant must sign in front of the Notary." },
          { title: "Scan + upload + email", body: "Scan signed Delegation + completed Application as a SINGLE document. Upload to Salesforce. Email to the Designated Individual." },
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "If Participant is remote",
        text:
          "Complete both forms on their behalf. Schedule them to come in to review and sign. The Delegation must still be notarized - they sign in front of a Notary in your office. If no valid picture ID on file, they must bring one.",
      },
      {
        kind: "callout",
        tone: "warn",
        title: "Name change or adoption",
        text:
          "Fill out the form with the Participant's name AS IT APPEARS ON THE ORIGINAL BIRTH CERTIFICATE. Provide a brief explanation + date of name change in the notes field. Example: 'John was legally adopted by his mother's husband, Dan Smith, one year after birth; last name legally changed to Smith.'",
      },
      {
        kind: "kv",
        heading: "Cost + waiver",
        items: [
          { label: "Standard fee", value: "$25.00" },
          { label: "Fee waiver", value: "Available if (a) homeless shelter administrator provides a letter verifying homeless status, OR (b) NJRC licensed social worker provides a letter on NJRC letterhead disclosing indigent/homeless status (include NJRC Tax ID 47-2139071 + written endorsement)" },
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "Turnaround + delivery",
        text:
          "About 4-6 weeks. When the BC arrives, scan into Salesforce + notify the Participant. Birth Certificates must be HAND-DELIVERED at the NJRC site. NJRC policy is to never mail or ship a BC unless approved by the CLO.",
      },
      {
        kind: "links",
        heading: "Forms (bundled)",
        items: [
          { label: "NJ Non-Genealogical Certification (REG-27a)", href: "/how-to-assets/bc-forms/01 NJ Non-Genealogocial Certification.pdf", description: "The application - fillable PDF" },
          { label: "NJ Delegation of Authority (REG-2)", href: "/how-to-assets/bc-forms/02 NJ Delegation of Authority.pdf", description: "Authorizes the Designated Individual to pick up - must be notarized" },
        ],
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/nj-bc-procedure.png",
        caption: "NJ Birth Certificate Procedure (LSM 2026 · LSC New Hire Packet) - overview of the in-state process.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/nj-bc-application-1.png",
        caption: "Form REG-27a - Non-Genealogical Certification (page 1). The application itself.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/nj-bc-delegation-1.png",
        caption: "Form REG-2 - Delegation of Authority (page 1). Authorizes NJRC's Designated Individual to pick up the BC in Trenton; participant signs at notary.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/nj-bc-amendment-form.png",
        caption: "**NJ BC Amendment Application (REG-15)** - when there's an error on an existing BC (misspelling, wrong DOB, wrong parent name), this is the form. Different from REG-27a which is for ordering certified copies; REG-15 is for correction.",
      },
      {
        kind: "links",
        heading: "Resources & contacts",
        items: [
          { label: "NJ DOH - Vital Records (state office)", href: "https://www.nj.gov/health/vital/", description: "Public landing page for NJ vital-records services." },
          { label: "NJ Vital Records (email)", href: "mailto:records@doh.nj.gov", description: "Status questions and follow-up." },
          { label: "NJ Vital Records (phone)", href: "tel:+16092924087", description: "(609) 292-4087 - main line for vital-records inquiries." },
          { label: "Correcting NJ vital records", href: "https://www.nj.gov/health/vital/correcting-vital/", description: "If the certificate has an error, this is the amendment path." },
          { label: "NJ Apostille service (NJ Treasury)", href: "https://www.nj.gov/treasury/revenue/apostilles.shtml", description: "Required for international use of NJ-issued vital records." },
        ],
      },
    ],
  },

  {
    id: "order-vitalchek-birth-certificate",
    category: "birth-certificates",
    title: "How do I use VitalChek for an out-of-state Birth Certificate?",
    summary:
      "The default tool for most US states. Phone interview with SDLS + 4 security questions; backup ID-verification path if questions fail.",
    source: "LSM 2026 · Module 8",
    estimatedTime: "20 min in-meeting + 10 days turnaround if smooth",
    difficulty: "medium",
    keywords: ["vitalchek", "out of state", "birth certificate", "security questions"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "**VitalChek** is the default for most US states. Exceptions: Hawaii, Wyoming, Vermont, Pennsylvania (no VitalChek support), and **New York City** (paper Attorney Protocol path even though State of NY supports VitalChek).",
      },
      {
        kind: "steps",
        heading: "Steps",
        items: [
          { title: "Confirm Participant knows the required info", body: "Father's full name (first, middle, last); Mother's full name (first, middle, MAIDEN); city/county/state of birth + hospital; SSN." },
          { title: "Complete the VitalChek Birth Certificate Request Form", body: "" },
          { title: "Upload to Salesforce + email request to SDLS", body: "" },
          { title: "Coordinate the phone interview with SDLS + Participant", body: "If Participant has no phone, schedule a time for them to use a phone at the NJRC office." },
          { title: "Phone interview - 5-10 minutes", body: "Participant + SDLS on the call. VitalChek asks 4 multiple-choice security questions." },
          { title: "If all questions correct → application forwarded immediately", body: "BC ships to NJRC HQ in ~10 calendar days from order." },
          { title: "If questions fail → identity verification path", body: "Participant provides identity-verification documents to SDLS. Document requirements vary by state. SDLS uploads. If Participant can't provide them, switch to that state's paper application." },
        ],
      },
      {
        kind: "links",
        heading: "Form (bundled)",
        items: [
          { label: "LSC VitalChek Request Form", href: "/how-to-assets/bc-forms/03 LSC_VitalChek_Request(083023).pdf", description: "The standardized request form to fill out + email to SDLS" },
        ],
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/vitalchek-request-1.png",
        caption: "LSC VitalChek Request Form (page 1) - fill out, scan, email to SDLS.",
      },
      {
        kind: "kv",
        heading: "Notable state exceptions to know",
        items: [
          { label: "California", value: "Some counties (e.g. LA) skip security questions; just notarized form + state ID via VitalChek" },
          { label: "Michigan", value: "No security questions, no qualifying documents required" },
          { label: "New York State (excl. NYC)", value: "VitalChek works" },
          { label: "New York City", value: "VitalChek NOT accepted - use Attorney Protocol paper path" },
          { label: "Pennsylvania / Hawaii / Vermont / Wyoming", value: "No VitalChek - paper applications only" },
        ],
      },
    ],
  },

  {
    id: "order-nyc-birth-certificate",
    category: "birth-certificates",
    title: "How do I order a NYC Birth Certificate?",
    summary:
      "Application + Attorney Protocol + Cover Letter; signed by GC; mail to NYC Office of Vital Records. Several months turnaround.",
    source: "LSM 2026 · Module 8",
    estimatedTime: "45 min in-meeting + several months turnaround",
    difficulty: "hard",
    keywords: ["nyc", "new york city", "birth certificate", "attorney protocol", "five boroughs"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "For Participants born in any of the **five boroughs of New York City** (Manhattan, Bronx, Brooklyn, Queens, Staten Island), VitalChek is NOT accepted. You complete a NYC Birth Certificate Application + Attorney Protocol + Cover Letter; the CLO signs; everything gets mailed to NYC Office of Vital Records.",
      },
      {
        kind: "steps",
        heading: "Steps",
        items: [
          { title: "Collect a front + back copy of the Participant's ID", body: "From the list of acceptable documents in the Attorney Protocol form." },
          { title: "Complete Application + Attorney Protocol form", body: "Designated sections only. Participant signs." },
          { title: "Notarize the application", body: "" },
          { title: "Prepare the Cover Letter with Participant info", body: "CLO signs the Cover Letter." },
          { title: "Submit the original Cover Letter + Application + Attorney Protocol + ID copies to SDLS", body: "Or to the Site Director to forward at the next Director's Meeting." },
        ],
      },
      {
        kind: "kv",
        heading: "Mailing details",
        items: [
          { label: "Cost", value: "$15.00" },
          { label: "Payable to", value: "NYC Department of Mental Health and Hygiene" },
          { label: "Mail to", value: "Office of Vital Records, 125 Worth St., CN-4 Room 119, New York NY 10013" },
          { label: "Max copies per order", value: "3" },
          { label: "Turnaround", value: "Several MONTHS - set Participant expectations clearly" },
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "Delivery",
        text:
          "BC is delivered to NJRC HQ. SDLS notifies you. Upload to Salesforce + notify Participant. Hand-deliver at the NJRC site (per NJRC policy).",
      },
      {
        kind: "links",
        heading: "Forms (bundled)",
        items: [
          { label: "NYC Vital Records Application", href: "/how-to-assets/bc-forms/04 NYC Vital Records Application.pdf" },
          { label: "NYC Vital Records Attorney Protocol", href: "/how-to-assets/bc-forms/05 NYC Vital Records Attorney Protocol.pdf", description: "Submitted alongside the application" },
        ],
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/nyc-bc-application-1.png",
        caption: "NYC Vital Records Application (page 1) - the participant signs; you complete designated sections.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/nyc-bc-attorney-protocol-1.png",
        caption: "NYC Vital Records Attorney Protocol (page 1) - submitted alongside the application; CLO signs the cover letter.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/nyc-id-requirements.png",
        caption: "NYC's accepted ID forms - ONE primary (with photo + signature) AND TWO alternates (current address, no more than 60 days old). The participant's ID copies must match the shipping address on the order.",
      },
      {
        kind: "links",
        heading: "Resources & contacts",
        items: [
          { label: "NYC Office of Vital Records (eVital portal)", href: "https://a816-evital.nyc.gov/eVitalVRRTS/", description: "Online status check / appointment booking. NJRC submits via the paper Attorney Protocol path, but you can use eVital to track." },
          { label: "NYC Vital Records (email)", href: "mailto:nycdohvr@health.nyc.gov", description: "For application status questions." },
          { label: "Mailing address", href: "https://www.google.com/maps/search/125+Worth+Street+New+York+NY", description: "Office of Vital Records, 125 Worth St., CN-4 Room 119, New York NY 10013" },
          { label: "In-person appointment booking", href: "https://wb-nycdohmh.qmatic.cloud/qmaticwebbooking", description: "If a participant ever needs to appear in person at 125 Worth St (rare)." },
        ],
      },
    ],
  },

  {
    id: "order-pr-birth-certificate",
    category: "birth-certificates",
    title: "How do I order a Puerto Rico Birth Certificate?",
    summary:
      "PR-specific Application + Cover Letter (signed by CLO) + Notary Packet + every ID the Participant has.",
    source: "LSM 2026 · Module 8",
    estimatedTime: "1 hour in-meeting + variable turnaround",
    difficulty: "hard",
    keywords: ["puerto rico", "PR", "birth certificate", "notary packet"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "Participants born in Puerto Rico require their own dedicated workflow - separate from VitalChek and from US states. The packet is signed by the CLO and includes copies of every piece of ID the Participant has on hand.",
      },
      {
        kind: "steps",
        heading: "Build the packet",
        items: [
          { title: "Prepare the Puerto Rico Birth Certificate Application", body: "To be signed by the CLO." },
          { title: "Prepare a Cover Letter", body: "" },
          { title: "Prepare the Notary Packet", body: "" },
          { title: "Collect front + back copies of EVERY piece of ID Participant has", body: "Set must include at minimum a 'reasonably official' photo ID - social services ID with photo, municipal ID, reintegration ID, library card, resource center ID, etc." },
          { title: "Email packet to SDLS for review", body: "SDLS handles submission to PR Vital Records." },
        ],
      },
      {
        kind: "links",
        heading: "Forms (bundled)",
        items: [
          { label: "Puerto Rico Cover Letter template", href: "/how-to-assets/bc-forms/13 Puerto Rico Cover Letter.pdf" },
          { label: "Puerto Rico PP Letter", href: "/how-to-assets/bc-forms/14 Puerto Rico PP Letter.pdf" },
          { label: "Puerto Rico Affidavit", href: "/how-to-assets/bc-forms/15 Puerto Rico Affidavit.pdf" },
          { label: "Puerto Rico Vital Records Application", href: "/how-to-assets/bc-forms/16 Puerto Rico Vital Records Application.pdf" },
        ],
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/pr-bc-application-1.png",
        caption: "Puerto Rico Vital Records Application (page 1) - signed by the CLO at the Attorney Notary meeting; do NOT have the participant pre-sign.",
      },
      {
        kind: "links",
        heading: "Resources & contacts",
        items: [
          { label: "PR Department of Health - Vital Records (email)", href: "mailto:seguimientosolicitudexpcorreo@salud.pr.gov", description: "For application status / follow-up." },
          { label: "PR Vital Records (phone)", href: "tel:+17877652929", description: "(787) 765-2929 - main line in San Juan." },
        ],
      },
    ],
  },

  {
    id: "order-pa-birth-certificate",
    category: "birth-certificates",
    title: "How do I order a Pennsylvania Birth Certificate (homeless / fee-waived path)?",
    summary:
      "PA has a dedicated 'Application for a Birth Certificate with Fees Waived for Individuals Experiencing Homelessness' for indigent participants.",
    source: "LSM 2026 · Module 8",
    estimatedTime: "30 min in-meeting + several weeks turnaround",
    difficulty: "medium",
    keywords: ["pennsylvania", "PA", "birth certificate", "homeless", "fee waiver", "indigent"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "Pennsylvania doesn't accept VitalChek. For an indigent Participant, use PA's special **'Application for a Birth Certificate with Fees Waived for an Individual Who is Experiencing Homelessness.'**",
      },
      {
        kind: "steps",
        heading: "Steps",
        items: [
          { title: "Download the homeless-fee-waiver application from PA's site", body: "" },
          { title: "Complete with Participant", body: "PDF is fillable. Only sections in green text." },
          { title: "Photocopy + attach Participant's valid government-issued photo ID", body: "" },
          { title: "DLS attaches: photocopy of valid government-issued photo ID + a letter on NJRC letterhead", body: "Letter shows proof of address, affiliation to organization, and association with the Participant." },
        ],
      },
      {
        kind: "kv",
        heading: "Mailing details",
        items: [
          { label: "Mail to", value: "Department of Health, Division of Vital Records, P.O. Box 1528, New Castle PA 16103" },
        ],
      },
      {
        kind: "links",
        heading: "Forms (bundled)",
        items: [
          { label: "PA Application - Fees Waived (Homelessness)", href: "/how-to-assets/bc-forms/06 PA Application for Birth Certificate with Fees Waived for Individual Who is Experiencing Homelessness.pdf" },
        ],
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/pa-bc-application-1.png",
        caption: "PA Application for Birth Certificate with Fees Waived (page 1) - homeless / indigent path.",
      },
    ],
  },

  {
    id: "international-birth-certificates",
    category: "birth-certificates",
    title: "🌎 How do I get a birth certificate from another country?",
    summary:
      "Open the international portal - pick the country, get an instant guide for that country's vital-records system.",
    source: "Curated NJRC + on-demand LLM",
    estimatedTime: "Variable per country",
    difficulty: "hard",
    keywords: ["international", "country", "birth certificate", "vital records", "consulate", "embassy", "abroad"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "International birth certificates are handled case-by-case - every country has its own vital-records system, requirements, fees, and turnaround. Click the button below to open the country picker. Each country page assembles current guidance on demand and caches it for the next time someone needs that country.",
      },
      {
        kind: "callout",
        tone: "info",
        title: "About the country guides",
        text:
          "First time someone opens a country page, the app does a fresh research pass and stores the result. Refresh button forces a re-fetch when info gets stale (visa portals, new fees, embassy address changes, etc.). The guides are a STARTING POINT - always verify with the country's official vital records authority before submitting.",
      },
      {
        kind: "links",
        heading: "Open the country picker",
        items: [
          { label: "Browse all 195 countries →", href: "/how-to/international-birth-certificates/countries", description: "Search-as-you-type; click a country for that country's BC instructions." },
        ],
      },
    ],
  },

  // ─── STATE ID ─────────────────────────────────────────────────────
  {
    id: "first-state-id",
    category: "state-id",
    title: "How do I help a Participant get their first State ID?",
    summary:
      "Confirm the participant's ID status, address current with MVC, 6 Points of Identity, and Proof of Address - then schedule the MVC trip. ID receipt at MVC; physical ID arrives by mail within 20 calendar days.",
    source: "LSM 2026 · Module 9",
    estimatedTime: "1 hour in-meeting + MVC trip",
    difficulty: "medium",
    keywords: ["state id", "non-driver", "first time", "6 points", "mvc"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**Many participants enrolling in the program - especially those recently released** - may not have an ID or may have lost it. **In many cases, IDs are destroyed as contraband when individuals are transferred from county jail to state prison.**",
          "**NJRC policy: participants are eligible for ONE NJRC-purchased ID per calendar year.** Includes duplicates AND renewals. If the participant loses the ID before year-end, they pay for the replacement.",
          "**REAL ID:** NJRC **will pay** for a duplicate or renewal of a REAL ID **if the participant already has one** when they enroll. NJRC **will NOT pay for an initial REAL ID**.",
          "**Determine ID status by asking:** ever had an NJ ID? Have a copy/photo? Lost or expired? When? Address current?",
          "**Before ordering: confirm the address on file with MVC.** If wrong, complete a [[change-of-address-mvc]] first - it's a free service and takes effect immediately.",
          "**To apply for a new ID, confirm:** **6 Points of Identity** (Primary 4-point doc + Secondary docs adding to 6 total) · **verifiable Social Security number** · **acceptable Proof of Address**.",
          "**The 4-page Acceptable 6 Points Documentation reference** is the **first four pages of the Legal Assessment** - that's the authoritative current list.",
          "**MVC does not print IDs on site.** Participant receives a **receipt** that includes their information; the ID arrives by mail **within 20 calendar days**. The receipt has a phone number to call if it doesn't arrive.",
          "**After MVC trip:** scan the receipt and upload to the participant's Salesforce profile, completing the **\"MVC ID Obtained\" section of the Legal/Info tab**.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "**During or before the legal meeting**, the LSC will determine the status of a participant's NJ ID. **Many participants enrolling in the program - especially those recently released from jail or prison - may not have an ID or may have lost it.** In many cases, **IDs are destroyed as contraband when individuals are transferred from county jail to state prison**. The LSC has multiple options for helping a participant obtain a Non-Driver ID. If needed, the LSC can also complete a [[change-of-address-mvc]] before requesting an ID.",
      },
      {
        kind: "callout",
        tone: "info",
        title: "NJRC funding policy",
        text:
          "**At the time of writing, NJRC policy is that participants are eligible to receive an NJRC-purchased ID once per calendar year.** If a participant loses their NJRC-purchased ID before the calendar year ends, they are responsible for paying for a replacement. **This policy includes duplicate IDs and renewals.** NJRC will also pay for a duplicate or renewal of a **REAL ID if the participant already has a REAL ID when they enroll** with NJRC. **NJRC will not, however, pay for an initial REAL ID.**",
      },
      {
        kind: "checklist",
        heading: "Determine ID status - ask the participant",
        items: [
          "**Have you ever had an NJ ID before?**",
          "**Do you have a copy or photo of the ID?**",
          "**Is the ID lost or expired?**",
          "**When was it lost, or when did it expire?**",
          "**Is the address on the ID current, or does it need to be updated?**",
        ],
      },
      {
        kind: "paragraph",
        text:
          "The participant's answers to these questions will guide the LSC's next steps. Based on the information provided, the LSC can determine whether to apply for a new ID, **renew an existing ID** ([[renew-state-id]]), or **order a duplicate ID** ([[duplicate-state-id]]).",
      },
      {
        kind: "paragraph",
        text:
          "**Before ordering a new or replacement ID, the LSC should confirm whether the participant's address on file with MVC is correct.** If the address needs to be updated, complete a [[change-of-address-mvc]] FIRST. **A Change of Address is a free service**, but **it does not include a duplicate ID** - duplicate IDs must be ordered and paid for separately.",
      },
      {
        kind: "steps",
        heading: "Applying for a New ID",
        items: [
          { title: "Confirm 6 Points of Identity", body: "Participant must provide a **Primary Document (4 points)** and one or more **Secondary Document(s) (1 or 2 points)**. **Most commonly**: birth certificate as the 4-point document and Social Security card as a 1-point document. **For the additional 1 point**, common options include health insurance card, debit card, or DOC ID - but the participant may provide any acceptable documentation they have available. See [[six-points-of-id]]. **The authoritative current list is in the first four pages of the Legal Assessment.**" },
          { title: "Confirm a verifiable Social Security number", body: "A State ID requires this." },
          { title: "Confirm acceptable Proof of Address", body: "Bank statement · utility bill · mail from a government agency (e.g., Social Services). **If the participant is using the NJRC site office as their mailing address, NJRC can provide a letter confirming the address for Proof of Address.** See [[proof-of-address-mvc]]." },
          { title: "Schedule the MVC trip", body: "**Although this may differ by NJRC site, a participant can be scheduled for an MVC trip with NJRC staff**, where they will meet at the appropriate MVC location. The staff member will help the participant through the application process." },
          { title: "Receive the ID receipt at MVC", body: "**At the time of writing, MVC does not print IDs on site.** Instead, the participant will receive an **ID receipt** that includes their information and states that **the ID will arrive by mail within 20 calendar days**. The receipt also includes a phone number to call if the ID does not arrive within that timeframe." },
          { title: "Upload to Salesforce", body: "**Upon returning from MVC, the LSC or staff member will scan the ID receipt and upload it to the participant's Salesforce profile, completing the \"MVC ID Obtained\" section of the Legal/Info tab.**" },
        ],
      },
    ],
  },

  {
    id: "six-points-of-id",
    category: "state-id",
    title: "What are the 6 Points of ID?",
    summary:
      "MVC requires the participant to provide a Primary Document (4 points) + Secondary Document(s) (1-2 points each) totaling 6 - plus a verifiable Social Security number and acceptable Proof of Address. Most common at NJRC: Birth Certificate (4) + Social Security card (1) + one more secondary (1).",
    source: "LSM 2026 · Module 9",
    estimatedTime: "3 min read",
    difficulty: "easy",
    keywords: ["6 points", "six points", "id", "primary", "secondary", "proof of address"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**MVC requires a Primary Document (4 points) AND one or more Secondary Document(s) (1 or 2 points)** for a State ID.",
          "**Most common at NJRC:** participant's **birth certificate** as the **4-point** document and **Social Security card** as a **1-point** document. **For the additional 1 point**, common options include health insurance card, debit card, or DOC ID - but the participant may provide any acceptable documentation they have available.",
          "**A State ID also requires:** **(1) a verifiable Social Security number** AND **(2) acceptable Proof of Address**.",
          "**Acceptable Proof of Address documents:** bank statement · utility bill · mail from a government agency (e.g., Social Services). **If the participant is using the NJRC site office as their mailing address, NJRC can provide a letter confirming the address for Proof of Address.**",
          "**The authoritative current list of accepted 6-Point documentation is in the first four pages of the Legal Assessment.** MVC's accepted-docs list updates periodically - always check there for the latest.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "**For the 6 Points of ID, the participant must provide a Primary Document (4 points) and one or more Secondary Document(s) (1 or 2 points).** **Most commonly, the LSC will use the participant's birth certificate as a 4-point document and a Social Security card as a 1-point document.** **For the additional 1 point, common options include a health insurance card, debit card, or DOC ID, but the participant may provide any acceptable documentation they have available.**",
      },
      {
        kind: "paragraph",
        text:
          "**A State ID also requires a verifiable Social Security number and Proof of Address.** Commonly accepted Proof of Address documents include a **bank statement, utility bill, or mail from a government agency** (e.g., Social Services). **If the participant is using the NJRC site office as their mailing address, NJRC can provide a letter confirming the address for Proof of Address.**",
      },
      {
        kind: "callout",
        tone: "info",
        title: "MVC's accepted-docs list updates periodically",
        text:
          "**An updated list of acceptable 6-Point documentation is included in the first four pages of the Legal Assessment.** That's the authoritative current reference - always check there.",
      },
      {
        kind: "table",
        heading: "How the points stack",
        columns: ["Tier", "Common examples", "Points each"],
        rows: [
          ["Primary (need exactly one)", "Birth Certificate (most common at NJRC), US Passport, Permanent Resident card", "4 points"],
          ["Secondary (need additional 2 points)", "Social Security card (most common), Health insurance card, Debit card, DOC ID", "1-2 points"],
        ],
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/mvc-6-points-poster.png",
        caption: "**Official MVC 6-Point poster** - the one MVC posts in their offices and the source of truth on accepted documents. Worth printing out and pinning at your desk.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/mvc-6-points-brochure-page1.png",
        caption: "MVC 6-Point ID Brochure (page 1) - primary documents (4-point) breakdown and the full accepted-doc list.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/mvc-6-points-brochure-page2.png",
        caption: "MVC 6-Point ID Brochure (page 2) - secondary documents (1-2 points) and the proof-of-address tier.",
      },
      {
        kind: "paragraph",
        text:
          "The most common 6-point combo at NJRC is **Birth Certificate (4) + Social Security card (1) + one more secondary doc (1)** = 6 points. Plus a verifiable SSN and acceptable Proof of Address.",
      },
      {
        kind: "kv",
        heading: "Common Proof of Address documents",
        items: [
          { label: "Bank statement", value: "Within last 90 days" },
          { label: "Utility bill", value: "Within last 90 days" },
          { label: "Government mail", value: "From Social Services, IRS, etc." },
          { label: "NJRC site office letter", value: "If Participant uses NJRC as their mailing address" },
        ],
      },
    ],
  },

  {
    id: "change-of-address-mvc",
    category: "state-id",
    title: "How do I do a Change of Address with MVC?",
    summary:
      "Free MVC online service. Required inputs same as the Restoration Fee Search. Takes effect immediately. Always do this BEFORE ordering a duplicate or renewal - and the Change of Address itself does NOT include a duplicate ID.",
    source: "LSM 2026 · Module 9",
    estimatedTime: "10 min",
    difficulty: "easy",
    keywords: ["change of address", "address", "mvc", "update", "online"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**Always do Change of Address BEFORE ordering a duplicate or renewal.** If the address on file is wrong, your duplicate/renewal mails to the wrong place.",
          "**Change of Address is a FREE service** through MVC's online portal - but it **does NOT include a duplicate ID**. Duplicate IDs must be ordered and paid for separately.",
          "**Required inputs:** same as the Restoration Fee Search - NJ ID number, ZIP code, SSN/DOB.",
          "**Takes effect immediately.** After Change of Address is submitted, the LSC should be able to access the Renewal/Duplicate portals **almost immediately**.",
          "**Voter address question:** if updating to the participant's actual address, select **YES**. If the participant is using **NJRC as their mailing address**, select **NO**.",
          "**After completion:** download the Address Confirmation, upload to Salesforce Attachments, **update the participant's ZIP code (if necessary) in the DL Restoration section**, and record a task note.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "**Before ordering a new or replacement ID, the LSC should confirm whether the participant's address on file with MVC is correct.** If the address needs to be updated, the LSC should complete a Change of Address **first**. NJ MVC has an online portal where you can check and update a participant's address on file. **At the time of writing, executing a Change of Address requires the same information as a Restoration Fee Search and takes effect immediately.**",
      },
      {
        kind: "callout",
        tone: "warn",
        title: "A Change of Address is FREE - but does NOT include a duplicate ID",
        text:
          "**Change of Address is a free service**, but **it does not include a duplicate ID**. **Duplicate IDs must be ordered and paid for separately** ([[duplicate-state-id]]).",
      },
      {
        kind: "steps",
        heading: "Execute a Change of Address",
        items: [
          { title: "Visit the MVC online portal", body: "Same family of portals as the Restoration Fee Search." },
          { title: "Enter the required information and click 'Begin Address Change'", body: "Same inputs as the Restoration Fee Search - NJ ID number, ZIP, SSN/DOB. **Once initiated, the portal will display the participant's current address on file.**" },
          { title: "Enter your email + the participant's updated address", body: "**The LSC will enter their email address and the participant's updated address information**, then proceed." },
          { title: "Answer the voter-address question", body: "**If the LSC is updating the participant's new address**, select **YES** when prompted to update the participant's voter address. **If the participant is using NJRC as their address**, select **NO**." },
          { title: "Submit", body: "" },
          { title: "Download the Address Confirmation + upload to Salesforce", body: "**Upload to the participant's Salesforce Attachments, update the participant's ZIP code (if necessary) in the DL Restoration section, and record a task note.**" },
        ],
      },
    ],
  },

  // ─── EXPUNGEMENT ──────────────────────────────────────────────────
  {
    id: "expungement-overview",
    category: "expungement",
    title: "What is Expungement and what kinds exist?",
    summary:
      "Expungement removes records from public access - the participant can legally answer 'no' on most job applications. NJRC supports three categories: Regular Expungement, Clean Slate Expungement, and Marijuana Decriminalization Expungement. The LSC's primary role is identifying eligibility and connecting the participant to pro bono representation.",
    source: "LSM 2026 · Module 10",
    estimatedTime: "5 min read",
    difficulty: "easy",
    keywords: ["expungement", "clean slate", "marijuana", "decriminalization", "felony"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**Expungement removes a record from public access** - the participant can legally answer \"no\" on most job applications.",
          "**NJRC supports three categories:** **(1) Regular Expungement** (typically single-felony or limited disorderly-persons history beyond the waiting period). **(2) Clean Slate Expungement** (entire eligible criminal record after a 10-year waiting period since most recent conviction / sentence completion). **(3) Marijuana Decriminalization Expungement** (NJ marijuana convictions automatically eligible - many auto-vacated by the state).",
          "**The LSC's primary role: identify likely eligibility from MCCS + PROMIS/Gavel + JOC, then refer to pro bono representation** - NJRC's pro bono attorney pool, Legal Services of New Jersey, or Volunteer Lawyers for Justice. **The petition itself is filed by an attorney**, not the LSC.",
          "**Two threshold questions** for any Clean Slate referral: **(1) Has the applicable waiting period expired?** (10 years since the latest of: most recent conviction · payment of fines · completion of probation/parole · release from incarceration). **(2) Does the participant's case history feature any crimes that cannot be expunged?**",
          "**Eligibility chart, full waiting periods, and statutory cites:** see [[expungement-eligibility-chart]].",
          "**Before filing:** the participant needs an official **NJ State Police criminal-history record** - see [[obtaining-criminal-record]]. NJSP only releases this to the individual themselves.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "**Expungement removes a record from public access** - the participant can legally answer \"no\" on most job applications. NJRC supports three categories of expungement work:",
      },
      {
        kind: "kv",
        heading: "The three categories",
        items: [
          { label: "Regular Expungement", value: "Single-felony or limited-disorderly-persons history beyond the applicable waiting period. See the full statutory chart in [[expungement-eligibility-chart]]." },
          { label: "Clean Slate Expungement", value: "**Entire eligible criminal record** after a **10-year waiting period** since the most recent conviction, payment of fines, completion of probation/parole, or release from incarceration - whichever is latest. See [[clean-slate-expungement]]." },
          { label: "Marijuana Decriminalization", value: "**4th-degree marijuana felonies and DP marijuana convictions are eligible immediately, no waiting period.** Many of these have been **auto-vacated by the state** since 2021. See [[marijuana-decrim-expungement]]." },
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "NJRC's role: identify eligibility + refer; attorneys file",
        text:
          "**The LSC's primary expungement role is identifying eligibility and connecting the participant to pro bono representation.** NJRC's pro bono attorney pool, **Legal Services of New Jersey (LSNJ)**, and **Volunteer Lawyers for Justice (VLJ)** handle the actual petition. Don't draft expungement petitions yourself - refer.",
      },
      {
        kind: "links",
        heading: "Read next",
        items: [
          { label: "How do I refer a Participant for expungement?", href: "/how-to/refer-for-expungement" },
          { label: "NJ Expungement Eligibility Chart (full statutory table)", href: "/how-to/expungement-eligibility-chart" },
          { label: "Clean Slate Expungement (10-year waiting period)", href: "/how-to/clean-slate-expungement" },
          { label: "Marijuana Decriminalization expungements", href: "/how-to/marijuana-decrim-expungement" },
          { label: "Obtaining the participant's criminal record", href: "/how-to/obtaining-criminal-record" },
        ],
      },
    ],
  },

  {
    id: "refer-for-expungement",
    category: "expungement",
    title: "How do I refer a Participant for expungement?",
    summary:
      "Identify likely eligibility from MCCS + PROMIS/Gavel + JOC, confirm waiting periods + no excluded crimes, then refer to NJRC's pro bono attorney pool, LSNJ, or VLJ. Track the referral in Salesforce + follow up monthly.",
    source: "LSM 2026 · Module 10",
    estimatedTime: "20 min triage + pro bono handoff",
    difficulty: "medium",
    keywords: ["expungement", "refer", "pro bono", "lsnj", "vlj"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**The LSC identifies eligibility and refers - attorneys file.** Don't draft petitions yourself.",
          "**Two threshold questions before any Clean Slate referral:** **(1)** Has the applicable waiting period expired? **(2)** Does the participant's case history feature any crimes that cannot be expunged?",
          "**Triage source:** **MCCS + PROMIS/Gavel + JOC** for the full record. **MCCS results may not be enough on their own** - many participants have only municipal-court history visible there.",
          "**Eligibility patterns:** Single felony beyond waiting period → Regular Expungement candidate. Entire record + 10 years since latest milestone → Clean Slate candidate. Cannabis-related → potentially auto-vacated already (check first).",
          "**Where to refer:** NJRC's **pro bono attorney pool** (Court Rule 1:21-11 certified) · **Legal Services of New Jersey (LSNJ)** · **Volunteer Lawyers for Justice (VLJ)**. SDLS has the current contact list.",
          "**Track in Salesforce** - referral date, pro bono assigned, follow up monthly with both the participant and the attorney.",
          "**Participant must obtain their own NJ State Police criminal-history record** before the petition can be drafted - see [[obtaining-criminal-record]]. NJSP only releases this to the individual.",
        ],
      },
      {
        kind: "steps",
        heading: "Triage + referral",
        items: [
          { title: "Confirm participant is in stable post-conviction status", body: "Expungement timing depends on years since the latest of: most recent conviction · payment of fines · completion of probation/parole · release from incarceration." },
          { title: "Pull the full record", body: "**MCCS** for municipal matters, **PROMIS/Gavel** for criminal convictions, **JOC** for post-2017 Superior Court judgments. Need a clear picture before referring." },
          { title: "Apply the waiting-period + excluded-crimes screen", body: "**(1) Has the applicable waiting period expired?** See [[expungement-eligibility-chart]] for type-by-type periods. **(2) Does the case history feature any crimes that cannot be expunged?** Consult SDLS for borderline cases." },
          { title: "Match to the right expungement category", body: "Single felony beyond waiting period → **Regular**. Entire eligible record + 10 years since latest milestone → **[[clean-slate-expungement]]**. Cannabis-related → **[[marijuana-decrim-expungement]]** (and check whether already auto-vacated)." },
          { title: "Coach the participant to get their NJSP criminal-history record", body: "**Required for the petition. NJSP only releases this to the individual themselves.** See [[obtaining-criminal-record]] for the IdentoGO walkthrough." },
          { title: "Refer to pro bono", body: "**NJRC's pro bono attorney pool** (NJ Supreme Court Rule 1:21-11 certified), **LSNJ**, or **VLJ**. SDLS has the current contact list and routes by case complexity. Coordinate the handoff via SDLS - don't cold-introduce to a pro bono attorney." },
          { title: "Track in Salesforce", body: "**Note the referral date + pro bono attorney assigned**. **Follow up with participant + attorney monthly.** Expungement timelines are measured in months to years, not weeks." },
        ],
      },
    ],
  },

  // ─── OPERATIONS ──────────────────────────────────────────────────
  {
    id: "common-resources-and-links",
    category: "operations",
    title: "Common resources, websites, and contact info",
    summary:
      "The websites you'll use every day, plus key NJRC and external contacts.",
    source: "LSM 2026 · LSC New Hire Packet",
    estimatedTime: "Reference",
    difficulty: "easy",
    keywords: ["resources", "websites", "contacts", "phone numbers", "links", "bookmarks"],
    blocks: [
      {
        kind: "links",
        heading: "Daily-use websites",
        items: [
          { label: "MCCS - NJ Municipal Court Case Search", href: "https://portal.njcourts.gov/webe41/MPAWeb/", description: "Run for every Participant" },
          { label: "PROMIS/Gavel - NJ criminal-conviction database", href: "https://portal.njcourts.gov/webe41/ExternalPGPA/CaptchaServlet", description: "Sentence date + jail days for Time Served letters" },
          { label: "NJ DOC Inmate Finder", href: "https://www20.state.nj.us/DOC_Inmate/inmatesearch", description: "Current + recently-released state-prison incarceration history" },
          { label: "NJ Courts JOC Public Access", href: "https://portal.njcourts.gov/webe41/JOCSearch/", description: "Judgments of Conviction (post-June 30, 2017)" },
          { label: "NJ MVC", href: "https://www.nj.gov/mvc/", description: "Surcharge, Restoration, Renewal, Duplicate, Change of Address portals" },
          { label: "VitalChek", href: "https://www.vitalchek.com/v/", description: "Out-of-state birth certificates (most states)" },
          { label: "NYC Office of Vital Records - BC info", href: "https://www.nyc.gov/site/doh/services/birth-death-records-birth.page", description: "NYC paper-only path" },
          { label: "PA Vital Records - Forms", href: "https://www.health.pa.gov/topics/certificates/Pages/Forms.aspx", description: "PA homeless-fee-waiver application" },
          { label: "Hawaii Vital Records (online)", href: "https://vitrec.ehawaii.gov/vitalrecords/", description: "HI online ordering" },
        ],
      },
      {
        kind: "kv",
        heading: "Internal NJRC roles to know",
        items: [
          { label: "CLO", value: "Chief Legal Officer (Damon Christian Watson, J.D. - also SDLS)" },
          { label: "SDLS", value: "Senior Director of Legal Services & Corporate Development" },
          { label: "DLS", value: "Director of Legal Services" },
          { label: "GC", value: "General Counsel" },
          { label: "Designated Individual (NJ BC)", value: "LSC who maintains the relationship with NJ Vital Records + makes Trenton trips" },
        ],
      },
      {
        kind: "kv",
        heading: "NJRC contact",
        items: [
          { label: "Email Damon", value: "dwatson@njreentry.org" },
          { label: "Fax", value: "201.604.7846" },
          { label: "HQ address", value: "591 Summit Ave, Suite 605B, Jersey City, NJ 07306" },
          { label: "Tax ID (for fee-waiver letters)", value: "47-2139071" },
        ],
      },
    ],
  },

  {
    id: "salesforce-basics",
    category: "operations",
    title: "Salesforce basics for the LSC",
    summary:
      "Where to put what - Legal/Info, DL Restoration, Attachments, Case Notes - and the cadence for keeping it current.",
    source: "LSM 2026 · Module 2 + LSC New Hire Packet",
    estimatedTime: "10 min read",
    difficulty: "easy",
    keywords: ["salesforce", "case notes", "legal/info", "dl restoration", "attachments"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "Salesforce is NJRC's system of record. The LSC owns the legal picture there - Legal/Info tab, DL Restoration tab, document attachments, and a continuous stream of Case Notes that document every action.",
      },
      {
        kind: "kv",
        heading: "Where things go",
        items: [
          { label: "Initial Assessment scan", value: "Attachments tab" },
          { label: "Court letters (sent + responses)", value: "Attachments tab; one Case Note per send" },
          { label: "PROMIS/Gavel + DOC + JOC PDFs", value: "Attachments tab" },
          { label: "Birth certificate / SS card / state ID receipts", value: "Attachments tab" },
          { label: "MCCS + MVC findings summary", value: "Legal/Info tab + matching Case Note" },
          { label: "License restoration status + ZIP code", value: "DL Restoration tab" },
          { label: "Every meeting / call / email", value: "New Case Note" },
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Cadence: write Case Notes contemporaneously",
        text:
          "Don't batch them at end-of-week. Write a Case Note immediately after every meaningful action - call, meeting, letter sent, response received, document scanned. Memory degrades fast and Salesforce is the institutional memory.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/sf-legal-services-provided.png",
        caption: "Salesforce 'Legal Services Provided' multi-select on the Legal Task form - pick every service you delivered (court letters, MCCS search, MVC call, LRRR request, JOC request, BC application, MVC ID, surcharge search, etc.).",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/sf-new-legal-muni-court.png",
        caption: "Salesforce 'New Legal - Municipal Court' record form - date, municipality, judge, fees vacated/converted, warrants recalled, comments. Use this when documenting a court outcome.",
      },
    ],
  },

  // ─── GETTING STARTED · supplementary ──────────────────────────────
  {
    id: "njrc-staff-roles",
    category: "getting-started",
    title: "Who else is on the NJRC team and what do they do?",
    summary:
      "Intake Coordinator, Case Manager, Social Worker, Employment Specialist - and where the LSC fits in the chain.",
    source: "LSM 2026 · Module 1",
    estimatedTime: "5 min read",
    difficulty: "easy",
    keywords: ["staff", "roles", "team", "intake", "case manager", "social worker", "employment specialist"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "Every NJRC participant interacts with **five staff roles**. Knowing what each one owns lets you triage faster - and stops you from re-doing work the Case Manager already did.",
      },
      {
        kind: "flowchart",
        heading: "Participant journey through NJRC staff",
        orientation: "vertical",
        nodes: [
          { tone: "start", title: "Intake Coordinator", body: "First point of contact. Explains the program, completes intake paperwork, creates the Salesforce profile, schedules appointments." },
          { title: "Case Manager", body: "Applies for benefits (Food Stamps, cash assistance, health insurance), helps replace basic IDs (SS card, BC, county/municipal ID), monthly check-ins." },
          { title: "Social Worker", body: "Biopsychosocial assessment → Individualized Service Plan. Connects to addiction treatment, mental health, MAT, harm reduction." },
          { title: "Employment Specialist", body: "Builds the Individualized Employment Plan, runs résumé / interview workshops, places participants with background-friendly employers, tracks 30/60/90-day employment." },
          { tone: "end", title: "Legal Services Coordinator (you)", body: "Owns the Legal Assessment, MCCS / MVC searches, court letters, license restoration, ID retrieval." },
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "Where you overlap with the Case Manager",
        text:
          "The CM/IC handles **first-pass** ID retrieval - birth certificate, SS card, county/municipal ID. **You** assist with the harder/legal-touching versions: out-of-state and international birth certificates, paper SS card applications, MVC state IDs, and anything that requires notarization, court letters, or NJRC's Vital Records Liaison. Coordinate; don't duplicate.",
      },
      {
        kind: "kv",
        heading: "Roles that report to / coordinate with the LSC",
        items: [
          { label: "SDLS", value: "Senior Director of Legal Services. Your supervisor. Approves restoration-fee payments, BC ordering, and all judgment calls. When in doubt, call Damon." },
          { label: "LSPM", value: "Legal Services Program Manager. Handles MVC payment requests + VitalChek logistics." },
          { label: "Vital Records Liaison", value: "Internal NJRC role that processes notarized BC paperwork. CC SDLS on every email." },
        ],
      },
    ],
  },
  {
    id: "referrals-attorney-pool",
    category: "getting-started",
    title: "When do I refer to LSNJ vs. VLJ vs. Public Defender vs. our Pro Bono Pool?",
    summary:
      "Decision tree for the four legal-aid pathways. The wrong referral wastes weeks and burns trust.",
    source: "LSM 2026 · Module 1 · LSC Process Cheat Sheet",
    estimatedTime: "4 min read",
    difficulty: "medium",
    keywords: ["referral", "attorney", "lsnj", "vlj", "public defender", "pro bono", "legal aid"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "NJRC's Legal Services Manual is intentionally narrow - LSCs handle municipal court, MVC, and ID retrieval. **Anything else needs a referral.** The choice between LSNJ, VLJ, the Public Defender, and our internal Pro Bono Attorney Pool is driven by case type and whether the participant is a defendant or plaintiff.",
      },
      {
        kind: "decisionTree",
        heading: "Where does this case go?",
        root: {
          kind: "decision",
          question: "Is the matter criminal (i.e., the participant is being charged or sentenced)?",
          yes: { kind: "result", tone: "info", label: "Local NJ Public Defender's Office (county-based)" },
          no: {
            kind: "decision",
            question: "Is the participant being sued (a defendant in a civil case)?",
            yes: {
              kind: "decision",
              question: "Is it a custody, restraining order, child support, or family-law matter?",
              yes: { kind: "result", tone: "tip", label: "NJRC Pro Bono Attorney Pool (via SDLS) - family-law focused" },
              no: { kind: "result", tone: "tip", label: "Legal Services of New Jersey (LSNJ)" },
            },
            no: {
              kind: "decision",
              question: "Is the participant the plaintiff (suing someone - wage theft, housing, etc.)?",
              yes: { kind: "result", tone: "warn", label: "Volunteer Lawyers for Justice (VLJ) - civil non-defendant" },
              no: { kind: "result", tone: "danger", label: "Talk to SDLS - likely outside scope, may need outside referral" },
            },
          },
        },
      },
      {
        kind: "kv",
        heading: "Key contacts",
        items: [
          { label: "LSNJ", value: "[lsnj.org/find-help](https://www.lsnj.org/find-help) - county-based intake; income-eligible only." },
          { label: "VLJ", value: "[vljnj.org](https://www.vljnj.org) - Newark-based pro bono for low-income NJ residents." },
          { label: "Public Defender", value: "Each county has its own office. Search '[county] public defender NJ'." },
          { label: "NJRC Pro Bono Pool", value: "Coordinated through SDLS. Operates under NJ Court Rule 1:21-11 certification. Reach out via Damon." },
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "When in doubt, call SDLS BEFORE referring",
        text:
          "A wrong referral can cost weeks while the participant waits for an intake call that never comes. If you're not sure where a case belongs, take 60 seconds to text Damon. Faster than guessing.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/lsnj-services-overview.png",
        caption: "**LSNJ services overview** (LSM 2026 / LSC New Hire Packet) - what LSNJ takes vs. doesn't, plus the per-county intake numbers and the income-eligibility cutoffs.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/vlj-client-services.png",
        caption: "**Volunteer Lawyers for Justice (VLJ) Client Services** (rev 2022) - the practice areas VLJ accepts (employment, housing, immigration, education) and the referral path.",
      },
    ],
  },
  {
    id: "search-input-cheat-sheet",
    category: "getting-started",
    title: "What inputs do I need for each search?",
    summary:
      "MCCS, PROMIS/Gavel, DOC, MVC searches - the minimum data you need to actually run each one. Print this and tape it next to your monitor.",
    source: "LSM 2026 · Module 1 · LSC Process Cheat Sheet",
    estimatedTime: "3 min read",
    difficulty: "easy",
    keywords: ["inputs", "cheat sheet", "search", "required data", "mccs", "mvc", "promis", "doc"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "The recurring frustration in week one is starting a search and realizing you're missing one field. This is the bare minimum each search needs.",
      },
      {
        kind: "table",
        heading: "Required input data per search",
        columns: ["Search", "Minimum inputs", "Output"],
        rows: [
          ["MCCS", "Name + DOB **OR** ID#", "Warrants, fines, suspensions, active cases, marijuana charges"],
          ["PROMIS/Gavel", "Name + birth year **OR** SBI#", "Criminal history, case list, sentencing info (jail days)"],
          ["DOC Offender Info", "Name **OR** SBI#", "Custody history (only goes back 1 year - print + preserve)"],
          ["MVC Restoration Fee", "ID# + ZIP on file with MVC + SSN (DOB substitutes if no SSN)", "License or registration restoration fee owed (or none)"],
          ["MVC Surcharge (NJSVS)", "ID# + DOB", "Surcharge total + restoration amount, or installment plan, or 'license # not exist'"],
          ["JOC (Judgment of Conviction)", "Case # + indictment year", "Custodial sentence detail for time-served letters"],
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Run an MCCS for *every* participant - even ones who say they have no record",
        text:
          "It costs you 90 seconds and only needs name + DOB. People forget tickets, decade-old cases, or warrants from a town they barely remember. The MCCS is your single highest-ROI search - never skip it.",
      },
      {
        kind: "callout",
        tone: "warn",
        title: "DOC results disappear after 1 year",
        text:
          "If a participant was released more than ~12 months ago, their DOC Offender Info page will likely be gone. **Print and preserve immediately** - for time-served letters you'll need this attached to the letter.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/nj-municipality-codes-one-sheet.png",
        caption: "**NJ Municipality Codes - one-sheet** (LSM 2026 / LSC New Hire Packet). The 4-digit MCCS court codes per municipality. When MCCS gives you a court name, this sheet maps to the code; when it gives you a code, this sheet maps to the court.",
      },
    ],
  },

  // ─── LEGAL ASSESSMENT · supplementary ─────────────────────────────
  {
    id: "assessment-cover-page-fields",
    category: "legal-assessment",
    title: "How do I fill out the Legal Assessment cover page?",
    summary:
      "Field-by-field walkthrough of every checkbox and blank on the Initial Assessment page. The cover page is where the LSC's whole mental model of a participant lives.",
    source: "LSM 2026 · Module 2 · LSC Participant Assessment 080525",
    estimatedTime: "8 min read",
    difficulty: "medium",
    keywords: ["assessment", "cover page", "initial", "fields", "checkboxes", "intake"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "The Initial Assessment page has 4 sections. Fill in what you know from intake; leave the rest blank until your first legal meeting with the participant. Don't guess.",
      },
      {
        kind: "kv",
        heading: "Section 1 - Identity & status",
        items: [
          { label: "Full Name", value: "First, middle, last, suffix (Jr/Sr/II/III)." },
          { label: "LSC", value: "Your name." },
          { label: "SSN", value: "Optional. If known, write the **last 4 only** unless full is needed for a specific filing." },
          { label: "DOB", value: "Required for almost every search. Get this." },
          { label: "Conviction Date", value: "Most recent. Drives time-served calculations." },
          { label: "Release Date", value: "From custody. Pairs with conviction date." },
          { label: "SBI #", value: "State Bureau of Identification number - appears on DOC paperwork. Used for PROMIS/Gavel + JOC searches." },
        ],
      },
      {
        kind: "kv",
        heading: "Section 2 - Documents (□ has □ needs)",
        items: [
          { label: "Social Security Card", value: "Has = participant brought it. Needs = LSC + Case Manager open the SS-5 process." },
          { label: "Birth Certificate", value: "Same logic. Trigger the BC decision tree if needs." },
          { label: "NJ MVC ID", value: "State-issued non-driver ID. Has / Needs." },
          { label: "NJ MVC DL", value: "Driver license. Has / Needs." },
          { label: "ID/DL #", value: "MVC's number for the participant. Required for both MVC searches." },
          { label: "Exp. Date", value: "If expired, may still be in MVC system but can't be used as ID." },
          { label: "MVC ZIP Code", value: "MVC's address-of-record ZIP. **Required** for restoration-fee search even if you have the right person." },
        ],
      },
      {
        kind: "kv",
        heading: "Section 3 - License-restoration flags",
        items: [
          { label: "RESTORATION FEE", value: "Check Lic. Rest. Fee, Reg. Rest. Fee, or No Fee after running the MVC Restoration Fee Search." },
          { label: "SURCHARGE", value: "Check Surcharges Owed (with $ amount), No Surcharges Owed, Current Payment Plan, or Lic. # Not Exist after running NJSVS." },
        ],
      },
      {
        kind: "kv",
        heading: "Section 4 - MCCS + Time Served",
        items: [
          { label: "MCCS flags", value: "DL Susp · Closed to MVC · Proposed Susp · Warrants · Fines · Active · Tix · MJ · No Cases (none / transferred / disposed)." },
          { label: "Time Served sources", value: "DOC Info · County Jail Face Sheet · PROMIS/Gavel · JOC · Other." },
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Use the Notes section as your scratch pad",
        text:
          "The bottom-of-page notes section is where you summarize MCCS findings, time-served candidates, MVC results, and your planned follow-up. When you (or another LSC) reopens this file 6 months from now, the notes are what saves you from re-reading every attached search result.",
      },
    ],
  },
  {
    id: "mvc-suspension-line-script",
    category: "license-restoration",
    title: "How do I (or the participant) call the MVC Suspension Line?",
    summary:
      "(609) 325-2885. The exact script + everything to ask the agent. Plan for 30 min – 2 hour hold.",
    source: "LSM 2026 · Module 2 · MVC Suspension Line Calling Instructions",
    estimatedTime: "5 min read",
    difficulty: "medium",
    keywords: ["mvc", "suspension line", "call", "script", "609 325 2885"],
    blocks: [
      {
        kind: "callout",
        tone: "info",
        title: "Phone number",
        text: "**(609) 325-2885**. NJ MVC Suspension Office. Be ready to be on hold 30 minutes to 2 hours.",
      },
      {
        kind: "flowchart",
        heading: "Call flow",
        nodes: [
          { tone: "start", title: "Have ID/DL # ready (or know that you don't)", body: "If no ID, the agent can verify identity with personal info." },
          { title: "Dial (609) 325-2885", body: "Brief recorded message → automatic queue." },
          { title: "If queue is high → leave callback", body: "MVC will call back. Make sure participant can answer unknown numbers." },
          { title: "When agent picks up: state the script", body: '"I believe my license is suspended, I would like to know how to restore it."' },
          { title: "Ask for ID# + ZIP on file", body: "If the participant doesn't have these, this call is the way to get them." },
          { title: "For each suspension issue, capture the details", body: "Ticket #, date of violation, offense, municipality, court phone." },
          { tone: "end", title: "Write everything down - text/email to LSC immediately", body: "Memory fades fast. ID# + ZIP unlock every other MVC search." },
        ],
      },
      {
        kind: "checklist",
        heading: "Information to ask the MVC agent for",
        items: [
          "**ID number** and **ZIP code** on file (essential for online searches)",
          "**Ticket Number** for each suspension",
          "**Date of Violation**",
          "**Offense Type**",
          "**Municipality** where the offense occurred",
          "**Phone number** of the municipality / court",
          "Any other suspension issues (Surcharges, Probation Fees, Superior Court, out-of-state, IDRC, IID Fee, Child Support)",
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Script for the agent",
        text:
          '"I believe my license is suspended, I would like to know how to restore it." - short, plain, gets the agent into the right workflow. Don\'t lead with surcharge or fee questions; start with restoration.',
      },
      {
        kind: "callout",
        tone: "warn",
        title: "If the only thing left is the restoration fee - tell us immediately",
        text:
          "NJRC has funding for both **License Restoration Fee** and **Registration Restoration Fee**. If MVC says that's the only outstanding item, send to SDLS for payment. Don't let the participant pay it themselves and then ask for reimbursement - that's not how the funding works.",
      },
    ],
  },
  {
    id: "assessment-when-to-redo",
    category: "legal-assessment",
    title: "When should I create a new Legal Assessment for a returning participant?",
    summary:
      "Returning after a long absence, post-arrest, or after a major life change. The signal-to-noise rule for refresh.",
    source: "LSM 2026 · Module 2",
    estimatedTime: "3 min read",
    difficulty: "easy",
    keywords: ["assessment", "redo", "refresh", "returning", "re-enrollment"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "The Legal Assessment is meant to be the *foundation*, not a one-time artifact. Re-do it when a participant's legal picture has materially changed - but don't waste cycles re-running searches that wouldn't have moved.",
      },
      {
        kind: "checklist",
        heading: "Trigger a new Legal Assessment when…",
        items: [
          "Participant has been **away from the program for 6+ months** and is re-engaging",
          "**New arrest, new charge, or new conviction** since the last assessment",
          "Participant **completed a major restoration step** (license restored, fines paid, expungement granted) and you want to confirm and move on",
          "**Address change** that affects the MVC ZIP on file - can break the restoration-fee search",
          "**Marriage / name change** - re-run searches under the new name",
          "Participant tells you they think there's something out there you didn't know about",
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "Don't refresh just because time has passed",
        text:
          "If nothing in the participant's life has changed, the old Legal Assessment is still accurate. A 2-month-old MCCS is fine. Refresh on triggers, not the calendar.",
      },
    ],
  },

  // ─── MCCS · supplementary ─────────────────────────────────────────
  {
    id: "mccs-by-ticket-or-complaint",
    category: "mccs",
    title: "How do I search MCCS by ticket number, complaint number, or DL?",
    summary:
      "Four ways to query MCCS. Use the alternative methods when name+DOB returns nothing or a spelling is off.",
    source: "LSM 2026 · Module 3",
    estimatedTime: "3 min read",
    difficulty: "easy",
    keywords: ["mccs", "ticket number", "complaint number", "driver license", "search"],
    blocks: [
      {
        kind: "comparison",
        heading: "Four MCCS search methods",
        options: [
          { title: "Name + DOB", subtitle: "Default", tone: "tip", items: ["Use first/middle/last name + date of birth", "Default for almost every Legal Assessment", "Use this unless name spelling is uncertain or you got zero hits"] },
          { title: "Driver's License #", tone: "info", items: ["Single most reliable identifier", "Use to confirm a person if name+DOB returned multiple matches", "Use when participant has changed names"] },
          { title: "Ticket Number", items: ["Format: court code + prefix + sequence (no year)", "Use when participant brings you a physical ticket and you want to pull the record", "MCCS shows the example format on the search screen"] },
          { title: "Case (Complaint) Number", items: ["Format: court code + prefix + year + sequence", "Use when you have a Complaint or Indictment # from another source", "Returns the specific case directly"] },
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Outstanding vs. All",
        text:
          "Status defaults to **Outstanding** (active cases, warrants, unpaid fines/tickets). Switch to **All** to also see disposed and transferred matters - useful for time-served eligibility checks and when 'Outstanding' returns nothing.",
      },
    ],
  },
  {
    id: "mccs-print-for-assessment",
    category: "mccs",
    title: "How do I print MCCS results properly for a Legal Assessment?",
    summary:
      "Which tabs to select before printing each kind of result. Get the wrong tab and the relief request is missing key info.",
    source: "LSM 2026 · Module 3",
    estimatedTime: "4 min read",
    difficulty: "medium",
    keywords: ["mccs", "print", "tabs", "bail", "time payment", "warrant", "additional details"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "MCCS' Additional Details page has multiple tabs (Warrant, Bail, Involved Persons, Time Payment) and each one prints differently. The court letter you're about to send needs the **right tab visible** at print time, or the judge can't see what they need.",
      },
      {
        kind: "table",
        heading: "Print tab by case type",
        columns: ["What you're working on", "Tab to select before printing", "Why"],
        rows: [
          ["Warrant recall request", "**Bail** tab", "Shows the bail amount the judge needs to consider"],
          ["Time-served / fine-conversion request", "**Time Payment** tab", "Shows assessed total, payments made, balance due"],
          ["Active case with hearing date", "**Default Additional Details**", "Shows hearing date and court info on the front"],
          ["DL Suspended / C2MVC", "**Default Additional Details**", "Front page shows the suspension status"],
        ],
      },
      {
        kind: "checklist",
        heading: "Standard MCCS print routine for the Legal Assessment",
        items: [
          "**Print every Results page** - the search summary itself.",
          "**Print Additional Details for every active case** (tickets, fines, warrants, C2MVC, DL Suspensions, upcoming hearings).",
          "If there's an **associated case list at the bottom** of an Additional Details page, print only the FIRST page in the set - the others are duplicates.",
          "Toggle to the **Bail tab** before printing a warrant.",
          "Toggle to the **Time Payment tab** before printing a fine or ticket.",
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "If you skip the tab toggle, you may have to re-print",
        text:
          "It's easy to print a warrant page with the default tab and not realize the bail amount is missing. Eyeball every printed page before stapling it into the assessment - does the right info show?",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/mccs-complaint-detail.png",
        caption: "MCCS Complaint Detail page - fine assessment, balance due, time payment summary, charge information. Default view; toggle Bail tab if it's a warrant matter.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/time-payment-order-inquiry.png",
        caption: "Court's internal Time Payment Order Inquiry printout (page 2 of 3) - sometimes the court will fax this back when responding to a Time-Served Letter. Shows assessed amount, payment frequency, and remaining balance.",
      },
    ],
  },

  // ─── TIME SERVED · supplementary ──────────────────────────────────
  {
    id: "what-time-served-counts",
    category: "time-served",
    title: "What time served actually counts? (and what doesn't)",
    summary:
      "Federal yes, out-of-state no, halfway-house sometimes, ankle monitor no. The eligibility rules every LSC fumbles in week one.",
    source: "LSM 2026 · Module 4",
    estimatedTime: "4 min read",
    difficulty: "medium",
    keywords: ["time served", "eligibility", "federal", "out of state", "halfway house", "ankle monitor", "probation"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "Two requirements every time-served request must meet:",
      },
      {
        kind: "callout",
        tone: "info",
        title: "Rule 1 - Time must come AFTER the offense",
        text:
          "Time Served only moves backward in time. If the ticket was issued **December 31, 2023**, only time spent in custody **after** that date is eligible. Earlier custody can't be applied - the participant hadn't done the offense yet.",
      },
      {
        kind: "callout",
        tone: "info",
        title: "Rule 2 - Monetary value must meet or exceed the fine",
        text:
          "Most courts apply **$50 × days incarcerated**. So a $500 fine needs at least 10 days. Not a hard rule - judges show leniency - but plan around it.",
      },
      {
        kind: "table",
        heading: "What counts as Time Served",
        columns: ["Type of custody", "Counts?", "Notes"],
        rows: [
          ["NJ county jail", "**Yes**", "Standard. Use DOC Inmate Finder + county jail records."],
          ["NJ state prison", "**Yes**", "Standard. Use PROMIS/Gavel + DOC + JOC."],
          ["Federal prison", "**Yes**", "Often surprises people - yes, federal counts. Get BOP records."],
          ["Out-of-state jail / prison", "**No**", "Not eligible for NJ municipal court time-served credit."],
          ["Drug treatment program", "**Sometimes**", "Some courts accept; depends on the judge. Ask first."],
          ["Halfway house", "**Sometimes**", "Some courts accept inpatient halfway-house time. Verify with court."],
          ["Ankle monitor / EM", "**No**", "Most courts will not credit. Don't pitch it."],
          ["Probation", "**No**", "Not custody."],
          ["Parole", "**No**", "Not custody."],
        ],
      },
    ],
  },
  // ─── BIRTH CERTIFICATES · country-specific (formerly LLM-only) ────
  {
    id: "order-jamaica-birth-certificate",
    category: "birth-certificates",
    title: "How do I order a Jamaica birth certificate?",
    summary:
      "Registrar General's Department (RGD) - paper application via the Jamaican Consulate in NYC. Fee + processing time vary; plan for 2-4 months.",
    source: "LSM 2026 · Module 8 · Jamaica Vital Records Application",
    estimatedTime: "5 min read",
    difficulty: "medium",
    keywords: ["jamaica", "birth certificate", "rgd", "consulate", "registrar general", "kingston"],
    blocks: [
      {
        kind: "callout",
        tone: "info",
        title: "Issuing agency",
        text:
          "**Registrar General's Department (RGD)**, Twickenham Park, Spanish Town, Jamaica. The Jamaican Consulate General in New York City processes US-side paperwork.",
      },
      {
        kind: "steps",
        heading: "Process",
        items: [
          { title: "Collect participant info per the BC info checklist", body: "Mother's maiden name is critical; many Jamaican BCs only list the mother." },
          { title: "Print + complete the Jamaica Vital Records Application", body: "Bundled PDF below. Type all entries; participant signs in pen." },
          { title: "Notarize the application", body: "Required for international submissions. Use NJRC notary or coordinate with SDLS." },
          { title: "Mail packet to Jamaican Consulate General in NYC", body: "767 Third Avenue, 2nd Floor, New York, NY 10017. Include cashier's check or money order - no personal checks." },
          { title: "Track + follow up after 60 days", body: "Consulate forwards to RGD in Jamaica. Total turnaround often 2-4 months." },
        ],
      },
      {
        kind: "kv",
        heading: "Key contacts",
        items: [
          { label: "Jamaican Consulate (NYC)", value: "767 Third Ave, 2nd Floor, New York, NY 10017" },
          { label: "Phone", value: "(212) 935-9000", mono: true },
          { label: "RGD Jamaica", value: "[rgd.gov.jm](https://www.rgd.gov.jm)" },
        ],
      },
      {
        kind: "links",
        heading: "Forms",
        items: [
          { label: "Jamaica Vital Records Application (bundled)", href: "/how-to-assets/bc-forms/27 Jamaica Vital Records Application.pdf" },
          { label: "RGD official site", href: "https://www.rgd.gov.jm" },
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "Verify current fees with the consulate",
        text:
          "Jamaica's fees change periodically and are payable in USD via cashier's check. Call the consulate before mailing to confirm current amount + accepted payment forms.",
      },
    ],
  },
  {
    id: "order-trinidad-birth-certificate",
    category: "birth-certificates",
    title: "How do I order a Trinidad and Tobago birth certificate?",
    summary:
      "Civil Registry, Office of the Attorney General. Online OR paper application; consulate in NYC for US-side submissions.",
    source: "LSM 2026 · Module 8 · Trinidad and Tobago Vital Records",
    estimatedTime: "4 min read",
    difficulty: "medium",
    keywords: ["trinidad", "tobago", "birth certificate", "civil registry", "consulate"],
    blocks: [
      {
        kind: "comparison",
        heading: "Online vs. paper",
        options: [
          { title: "Online (faster)", subtitle: "Recommended", tone: "tip", items: ["Available via Civil Registry's web portal", "Requires participant to upload ID + payment", "Faster turnaround (often 4-6 weeks)"] },
          { title: "Paper / Mail-In", items: ["Bundled PDF below", "Submit through TT Consulate General (NYC)", "Slower (8-12 weeks typical)"] },
        ],
      },
      {
        kind: "kv",
        heading: "Key contacts",
        items: [
          { label: "TT Consulate General (NYC)", value: "125 Maiden Lane, 5th Floor, New York, NY 10038" },
          { label: "Phone", value: "(212) 682-7272", mono: true },
          { label: "Civil Registry (TT)", value: "[ag.gov.tt](https://www.ag.gov.tt)" },
        ],
      },
      {
        kind: "links",
        heading: "Forms",
        items: [
          { label: "Trinidad & Tobago Online Application reference (bundled)", href: "/how-to-assets/bc-forms/28 Trinidad and Tobago Vital Records Online Application.pdf" },
          { label: "Trinidad & Tobago Mail-In Application (bundled)", href: "/how-to-assets/bc-forms/29 Trinidad and Tobago Vital Records Mail In Application.pdf" },
        ],
      },
    ],
  },
  {
    id: "order-manitoba-birth-certificate",
    category: "birth-certificates",
    title: "How do I order a Manitoba (Canada) birth certificate?",
    summary:
      "Vital Statistics Manitoba - online or paper. CAD payment via credit card; turnaround 4-6 weeks regular, 1-2 weeks expedited.",
    source: "LSM 2026 · Module 8 · Manitoba Vital Records",
    estimatedTime: "4 min read",
    difficulty: "medium",
    keywords: ["manitoba", "canada", "birth certificate", "vital statistics", "winnipeg"],
    blocks: [
      {
        kind: "callout",
        tone: "info",
        title: "Issuing agency",
        text:
          "**Vital Statistics Agency of Manitoba** - based in Winnipeg. Accepts requests from outside Canada via credit card.",
      },
      {
        kind: "comparison",
        heading: "Online vs. mail",
        options: [
          { title: "Online (recommended)", tone: "tip", items: ["Apply via vitalstats.gov.mb.ca", "ID upload + Visa/MC/Amex payment", "Regular: ~4-6 weeks · Expedited: ~1-2 weeks", "Track order online"] },
          { title: "Mail-In", items: ["Bundled PDF below", "Money order in Canadian dollars (CAD)", "Slower turnaround"] },
        ],
      },
      {
        kind: "links",
        heading: "Forms & resources",
        items: [
          { label: "Vital Statistics Manitoba - main page", href: "https://vitalstats.gov.mb.ca" },
          { label: "Manitoba Online Application reference (bundled)", href: "/how-to-assets/bc-forms/19 Manitoba Vital Records Online Application.pdf" },
          { label: "Manitoba Track Order (bundled)", href: "/how-to-assets/bc-forms/20 Manitoba Vital Records Track Order.pdf" },
          { label: "Manitoba Mail-In Application (bundled)", href: "/how-to-assets/bc-forms/21 Manitoba Vital Records Mail-In Application.pdf" },
        ],
      },
    ],
  },
  {
    id: "order-ontario-birth-certificate",
    category: "birth-certificates",
    title: "How do I order an Ontario (Canada) birth certificate?",
    summary:
      "ServiceOntario - online, by phone, or by mail. CAD fees; online is fastest.",
    source: "LSM 2026 · Module 8 · Ontario Vital Records",
    estimatedTime: "4 min read",
    difficulty: "medium",
    keywords: ["ontario", "canada", "birth certificate", "service ontario", "thunder bay"],
    blocks: [
      {
        kind: "callout",
        tone: "info",
        title: "Issuing agency",
        text:
          "**ServiceOntario Office of the Registrar General** in Thunder Bay, ON. Accepts US-side requests; payment via credit card or international money order.",
      },
      {
        kind: "steps",
        heading: "Online process (recommended)",
        items: [
          { title: "Go to ontario.ca/page/birth-certificate", body: "Use the 'Order online' button. Participant info needed: full name at birth, DOB, place of birth (city in Ontario), parents' names." },
          { title: "Choose certificate type", body: "Standard certificate covers most US uses. Long-form certificate has parents' info + sometimes required for citizenship-adjacent processes." },
          { title: "Pay with credit card", body: "Fees in CAD: ~$25 for standard, ~$35 for long-form. Add expedited shipping if needed." },
          { title: "Track order via reference number", body: "Standard turnaround: 6-8 weeks. Expedited: ~5 business days + shipping." },
        ],
      },
      {
        kind: "links",
        heading: "Forms & resources",
        items: [
          { label: "ServiceOntario birth certificate page", href: "https://www.ontario.ca/page/birth-certificate" },
          { label: "Ontario Application reference (bundled)", href: "/how-to-assets/bc-forms/22 Ontario Vital Records Application.pdf" },
          { label: "Ontario Online Application reference (bundled)", href: "/how-to-assets/bc-forms/23 Ontario Vital Records Online Application.pdf" },
          { label: "Ontario Mail-In Application (bundled)", href: "/how-to-assets/bc-forms/24 Ontario Vital Records Mail-In Application.pdf" },
        ],
      },
    ],
  },
  {
    id: "order-belize-birth-certificate",
    category: "birth-certificates",
    title: "How do I order a Belize birth certificate?",
    summary:
      "Vital Statistics Unit in Belmopan. Paper application via the Belize Embassy in DC or directly to the Vital Statistics Unit.",
    source: "LSM 2026 · Module 8 · Belize Vital Records",
    estimatedTime: "4 min read",
    difficulty: "medium",
    keywords: ["belize", "birth certificate", "vital statistics", "belmopan", "embassy"],
    blocks: [
      {
        kind: "callout",
        tone: "info",
        title: "Issuing agency",
        text:
          "**Vital Statistics Unit, Government of Belize** - Belmopan. Standard route is mail-in via the Belize Embassy in Washington, DC.",
      },
      {
        kind: "steps",
        heading: "Process",
        items: [
          { title: "Complete the Vital Records Application (bundled below)", body: "Type entries; participant signs in pen." },
          { title: "Notarize", body: "Use NJRC notary." },
          { title: "Mail to Belize Embassy (DC)", body: "2535 Massachusetts Avenue NW, Washington DC 20008. Include money order in USD; consulate forwards to Belmopan." },
          { title: "Wait 8-12 weeks", body: "International mail + processing in Belmopan adds significant time. Track via the embassy if delayed." },
        ],
      },
      {
        kind: "kv",
        heading: "Key contacts",
        items: [
          { label: "Belize Embassy (DC)", value: "2535 Massachusetts Avenue NW, Washington DC 20008" },
          { label: "Phone", value: "(202) 332-9636", mono: true },
        ],
      },
      {
        kind: "links",
        heading: "Forms",
        items: [
          { label: "Belize Vital Records Application (bundled)", href: "/how-to-assets/bc-forms/31 Belize Vital Records Application.pdf" },
        ],
      },
    ],
  },
  {
    id: "order-india-birth-certificate",
    category: "birth-certificates",
    title: "How do I order an India birth certificate?",
    summary:
      "India does NOT have universal birth registration. Procedure varies by state and date of birth - often requires a registered alternative ('non-availability of birth certificate') if the participant was never registered.",
    source: "LSM 2026 · Module 8 · India Register Page",
    estimatedTime: "6 min read",
    difficulty: "hard",
    keywords: ["india", "birth certificate", "non availability", "registrar", "embassy", "consulate"],
    blocks: [
      {
        kind: "callout",
        tone: "danger",
        title: "India is the hardest BC jurisdiction we work with",
        text:
          "Many Indian-born participants were never formally registered at birth - common for births before 2000, especially rural births. The 'birth certificate' may not exist in the form US agencies expect. Consult SDLS before promising the participant a quick turnaround.",
      },
      {
        kind: "decisionTree",
        heading: "First, determine: is there a registered birth?",
        root: {
          kind: "decision",
          question: "Was the participant's birth formally registered with their state's local Registrar of Births and Deaths?",
          yes: { kind: "result", tone: "tip", label: "Apply to that local Registrar's office for a certified copy (process varies by state)" },
          no: { kind: "result", tone: "warn", label: "Pursue Non-Availability of Birth Certificate (NABC) + delayed registration - see steps below" },
        },
      },
      {
        kind: "steps",
        heading: "If no registered birth - Non-Availability Certificate (NABC) process",
        items: [
          { title: "Obtain NABC from the relevant local Municipal Corporation / Panchayat", body: "This formally certifies that no birth record exists for the participant. Often requires a relative in India to apply on participant's behalf." },
          { title: "File for Delayed Registration of Birth", body: "Procedure varies by state. Generally requires NABC + affidavit from parents (or relatives if parents deceased) + supporting docs (school records, hospital records if any)." },
          { title: "Once registered, request the birth certificate", body: "From the relevant local Registrar of Births and Deaths." },
        ],
      },
      {
        kind: "kv",
        heading: "Key contacts",
        items: [
          { label: "Embassy of India (DC)", value: "2107 Massachusetts Avenue NW, Washington DC 20008" },
          { label: "Consulate General of India (NYC)", value: "3 East 64th Street, New York NY 10065" },
          { label: "Phone (NYC)", value: "(212) 774-0600", mono: true },
        ],
      },
      {
        kind: "links",
        heading: "Forms & resources",
        items: [
          { label: "India Register Page reference (bundled)", href: "/how-to-assets/bc-forms/32 India Register Page.pdf" },
          { label: "Embassy of India in USA", href: "https://www.indianembassyusa.gov.in" },
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Workaround for US purposes",
        text:
          "If the BC process is going to take 6+ months, look at alternative documents the participant might already have - Indian passport, naturalization paperwork, school leaving certificate. Some US agencies will accept these as identity proof in lieu of a BC.",
      },
    ],
  },

  // ─── OPERATIONS · Salesforce depth ────────────────────────────────
  {
    id: "salesforce-case-notes",
    category: "operations",
    title: "How do I write a good Salesforce Case Note?",
    summary:
      "Date the action. Name the source. State what you did. State what's next. Three sentences max. The Case Note is the institutional memory.",
    source: "LSM 2026 · Module 1 · Salesforce mechanics",
    estimatedTime: "3 min read",
    difficulty: "easy",
    keywords: ["salesforce", "case notes", "documentation", "log", "audit"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "Case Notes are how the next LSC (or you, six months from now) reconstructs what happened. They're also the audit trail funders care about. Write them **contemporaneously** - not at the end of the week.",
      },
      {
        kind: "comparison",
        heading: "Bad vs. good Case Note",
        options: [
          {
            title: "Bad",
            tone: "warn",
            items: [
              "\"Talked to participant\"",
              "\"Working on stuff\"",
              "\"Will follow up\"",
              "(none of these are useful 30 days from now)",
            ],
          },
          {
            title: "Good",
            tone: "tip",
            items: [
              "\"**11/12** - MCCS run; 2 active warrants in Newark + Paterson, no time-served available yet. Drafted warrant-recall letter for both.\"",
              "\"**11/14** - Faxed warrant-recall to Newark Muni Court (973-733-3333). Awaiting reply by 11/28.\"",
              "\"**11/28** - Newark recalled both warrants; calendar set for 12/15. Updated participant by phone.\"",
            ],
          },
        ],
      },
      {
        kind: "checklist",
        heading: "Anatomy of a good Case Note",
        items: [
          "**Date** the action (not the note creation)",
          "**Action** - what specifically you did (faxed letter, made phone call, updated MVC ZIP, etc.)",
          "**Source** - court, agency, document, phone number, person you spoke with",
          "**Outcome** - what came back, or that you're awaiting reply",
          "**Next step** - what you plan to do next, by when",
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Cadence: write Case Notes contemporaneously",
        text:
          "Don't batch them at end-of-week. Write a Case Note immediately after every meaningful action - call, meeting, letter sent, response received, document scanned. Memory degrades fast.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/sf-new-task-picker.png",
        caption: "Salesforce New Task → Select a record type → pick **Legal Task/Note**. Don't pick CRC, ESN, or NJRC Case Management - those belong to other staff roles.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/sf-new-task-legal.png",
        caption: "Salesforce 'New Task: Legal Task/Note' form - Subject, Due Date, Date of Contact, and the Comments field where the actual case-note prose goes.",
      },
    ],
  },
  {
    id: "salesforce-attachments",
    category: "operations",
    title: "How do I name and organize Salesforce attachments?",
    summary:
      "Consistent naming is the difference between a usable participant file and a junk drawer. Use the convention every NJRC site uses.",
    source: "LSM 2026 · NJRC Salesforce conventions",
    estimatedTime: "3 min read",
    difficulty: "easy",
    keywords: ["salesforce", "attachments", "naming", "files", "documents"],
    blocks: [
      {
        kind: "kv",
        heading: "Naming convention",
        items: [
          { label: "Format", value: "`YYYY-MM-DD_DocType_Detail.pdf`", mono: true },
          { label: "Example", value: "`2026-04-12_LegalAssessment_v1.pdf`", mono: true },
          { label: "Example", value: "`2026-05-03_WarrantRecallLetter_NewarkMuni.pdf`", mono: true },
          { label: "Example", value: "`2026-05-15_BC_NJ.pdf`", mono: true },
          { label: "Example", value: "`2026-06-01_StateID_Receipt.pdf`", mono: true },
        ],
      },
      {
        kind: "checklist",
        heading: "What to upload to Salesforce (always)",
        items: [
          "Every **Legal Assessment** (initial + every refresh)",
          "Every **court letter** sent (after signing)",
          "Every **MVC payment receipt** (restoration fee, registration)",
          "Every **vital record received** (BC, SS card)",
          "Every **state ID receipt** + the ID itself when received",
          "**Notarized documents** - both the unsigned and signed versions",
          "Email correspondence with courts / MVC if substantive",
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "Don't upload SSNs or full credit-card info",
        text:
          "Redact before uploading. The exception is the SS card itself - but flag the document as sensitive in Salesforce when you upload, and limit access per NJRC policy.",
      },
    ],
  },
  {
    id: "salesforce-restoration-tab",
    category: "operations",
    title: "What goes in the Salesforce DL Restoration tab?",
    summary:
      "NJ ID#, ZIP, SSN, restoration fees, surcharge status - the data that unlocks every MVC search and payment request.",
    source: "LSM 2026 · NJRC Salesforce conventions",
    estimatedTime: "3 min read",
    difficulty: "easy",
    keywords: ["salesforce", "dl restoration", "tab", "fields", "mvc"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "The DL Restoration tab is where the LSC keeps the canonical copy of the participant's MVC-relevant identifiers. SDLS reads from this tab when processing License Restoration Requests - so missing or wrong data = denied request.",
      },
      {
        kind: "kv",
        heading: "Required fields",
        items: [
          { label: "NJ ID/DL #", value: "Participant's MVC-issued number." },
          { label: "ZIP on file with MVC", value: "Required for restoration-fee search; not always = current address." },
          { label: "SSN", value: "Required for restoration-fee search (DOB substitutes if unavailable)." },
          { label: "Restoration Fee Status", value: "DL fee owed · Reg fee owed · No fee · Pending" },
          { label: "Surcharge Status", value: "Owed ($amount) · No surcharge · Current payment plan · Lic # Not Exist" },
          { label: "Last Restoration Action", value: "Free-text most recent action - \"submitted to SDLS for payment 11/12\", etc." },
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "MVC ZIP is the #1 source of failed searches",
        text:
          "MVC keeps an internal ZIP for every record. It's not always the participant's current address. If you can't get the restoration-fee search to load, it's almost always a ZIP mismatch - call the MVC Suspension Line to verify.",
      },
    ],
  },

  // ─── COURT LETTERS · supplementary ────────────────────────────────
  {
    id: "certified-mail-protocol",
    category: "court-letters",
    title: "When and how do I send court letters by certified mail vs. fax vs. email?",
    summary:
      "Fax is preferred for fast turnaround. Email when explicitly accepted. Certified mail for high-stakes or no-fax-available courts.",
    source: "LSM 2026 · Module 5 · Court Letters",
    estimatedTime: "3 min read",
    difficulty: "medium",
    keywords: ["certified mail", "fax", "email", "court letter", "transmission"],
    blocks: [
      {
        kind: "comparison",
        heading: "Transmission methods",
        options: [
          {
            title: "Fax (default)",
            subtitle: "Preferred",
            tone: "tip",
            items: [
              "**Fastest** - court receives within minutes",
              "Confirmation page = proof of transmission",
              "Use the fax # from the [LSC Court Directory](/lookup) (live data)",
              "Always print + save the fax confirmation",
            ],
          },
          {
            title: "Email",
            tone: "info",
            items: [
              "**Only when explicitly accepted** by the specific court",
              "Some Muni Courts accept; many do not",
              "Use the Administrator's email + CC the general court inbox",
              "Save the sent message + read receipt",
            ],
          },
          {
            title: "Certified Mail (USPS)",
            tone: "warn",
            items: [
              "**High-stakes** matters where you need a signature receipt",
              "Courts without working fax + don't accept email",
              "Slower - 3-5 business days + processing",
              "Save the green card + tracking #",
            ],
          },
        ],
      },
      {
        kind: "checklist",
        heading: "Always - regardless of method",
        items: [
          "**Print the cover page / confirmation** and staple to your retained copy",
          "**Save in Salesforce** under the participant's profile with the date sent",
          "**Add a Case Note** with the transmission method + confirmation #/email subject + expected response window",
          "**CC the participant** by email so they have a copy",
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "If you don't hear back within 2-3 weeks → follow up",
        text:
          "Most Muni Courts respond within 2 weeks. If you sent a letter and haven't heard back in 21 days, call the court Administrator's office. Don't wait passively.",
      },
    ],
  },
  {
    id: "hearing-day-coaching",
    category: "court-letters",
    title: "How do I coach a participant for a Municipal Court hearing day?",
    summary:
      "What to wear, what to say, what to bring, what NOT to say. The 15-minute call before the hearing that prevents disasters.",
    source: "LSM 2026 · LSC training materials",
    estimatedTime: "5 min read",
    difficulty: "medium",
    keywords: ["hearing", "court day", "coaching", "participant", "prep"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "When a participant has a hearing date, your job isn't to attend - you're not the attorney. Your job is to **prepare the participant** so they show up on time, dressed appropriately, with the right paperwork, knowing what to expect.",
      },
      {
        kind: "timeline",
        heading: "Hearing-day prep timeline",
        events: [
          { when: "1-2 weeks before", title: "Confirm the hearing date + time", body: "Call the court directly. Re-verify date hasn't moved. Update Case Note." },
          { when: "1 week before", title: "Mail or email the participant a hearing-prep packet", body: "Includes hearing date/time/court address, what to wear, what to bring, what to expect." },
          { when: "2 days before", title: "Schedule a 15-min prep call", body: "Walk through the specifics. Confirm transportation. Confirm childcare if needed." },
          { when: "Day before", title: "Text reminder + court address", tone: "info", body: "\"Your hearing is tomorrow at 9am at [court]. I dropped off your letter on [date]. Reply OK so I know you got this.\"" },
          { when: "Hearing day morning", title: "Be reachable by phone", tone: "warn", body: "Participants sometimes need real-time coaching from the courthouse. Don't be in a meeting between 8am and the hearing time." },
          { when: "After hearing", title: "Call participant for a debrief", tone: "tip", body: "What happened? What did the judge say? Any follow-up actions? Update Salesforce immediately." },
        ],
      },
      {
        kind: "checklist",
        heading: "What to tell the participant to bring",
        items: [
          "**Photo ID** (state ID, driver license, or any government photo ID)",
          "**Copy of any letter NJRC sent** to the court on their behalf",
          "**Copies of all attachments** that went with the letter (DOC, JOC, PROMIS, etc.)",
          "**Pen** for signing anything the court provides",
          "**Notepad** to write down anything the judge says",
          "**Cash or card** for any minimal court fees that may come up",
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "What to tell the participant NOT to say",
        text:
          "Don't admit guilt to anything they're not already convicted of. Don't make excuses. Don't argue with the judge. If asked about an old case they don't remember - say so honestly. The judge appreciates clarity, not invented details.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Dress code",
        text:
          "Business casual. Closed-toe shoes. Collared shirt. No graphic t-shirts. No hat in the courtroom. NJRC sites often have donated clothing closets - coordinate with the Employment Specialist if needed.",
      },
    ],
  },

  // ─── GETTING STARTED · NJRC pays for what ─────────────────────────
  {
    id: "what-njrc-pays-for",
    category: "getting-started",
    title: "What does NJRC pay for, and what does the participant pay for?",
    summary:
      "License + registration restoration fees: yes. Surcharges: no. State ID + duplicate: 1 per calendar year. BC: 1 per calendar year. The full money matrix.",
    source: "LSM 2026 · NJRC Funding Policy",
    estimatedTime: "3 min read",
    difficulty: "easy",
    keywords: ["njrc pays", "funding", "policy", "money", "fees", "what we cover"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "Funding rules drive every recommendation you make. Knowing what NJRC will (and won't) cover prevents you from offering something we can't deliver - and prevents the participant from spending money on something NJRC would have paid for.",
      },
      {
        kind: "table",
        heading: "What NJRC pays for",
        columns: ["Item", "NJRC pays?", "Limits / conditions"],
        rows: [
          ["License Restoration Fee ($100)", "**Yes**", "Submit to SDLS *after* all other suspension issues resolved"],
          ["Registration Restoration Fee ($100)", "**Yes**", "Same - after other suspensions cleared"],
          ["NJ State ID (initial)", "**Yes**", "1 per calendar year"],
          ["NJ State ID renewal/duplicate", "**Yes**", "Same 1-per-year limit"],
          ["NJ DL renewal/duplicate", "**Yes**", "Same 1-per-year limit"],
          ["REAL ID **renewal or duplicate**", "**Yes**", "If participant already has REAL ID"],
          ["REAL ID **first-time conversion**", "**No**", "Participant pays for the upgrade"],
          ["Birth Certificate (any jurisdiction)", "**Yes**", "1 per calendar year, 1 copy at a time"],
          ["Social Security Card replacement", "**Yes** (no fee anyway)", "SSA doesn't charge for replacements"],
          ["MVC Surcharges", "**No**", "Participant's responsibility - NJRC has no funding for surcharges"],
          ["Court fines (paying off)", "**No**", "Pursue time-served / hardship instead"],
          ["Notary fees (NJRC notary)", "**No charge** - internal", "Use NJRC's facility notary for free"],
          ["Pro bono attorney fees", "**Yes** (or zero)", "Pro Bono Pool / LSNJ / VLJ - no fee to participant"],
          ["IDRC + IID fees", "**No**", "Participant's responsibility"],
          ["Probation/parole fees", "**No**", "Same"],
          ["Child support arrears", "**No**", "Family-court matter; refer for legal aid"],
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "When participant's only restoration barrier is the fee - that's our sweet spot",
        text:
          "If MVC tells the participant the **only** thing left is the License or Registration Restoration Fee, immediately submit to SDLS for payment. Don't let the participant pay it themselves and then ask for reimbursement - that's not how the funding works.",
      },
      {
        kind: "callout",
        tone: "warn",
        title: "Surcharge balances are not forgivable or negotiable",
        text:
          "NJRC has no funding for surcharges. Participant must pay them - or get on a payment plan via the [Surcharge Hardship Letter](/how-to/surcharge-hardship-letter) process. Don't promise a participant we can pay surcharges.",
      },
    ],
  },

  // ─── OPERATIONS · Notary + Websites ───────────────────────────────
  {
    id: "become-a-notary",
    category: "operations",
    title: "How do I get my NJ Notary Public commission?",
    summary:
      "5-step process: register → exam → registration → legislator approval → swearing-in. NJRC reimburses up to $110 of expenses.",
    source: "LSM 2026 · NJRC Notary Public Guide and Policy",
    estimatedTime: "8 min read",
    difficulty: "medium",
    keywords: ["notary", "commission", "njrc", "exam", "legislator"],
    blocks: [
      {
        kind: "callout",
        tone: "info",
        title: "NJRC policy: 2 notaries per facility",
        text:
          "Each facility may have up to 2 employees serving as Notary Public for NJRC clients. The Facility Director and Legal Services Coordinator should both be certified. Notify General Counsel and HR before applying.",
      },
      {
        kind: "flowchart",
        heading: "5-step commission process",
        nodes: [
          { tone: "start", title: "Step 1: Apply for new Notary Commission online", body: "[njportal.com/DOR/Notary/](https://www.njportal.com/DOR/Notary/) - review the applicant process. Pay ~$5 commission fee. Get an Exam Access Key." },
          { title: "Step 2: Take the Notary Public Exam", body: "Use Exam Access Key. 80% to pass. 3 attempts allowed; can keep the [NJ Notary Public Manual](https://www.nj.gov/treasury/revenue/pdf/NotaryPublicManual.pdf) open in another tab during the exam." },
          { title: "Step 3: Register the Commission", body: "Back in the portal: 'New Notary Registration' → enter info, provide Exam Access Key for grade verification, select a Legislator to approve, pay $30 registration fee." },
          { title: "Step 4: Wait ~2 weeks for Legislator approval", body: "Will receive a letter from the chosen Legislator + Commission Packet from NJ Treasury. If denied, contact General Counsel." },
          { tone: "end", title: "Step 5: Swear in at County Clerk's office", body: "Take the Commission Packet to your County Clerk, pay $15 swearing-in fee, register the commission. Receive email confirmation of Active Notary Commission - forward to General Counsel." },
        ],
      },
      {
        kind: "kv",
        heading: "Costs (NJRC reimburses up to $110 total)",
        items: [
          { label: "Commission fee", value: "~$5" },
          { label: "Registration fee", value: "$30" },
          { label: "Swearing-in fee", value: "$15" },
          { label: "Notary Essentials Kit (stamp/seal/journal)", value: "~$50 - Anchor Rubber Stamps is an accepted retailer" },
          { label: "Total estimate", value: "**~$100** - within reimbursement cap" },
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "Keep your receipts",
        text:
          "NJRC reimburses up to $110 of notary application + supplies + shipping + oath expenses. Save every receipt and submit for reimbursement. Notary supplies purchased by NJRC stay at NJRC premises (you keep your stamp + seal upon departure).",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/njrc-notary-public-policy.png",
        caption: "**NJRC Notary Public Policy** (LSM 2026 / LSC New Hire Packet) - the internal one-pager covering the 2-per-facility cap, reimbursement ceiling, and what happens to your stamp + journal when you leave.",
      },
    ],
  },
  {
    id: "notary-journal-policy",
    category: "operations",
    title: "How do I keep a proper notary journal at NJRC?",
    summary:
      "Every notary act gets logged: date, type, document, signer info, ID method. Required by NJRC policy and NJ law.",
    source: "LSM 2026 · NJRC Notary Public Guide and Policy",
    estimatedTime: "3 min read",
    difficulty: "easy",
    keywords: ["notary", "journal", "log", "policy", "compliance"],
    blocks: [
      {
        kind: "callout",
        tone: "info",
        title: "Every notary act must be logged",
        text:
          "Required by NJRC policy and NJ Notary law. Keep your journal in a secure location to avoid loss or tampering. Violations can result in discipline up to and including termination.",
      },
      {
        kind: "checklist",
        heading: "Per-entry log requirements",
        items: [
          "**Date & time** of the notary act",
          "**Type** of act (acknowledgment, jurat, oath, copy certification, etc.)",
          "**Title of document** being notarized",
          "**Printed name + signature + address** of each signer (and witness, if applicable)",
          "**Form of ID accepted** - ID Document, Personal Knowledge, or Credible Witness",
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Bound journal, sequential entries, no skipped pages",
        text:
          "Use a bound notary journal (not a loose-leaf binder) so entries can't be removed or rearranged. Number entries sequentially. If you skip a line, draw a single line through it.",
      },
      {
        kind: "callout",
        tone: "warn",
        title: "Common mistakes to avoid",
        text:
          "Don't notarize for family members. Don't notarize blank documents. Don't notarize a signature you didn't witness in person. Don't lend your stamp/seal to anyone - ever.",
      },
    ],
  },
  {
    id: "lsc-websites-cheat-sheet",
    category: "operations",
    title: "What's the master list of websites an LSC uses?",
    summary:
      "Every URL you'll need - MCCS, MVC searches, DOC, PROMIS/Gavel, JOC, SSA forms. Bookmark or print and tape to your monitor.",
    source: "LSM 2026 · LSC Websites Cheat Sheet 072225",
    estimatedTime: "3 min read",
    difficulty: "easy",
    keywords: ["websites", "urls", "cheat sheet", "links", "bookmarks", "tools"],
    blocks: [
      {
        kind: "table",
        heading: "Court & criminal-history searches",
        columns: ["Tool", "URL", "What you're looking for"],
        rows: [
          ["MCCS", "[portal.njcourts.gov/webe41/MPAWeb](https://portal.njcourts.gov/webe41/MPAWeb/index.jsp)", "Active cases, warrants, fines (DLS, CMVC, time payment, Active)"],
          ["DOC Inmate Finder", "[www-doc.state.nj.us/DOC_Inmate/inmatefinder](https://www-doc.state.nj.us/DOC_Inmate/inmatefinder?i=I)", "NJ state-prison incarceration history"],
          ["Bergen Co. Cor. Inmate Finder", "[iml.bcsd.us:8443/IML](https://iml.bcsd.us:8443/IML)", "Bergen County jail incarceration history"],
          ["Essex Co. Cor. Inmate Finder", "eccorrections.org/inmatelookup", "Essex jail history (was broken July 2025; verify)"],
          ["US BOP Inmate Lookup", "[bop.gov/mobile/find_inmate/byname.jsp](https://www.bop.gov/mobile/find_inmate/byname.jsp)", "Federal release date"],
          ["PROMIS/Gavel", "[portal.njcourts.gov/webe41/ExternalPGPA](https://portal.njcourts.gov/webe41/ExternalPGPA/)", "Most recent Superior Court conviction; jail days; Recovery Court"],
          ["JOC (post-2017)", "[portal.njcourts.gov/webe41/jocpa_internet](https://portal.njcourts.gov/webe41/jocpa_internet/jsp_public/home_public.jsp)", "Last Judgment of Conviction"],
        ],
      },
      {
        kind: "table",
        heading: "MVC searches & portals",
        columns: ["Tool", "URL", "Inputs needed"],
        rows: [
          ["MVC Surcharge Search", "[www1.state.nj.us/TYTR_NJAISC_Web](https://www1.state.nj.us/TYTR_NJAISC_Web/index.jsp)", "ID# + DOB"],
          ["MVC Restoration Fee Search", "[mymvc.state.nj.us/restoration-fees](https://mymvc.state.nj.us/restoration-fees/)", "ID# + SSN + ZIP on file"],
          ["MVC Master Portal", "[state.nj.us/mvc](https://www.state.nj.us/mvc/)", "Varies"],
          ["MVC Suspension Line", "(609) 325-2885 (phone)", "ID# / personal info"],
        ],
      },
      {
        kind: "table",
        heading: "ID & social services",
        columns: ["Tool", "URL", "Use"],
        rows: [
          ["Essex Co. ID Portal", "[essexclerk.com/services/18](https://www.essexclerk.com/services/18)", "Essex County ID appointments"],
          ["SSA Forms (incl. SS-5)", "[ssa.gov/forms](https://www.ssa.gov/forms/)", "Replacement SS card application"],
          ["NJ Courts Child Support", "[njcourts.gov/courts/child-support](https://www.njcourts.gov/courts/child-support)", "Child support case info; suspension issues"],
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Bookmark these in a 'NJRC LSC' browser folder",
        text:
          "Most LSCs hit 6–8 of these URLs every day. Worth 5 minutes of one-time setup to save 30 seconds × 50 sessions = 25 minutes per week.",
      },
    ],
  },

  // ─── 🎂 EASTER EGG - keep moving, nothing to see here ─────────────
  {
    id: "deathday-cake",
    category: "operations",
    title: "What is Deathday Cake?",
    summary:
      "Not a legal-services topic. The story behind the app named after Damon's worst birthday-cake decision - and what it actually does. Continue at your own glycemic risk.",
    source: "Internal NJRC lore · CGM data, sworn under oath",
    estimatedTime: "3 min read",
    difficulty: "easy",
    keywords: ["deathday cake", "damon", "cgm", "glucose", "blood sugar", "easter egg", "reaper"],
    blocks: [
      {
        kind: "callout",
        tone: "warn",
        title: "This is not actually about legal services",
        text:
          "If you got here from the index, congratulations - you found the easter egg. If you got here looking for an LSC workflow, sorry, but stick around: you're going to learn something.",
      },
      {
        kind: "paragraph",
        text:
          "**Deathday Cake** is a continuous-glucose-monitor (CGM) analytics app named after a single bad afternoon. Damon - yes, *that* Damon, your Senior Director of Legal Services - was at a birthday party. There was cake. He ate cake. His CGM, with no sense of either occasion or restraint, dutifully reported a postprandial glucose of **185 mg/dL**.",
      },
      {
        kind: "paragraph",
        text:
          "It was at that moment that the cake stopped being a *birthday* cake. It revealed itself as a **deathday** cake - a celebratory food that, on closer inspection, was simply trying to kill him. The name stuck. An app was built. The pancreas filed a grievance.",
      },
      {
        kind: "callout",
        tone: "info",
        title: "The app's tagline",
        text:
          "**\"Your blood is drowning in sugar.\"** This is meant as encouragement.",
      },
      {
        kind: "paragraph",
        text:
          "Underneath the gallows humor it's a real diagnostic tool - a memento mori dressed up as a phone app, on the bet that *a number you remember is more useful than a number you ignore*. Every chart, grade, and verdict is computed from your actual readings; no default copy survives if your data doesn't back it up.",
      },
      {
        kind: "comparison",
        heading: "What's actually in there",
        options: [
          {
            title: "Home - the daily score",
            tone: "info",
            items: [
              "**One number per day**, decomposed into Time in Range, Spikes vs. Curve, Average Glucose, and Variability",
              "Each component is individually scored and individually inspectable",
              "Tap any number → drilldown showing what was measured, what target it was scored against, how much it weighted into the composite",
              "**No opaque numbers.** Every figure on the page has a 'why this is what it is' panel",
            ],
          },
          {
            title: "Movement - wearable data",
            tone: "tip",
            items: [
              "Pulls overnight HR, sleep stages, step intervals, and exercise sessions from a Garmin watch (via Health Connect / HealthSync)",
              "Morning Report: **Readiness** (sleep duration weighted 40%), **Heart Health** letter grade combining resting HR + weighted Z2-Z5 cardio minutes, **Sleep** grade that calls out short nights honestly",
              "RHR calibrated to match what the watch displays - within 0.5 bpm on average after a recent recalibration against 7 nights of reference data",
            ],
          },
          {
            title: "Patterns - longer arcs",
            items: [
              "**365-day daily-range scatter** with the last 30 days highlighted in orange so you see how this month sits against the year",
              "Cross-day **spike heatmap**",
              "**Hour-of-day glucose band** overlay",
            ],
          },
          {
            title: "Meals + Death Reel",
            items: [
              "Log what you ate alongside the spike that followed",
              "**\"Death Reel\"** - fullscreen slideshow that scrolls your eating history as a slow cinematic loop set to your own music",
              "Useful for accountability; also for haunting",
            ],
          },
        ],
      },
      {
        kind: "comparison",
        heading: "For multi-patient households",
        options: [
          {
            title: "Heads Up",
            items: [
              "Compares everyone in the family across the same metrics",
              "One row per person; one glance to see who's drowning today",
            ],
          },
          {
            title: "Patient Battle",
            tone: "warn",
            items: [
              "Pits two members against each other across **8 rounds** - Glucose Control, Spike Defense, Stability, Recovery Speed, etc.",
              "**KO callouts** when a round is decisive",
              "**Six-axis radar** comparison",
              "**Gold confetti** for the winner",
              "Brutally honest verdict from a character called **The Reaper**",
            ],
          },
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "The Reaper",
        text:
          "Throughout the app, a character called **The Reaper** delivers data-driven verdicts in the voice of a patient ferryman rather than a peppy wellness coach. Drilldowns, daily summaries, battle commentary - Reaper everywhere. The premise: the most reliable way to make people care about their glucose curve is to make checking it the highlight of the morning.",
      },
      {
        kind: "paragraph",
        text:
          "What separates Deathday Cake from typical CGM dashboards is exactly that: a serious tool willing to be unserious about itself. NJRC has nothing to do with it. But you now know why Damon occasionally mutters \"the Reaper sees you\" at staff meetings, and that has to count for something.",
      },
      {
        kind: "callout",
        tone: "danger",
        title: "Now get back to work",
        text:
          "Your participants are not going to file their own time-served letters. The cake has been thoroughly accounted for. Onwards.",
      },
    ],
  },

  // ─── COURT LETTERS · supplementary ────────────────────────────────
  {
    id: "letter-backup-materials",
    category: "court-letters",
    title: "What attachments go behind each kind of court letter?",
    summary:
      "The order matters. Time-served and MVC-correction letters need the same backup chain; warrants are usually backup-free.",
    source: "LSM 2026 · Module 5",
    estimatedTime: "3 min read",
    difficulty: "easy",
    keywords: ["court letter", "backup", "attachments", "time served", "warrant", "mvc correction"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**Order matters.** The corpus specifies a strict order for assembling backup materials. Don't rearrange - judges and Court Administrators expect to see DOC → PROMIS/Gavel → JOC → drug program in that sequence.",
          "**Time-Served + MVC-Correction letters share the same backup chain:** DOC Offender Info → PROMIS/Gavel sentence pages → JOC → drug-program completion → participant fallback (jail face sheet / probation discharge / PO letter / SDLS consult).",
          "**Warrant Recall letters: no backup in most cases.** Exception 1: combined fines + warrant relief. Exception 2: time served available to dismiss the warrant matter. In those cases, attach the full Time-Served chain.",
          "**Attached case-detail pages must be in the SAME ORDER as the case numbers in the Re: line.** This is from the [[court-letter-checklist]].",
          "**SDLS keeps the templates.** Don't write letters from scratch. If no existing form fits, **collaborate with SDLS to create one**.",
        ],
      },
      {
        kind: "comparison",
        heading: "Backup materials by letter type",
        options: [
          {
            title: "Time-Served Letter",
            tone: "tip",
            items: [
              "**1.** NJ DOC Offender Info (if available)",
              "**2.** PROMIS/Gavel Sentence Details (if available)",
              "**3.** Judgment of Conviction (if available)",
              "**4.** Drug program completion letter / certificate (if applicable)",
              "**Fallback** if none above: jail face sheet, probation discharge paperwork, or PO letter - consult SDLS",
            ],
          },
          {
            title: "Warrant Recall",
            tone: "warn",
            items: [
              "**No backup** in most cases",
              "Exception 1: letter seeks both fines AND warrant relief → attach time-served backup chain",
              "Exception 2: time served available to support dismissal of the warrant matter → attach DOC + sentence info",
            ],
          },
          {
            title: "MVC Suspension Correction",
            tone: "info",
            items: [
              "**1.** NJ DOC Offender Info (if available)",
              "**2.** PROMIS/Gavel Sentence Details (if available)",
              "**3.** Judgment of Conviction (if available)",
              "**4.** Drug program completion letter / certificate (if applicable)",
              "Same chain as time-served. Order matters for the judge's reading.",
            ],
          },
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "Order matters: case-detail pages must match the RE: line",
        text:
          "If the RE: line lists three case numbers in a specific order, the attached MCCS Additional Details pages must be **in the same order**. Otherwise the judge's clerk has to puzzle out which attachment goes with which case.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Don't write letters from scratch",
        text:
          "SDLS maintains a comprehensive set of form letters that cover almost every scenario. If you can't find a template that fits, **collaborate with SDLS to create one** - don't invent your own.",
      },
    ],
  },

  // ─── LICENSE RESTORATION · supplementary ──────────────────────────
  {
    id: "dl-suspended-vs-closed-to-mvc",
    category: "license-restoration",
    title: "What's the difference between 'DL Suspended' and 'Closed to MVC'?",
    summary:
      "Both are MCCS Case Status codes. Both block license restoration. Both are typically tied to unpaid time-payment plans or tickets - and both must be cleared before MVC will issue a license.",
    source: "LSM 2026 · Module 6",
    estimatedTime: "3 min read",
    difficulty: "medium",
    keywords: ["dl suspended", "closed to mvc", "c2mvc", "mccs status", "suspension"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "Both **DL Suspended** and **Closed to MVC** are Case Status codes that appear in MCCS results.",
          "They're often connected to **time payments and/or tickets**.",
          "They **block the participant from obtaining or restoring their Driver License**, online or in person.",
          "Both must be **resolved before MVC will issue the license**.",
          "**Run an Outstanding MCCS search** to check for these - they appear in the Case Status column.",
          "**Click Additional Details** on each to review the case and confirm next steps.",
          "**If pursuing Time Served Relief**, the LSC should determine whether time served is available AND **request that the Municipal Court lift the suspension as part of the relief request**.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "A common barrier to obtaining or restoring a Driver License is an **outstanding Municipal Court matter that shows a status of \"DL Suspended\" or \"Closed to MVC\" in MCCS**. To check for these issues, the LSC can run an **Outstanding MCCS Search**. If the participant has any suspensions, they will appear in the **Case Status column** of the MCCS results. Select **Additional Details** on each to review the case and confirm next steps.",
      },
      {
        kind: "paragraph",
        text:
          "**DL Suspended and Closed to MVC cases are often connected to time payments and/or tickets.** These cases can block the participant from obtaining or restoring their Driver License online or in person, and **must be resolved before MVC will issue the license**. If the LSC is pursuing Time Served Relief, the LSC should determine whether it is available and **request that the Municipal Court lift the suspension as part of the relief request** - don't make it two separate asks; combine them into one [[write-a-time-served-letter]].",
      },
      {
        kind: "comparison",
        heading: "DL Suspended vs. Closed to MVC",
        options: [
          {
            title: "DL Suspended",
            subtitle: "Active suspension",
            tone: "warn",
            items: [
              "Court has actively reported a suspension to MVC",
              "Often tied to **unpaid time-payment** plan",
              "MVC will refuse to renew or restore until the suspension is resolved",
              "Resolve via time-served relief OR full payment",
            ],
          },
          {
            title: "Closed to MVC (C2MVC)",
            subtitle: "Court closed the matter and sent it to MVC",
            tone: "info",
            items: [
              "The municipal court has effectively handed the case to MVC for collection",
              "Often connected to old fines that were converted to administrative debt",
              "May still be resolvable via time-served relief - but ALSO requires MVC follow-up",
              "Verify status via MCCS Additional Details, then call MVC Suspension Line if needed",
            ],
          },
        ],
      },
      {
        kind: "checklist",
        heading: "What to do when you see either status",
        items: [
          "**Open Additional Details** in MCCS - confirm there's a fine balance and the suspension date",
          "**Check Time Served eligibility** - was the participant in custody after the offense date?",
          "**If yes** to time served → prepare a Time-Served Letter requesting fine conversion AND lift of the suspension as part of the relief",
          "**If no** time served → talk to participant about payment plan or hardship; coordinate with SDLS",
          "**For C2MVC specifically** → after court resolution, follow up with MVC Suspension Line to confirm MVC has updated their records",
        ],
      },
    ],
  },
  {
    id: "mvc-portal-duplicate-trap",
    category: "license-restoration",
    title: "Why does the MVC online portal sometimes order the wrong credential?",
    summary:
      "MVC's duplicate/renewal portals do not distinguish between State ID and Driver License - they always issue whichever credential was issued most recently. If the wrong one is most recent, the participant must go in person.",
    source: "LSM 2026 · Module 6",
    estimatedTime: "2 min read",
    difficulty: "medium",
    keywords: ["mvc", "portal", "duplicate", "state id", "driver license", "renewal", "trap"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**The MVC online portals for duplicates and renewals do NOT distinguish between State IDs and Driver Licenses.**",
          "If a participant has both, the portal will order **whichever credential was issued most recently**.",
          "If the participant needs to renew or replace a Driver License but recently ordered a State ID, **the participant must go in person to an MVC Center** to obtain the Driver License.",
          "Same applies in reverse - if the DL is more recent and they need a State ID, in-person is required.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "**The MVC online portals for duplicates and renewals do not distinguish between State IDs and Driver Licenses.** If a participant has both, the portal will order **whichever credential was issued most recently**. If the participant needs to renew or replace a Driver License but recently ordered a State ID, the participant must **go in person to an MVC Center** to obtain the Driver License.",
      },
      {
        kind: "callout",
        tone: "danger",
        title: "The MVC online portal does NOT let you pick which credential to issue",
        text:
          "Whether you use the duplicate or renewal portal, MVC will always issue the credential that was **most recently issued** - regardless of whether the participant wants the DL or the State ID. There is no override switch.",
      },
      {
        kind: "paragraph",
        text:
          "Practical impact: if a participant has both a State ID and a DL and the State ID was the more recent issuance, requesting a DL renewal online will get them another State ID. To get the DL, they must visit an MVC Center in person.",
      },
      {
        kind: "flowchart",
        heading: "How to avoid the trap",
        nodes: [
          { tone: "start", title: "Confirm what credentials the participant currently holds (or recently held)", body: "DL only · State ID only · Both" },
          { title: "If both: identify which was issued more recently", body: "If you don't know, the portal will tell you indirectly - try the renewal portal and see what comes up." },
          { title: "If the desired credential is the most recent: use the online portal", body: "Renewal or duplicate works as expected." },
          { tone: "warn", title: "If the desired credential is NOT the most recent: book an in-person MVC appointment", body: "Don't use the online portal - it will issue the wrong one." },
          { tone: "end", title: "After receiving the correct credential, scan + upload to Salesforce", body: "Note in Case Note: 'in-person required because of credential issuance order'." },
        ],
      },
    ],
  },
  {
    id: "license-restoration-requirements-report",
    category: "license-restoration",
    title: "What is a License Restoration Requirements Report (LRRR) and how do I get one?",
    summary:
      "MVC's authoritative list of everything standing between a participant and license restoration - including out-of-state holds, IDRC, IID, child support, and other barriers the public searches can't surface. Request via SDLS, who forwards to NJRC's MVC contact.",
    source: "LSM 2026 · Module 6",
    estimatedTime: "3 min read",
    difficulty: "medium",
    keywords: ["lrrr", "requirements report", "mvc", "restoration", "barriers"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "An **LRRR is the authoritative, MVC-internal list** of everything blocking restoration - more comprehensive than any combination of public searches.",
          "**Includes barriers public tools can't surface:** out-of-state suspensions, child-support warrants, IDRC requirements, IID fees, default judgments, multiple ID numbers, etc.",
          "**Request via SDLS.** SDLS forwards to NJRC's contact at MVC.",
          "**Submit when:** the participant is unable to complete the [[mvc-suspension-line-call]], OR didn't record the info legibly, OR the public searches don't add up to the participant's lived experience.",
          "**Required inputs:** participant's name, NJ ID#, DOB, and ZIP code on file with MVC.",
          "**Use the LRRR** to drive next-step letters - an MVC-correction letter can cite the report directly when asking a court to communicate with MVC.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "An **LRRR is the comprehensive, MVC-internal list** of **everything** standing between a participant and license restoration. It's more authoritative than the combination of restoration-fee + surcharge + MCCS searches because it includes things those public tools can't surface - out-of-state suspensions, child-support warrants, IDRC requirements, IID fees, default judgments, multiple ID numbers, etc.",
      },
      {
        kind: "paragraph",
        text:
          "The LRRR is the LSC's **escalation tool** when public databases don't tell the full story. **If the participant is unable to complete the [[mvc-suspension-line-call]], or does not record the information in a legible and usable way, the LSC may submit a License Restoration Requirements Report request to SDLS. SDLS will then forward the LRRR to NJRC's contact at MVC.**",
      },
      {
        kind: "checklist",
        heading: "When to request an LRRR",
        items: [
          "Restoration-fee + surcharge + MCCS results don't add up to the participant's lived experience",
          "Participant says MVC told them about a barrier you can't find in any public search",
          "Participant called the MVC Suspension Line but couldn't write down the info legibly",
          "Suspicion of out-of-state issues, IDRC, IID, or child-support holds",
        ],
      },
      {
        kind: "steps",
        heading: "How to request",
        items: [
          { title: "Confirm participant's NJ ID# + DOB + ZIP on file with MVC", body: "Without these MVC can't run the report." },
          { title: "Email SDLS the request", body: "Include the participant's name, NJ ID#, DOB, and ZIP. SDLS forwards to NJRC's MVC contact." },
          { title: "Wait for SDLS to receive + share the report", body: "Turnaround varies - usually a few business days." },
          { title: "Use the report to drive next-step letters", body: "An MVC-correction letter can cite the report directly when asking the court to communicate with MVC." },
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Pair LRRR with the MVC Suspension Line",
        text:
          "If a participant can call the Suspension Line and write down the info legibly, that's faster than waiting for an LRRR. The LRRR is the formal/authoritative version - request it when a participant can't make the call themselves or when you need documentation for a letter.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/lrrr-request-form.png",
        caption: "**Blank LRRR Request Form** (LSM 2026 / Module 6) - the LSC sheet that goes to SDLS, who forwards it to NJRC's MVC contact. Shows the exact fields MVC needs to run the report.",
      },
      {
        kind: "links",
        heading: "Resources & contacts",
        items: [
          { label: "NJ MVC main portal", href: "https://www.state.nj.us/mvc/", description: "Public MVC services landing page." },
          { label: "MVC general inquiry email", href: "mailto:mvcinquiry@gilacorp.com", description: "MVC's outsourced general-inquiry inbox (Gila Corp)." },
          { label: "MVC Suspension Office email", href: "mailto:suspension.info@mvc.nj.gov", description: "Direct contact for suspension-related questions." },
          { label: "MVC Suspension Office (phone)", href: "tel:+16093252885", description: "(609) 325-2885 - same number as the Suspension Line walkthrough." },
          { label: "NJ Courts MVC liaison (Alexandro Marin)", href: "mailto:alexandro.marin@njcourts.gov", description: "For municipal-court → MVC reconciliation issues." },
        ],
      },
    ],
  },

  // ─── BIRTH CERTIFICATES · supplementary ───────────────────────────
  {
    id: "bc-required-participant-info",
    category: "birth-certificates",
    title: "What information do I need from the participant before starting a BC application?",
    summary:
      "Six core fields, regardless of jurisdiction. Get them all in the first conversation - going back for a missing field can lose a week.",
    source: "LSM 2026 · Module 8",
    estimatedTime: "2 min read",
    difficulty: "easy",
    keywords: ["birth certificate", "intake", "required info", "participant data"],
    blocks: [
      {
        kind: "checklist",
        heading: "Always collect",
        items: [
          "**Full legal name** at birth (including any subsequent name changes or adoption names)",
          "**Date of birth** (DOB)",
          "**Gender** (as listed on the original certificate, if known)",
          "**Place of birth** - town/city, county, state, country",
          "**Hospital of birth** (if known)",
          "**Father's** first, middle, and last name",
          "**Mother's** first, middle, and **maiden** name",
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "If father's name is unknown or not on the original certificate",
        text:
          "Just enter the mother's information. Many participants' birth certificates only list the mother - that's normal and not a problem for the application.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "All applications must be **typed**",
        text:
          "Handwritten applications get rejected by most vital-records offices. Use the PDF fillable form. Sign in pen ink only at the signature line.",
      },
    ],
  },
  {
    id: "order-hawaii-birth-certificate",
    category: "birth-certificates",
    title: "How do I order a Hawaii birth certificate?",
    summary:
      "Online via vitrec.ehawaii.gov OR mail-in to Honolulu. $10 first copy + $4 each additional + $2.50 admin fee. Hawaii does not accept VitalChek.",
    source: "LSM 2026 · Module 8 · HI/VT/WY Guide",
    estimatedTime: "5 min read",
    difficulty: "medium",
    keywords: ["hawaii", "birth certificate", "vital records", "honolulu"],
    blocks: [
      {
        kind: "callout",
        tone: "info",
        title: "Hawaii does NOT use VitalChek",
        text:
          "Direct application required - online or mail-in. Both go through the State Department of Health.",
      },
      {
        kind: "comparison",
        heading: "Online vs. mail-in",
        options: [
          {
            title: "Online (recommended)",
            tone: "tip",
            items: [
              "[vitrec.ehawaii.gov/vitalrecords](https://vitrec.ehawaii.gov/vitalrecords/) → click START",
              "Government-issued ID required (upload)",
              "Pay via debit / credit card",
              "Faster than mail-in",
            ],
          },
          {
            title: "Mail-in",
            items: [
              "Download fillable Request for Certified Copy of Birth Record from health.hawaii.gov",
              "Mail completed form + fees + ID copy to address below",
              "Slower turnaround",
            ],
          },
        ],
      },
      {
        kind: "kv",
        heading: "Fees",
        items: [
          { label: "First copy", value: "**$10**", mono: true },
          { label: "Each additional copy", value: "**$4**", mono: true },
          { label: "Admin fee (up to 5 copies)", value: "**$2.50**", mono: true },
        ],
      },
      {
        kind: "kv",
        heading: "Mail-in address",
        items: [
          { label: "Agency", value: "State Department of Health, Office of Health Status Monitoring, Issuance/Vital Statistics Section" },
          { label: "Address", value: "P.O. Box 3378, Honolulu, HI 96801", mono: true },
        ],
      },
      {
        kind: "links",
        heading: "Forms & resources",
        items: [
          { label: "Hawaii Vital Records - main page", href: "https://health.hawaii.gov/vitalrecords/birth-marriage-certificates/" },
          { label: "Online application portal", href: "https://vitrec.ehawaii.gov/vitalrecords/" },
          { label: "Hawaii Mail-In Application (PDF, bundled)", href: "/how-to-assets/bc-forms/09 Hawaii Vital Records Mail-In Application.pdf" },
          { label: "Hawaii Online Application reference (PDF, bundled)", href: "/how-to-assets/bc-forms/07 Hawaii Vital Records Online Application.pdf" },
        ],
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/hawaii-bc-application-1.png",
        caption: "Hawaii Vital Records Mail-In Application (page 1).",
      },
    ],
  },
  {
    id: "order-vermont-birth-certificate",
    category: "birth-certificates",
    title: "How do I order a Vermont birth certificate?",
    summary:
      "Online only via secure.vermont.gov/VSARA. $12 standard, $14.50 expedited. Vermont does not accept VitalChek.",
    source: "LSM 2026 · Module 8 · HI/VT/WY Guide",
    estimatedTime: "3 min read",
    difficulty: "easy",
    keywords: ["vermont", "birth certificate", "vital records", "vsara"],
    blocks: [
      {
        kind: "callout",
        tone: "info",
        title: "Vermont is online-only and doesn't accept VitalChek",
        text:
          "Skip the paper application; use the state's own portal.",
      },
      {
        kind: "steps",
        heading: "Process",
        items: [
          { title: "Go to [secure.vermont.gov/VSARA/vitalrecords](https://secure.vermont.gov/VSARA/vitalrecords/)", body: "Click START." },
          { title: "Have a government-issued ID ready", body: "Required. Photo of front + back may be requested during checkout." },
          { title: "Pay $12 (standard) or $14.50 (expedited)", body: "Credit/debit card." },
          { title: "Wait for delivery", body: "Standard turnaround varies; expedited shaves several days off." },
        ],
      },
      {
        kind: "links",
        heading: "Forms & resources",
        items: [
          { label: "Vermont Vital Records Online Application portal", href: "https://secure.vermont.gov/VSARA/vitalrecords/" },
          { label: "Vermont Online Application reference (PDF, bundled)", href: "/how-to-assets/bc-forms/10 Vermont Vital Records Online Application.pdf" },
        ],
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/vermont-bc-application-1.png",
        caption: "Vermont Vital Records Online Application reference (page 1) - what the participant will see in the portal.",
      },
    ],
  },
  {
    id: "order-wyoming-birth-certificate",
    category: "birth-certificates",
    title: "How do I order a Wyoming birth certificate?",
    summary:
      "Mail-in only. $25 fee. ID required (or notarized signature). Money order or bank check payable to Vital Statistics Services.",
    source: "LSM 2026 · Module 8 · HI/VT/WY Guide",
    estimatedTime: "4 min read",
    difficulty: "medium",
    keywords: ["wyoming", "birth certificate", "vital records", "cheyenne"],
    blocks: [
      {
        kind: "callout",
        tone: "info",
        title: "Wyoming is mail-in only",
        text: "No online ordering, no VitalChek. Paper application + money order to Cheyenne.",
      },
      {
        kind: "checklist",
        heading: "What to include in the envelope",
        items: [
          "**Completed application** (download from health.wyo.gov)",
          "**Photocopy of ID** (driver license, state ID card, or passport) - current",
          "**Money order or bank-personalized check** for $25, payable to Vital Statistics Services",
          "If 4+ copies → additional form required (request from agency or the website)",
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "ID alternative: notarized signature",
        text:
          "If the participant has no ID, the agency accepts a **notarized signature** as proof of identity instead. If even that's not workable, a parent with ID can request the certificate on the participant's behalf.",
      },
      {
        kind: "kv",
        heading: "Mail-in address",
        items: [
          { label: "Agency", value: "Vital Statistics Services" },
          { label: "Address", value: "2300 Capitol Avenue, Hathaway Building, Cheyenne, WY 82002", mono: true },
          { label: "Phone", value: "(307) 777-7591", mono: true },
        ],
      },
      {
        kind: "links",
        heading: "Forms & resources",
        items: [
          { label: "Wyoming Vital Statistics - main page", href: "https://health.wyo.gov/admin/vitalstatistics/vital-record/officialbirthcertificate/" },
          { label: "Wyoming Vital Records Application (PDF, bundled)", href: "/how-to-assets/bc-forms/11 Wyoming Vital Records Application.pdf" },
          { label: "Wyoming Excessive Copies Form (PDF, bundled)", href: "/how-to-assets/bc-forms/12 Wyoming Vital Records Excessive Copies Form.pdf" },
        ],
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/wyoming-bc-application-1.png",
        caption: "Wyoming Vital Records Application (page 1) - mail with money order to Cheyenne.",
      },
    ],
  },
  {
    id: "consular-report-birth-abroad",
    category: "birth-certificates",
    title: "When does a participant need a Consular Report of Birth Abroad (CRBA)?",
    summary:
      "Participant born outside the US to at least one US-citizen parent - and the participant has a valid government ID. The CRBA is the US's official 'foreign birth certificate' substitute.",
    source: "LSM 2026 · Module 8 · CRBA / VitalChek CRBA",
    estimatedTime: "4 min read",
    difficulty: "hard",
    keywords: ["crba", "consular report", "birth abroad", "foreign", "us citizen parent"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "The CRBA (Consular Report of Birth Abroad) is the US State Department's record proving a US citizen was born outside the US. For NJRC purposes, it functions as a US-recognized birth certificate - unlocking SS card, state ID, passport, and license restoration.",
      },
      {
        kind: "decisionTree",
        heading: "Is CRBA the right path?",
        root: {
          kind: "decision",
          question: "Was the participant born outside the United States?",
          yes: {
            kind: "decision",
            question: "Does the participant have a VALID, NON-EXPIRED government-issued ID?",
            yes: {
              kind: "decision",
              question: "Was at least one of the participant's parents a US citizen at the time of birth?",
              yes: { kind: "result", tone: "tip", label: "CRBA path: VitalChek paper application - coordinate with SDLS" },
              no: { kind: "result", tone: "info", label: "Not CRBA - pursue foreign BC via consulate or international BC topic" },
            },
            no: { kind: "result", tone: "warn", label: "ID barrier first - work the State ID / 6-points process; then revisit CRBA" },
          },
          no: { kind: "result", tone: "info", label: "Use US state-specific BC process (NJ, NYC, etc.) - not CRBA" },
        },
      },
      {
        kind: "steps",
        heading: "If CRBA applies - process",
        items: [
          { title: "Confirm both prerequisites with documentation", body: "Participant's valid ID + evidence of US-citizen parent (parent's birth certificate, naturalization cert, or US passport)." },
          { title: "Pull the VitalChek paper CRBA application", body: "SDLS has the master copy - request via email." },
          { title: "Complete and notarize as instructed", body: "Like other VitalChek processes, the participant signs at notary; the LSC scans + emails to SDLS." },
          { title: "SDLS reviews + submits", body: "VitalChek processes the request; turnaround weeks to months." },
          { title: "When CRBA arrives at NJRC HQ, scan + upload to Salesforce; notify participant", body: "Hand-deliver - same protocol as any vital record." },
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "If prerequisites are NOT met",
        text:
          "Skip CRBA entirely. Either pursue the foreign birth certificate directly via the consulate/embassy of the country of birth, or use the [International Birth Certificates picker](/how-to/international-birth-certificates) for country-specific guidance.",
      },
    ],
  },
  {
    id: "bc-pickup-and-handling",
    category: "birth-certificates",
    title: "How do I handle a birth certificate once it arrives at NJRC?",
    summary:
      "Hand-delivery only. No mail, no shipping. Scan to Salesforce immediately. NJRC policy is firm.",
    source: "LSM 2026 · Module 8",
    estimatedTime: "2 min read",
    difficulty: "easy",
    keywords: ["birth certificate", "pickup", "delivery", "policy", "salesforce"],
    blocks: [
      {
        kind: "callout",
        tone: "danger",
        title: "Birth certificates must be hand-delivered to the participant - never mailed",
        text:
          "NJRC policy: do NOT mail or ship a participant's BC unless **specifically approved by the CLO**. Hand-delivery at the NJRC site is the standard. This protects against mail loss + identity theft.",
      },
      {
        kind: "steps",
        heading: "When the BC arrives",
        items: [
          { title: "**Immediately** scan the BC", body: "Both sides if applicable. Save as a single PDF." },
          { title: "Upload the scan to the participant's Salesforce profile", body: "Under the appropriate Documents section. Tag clearly." },
          { title: "Add a Salesforce Case Note", body: "\"Birth certificate received [date]. Awaiting participant pickup.\"" },
          { title: "Contact the participant to schedule pickup", body: "Phone + text. Don't leave the BC sitting around." },
          { title: "When participant comes in, hand it over in person", body: "Add a follow-up Case Note: \"BC delivered to participant in person on [date].\"" },
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "BCs are once per calendar year",
        text:
          "NJRC policy: one BC per calendar year per participant, one copy at a time. If a participant loses the BC after NJRC has already paid, they're responsible for the replacement (or wait for next calendar year).",
      },
    ],
  },

  // ─── SOCIAL SECURITY · supplementary ──────────────────────────────
  {
    id: "ss-card-id-documents",
    category: "social-security",
    title: "What documents satisfy SSA's ID requirement for the SS-5?",
    summary:
      "SSA wants ONE valid, original, unexpired primary document. The fallback is a doctor's note with strict formatting requirements.",
    source: "LSM 2026 · Module 7",
    estimatedTime: "3 min read",
    difficulty: "easy",
    keywords: ["ss-5", "social security", "id", "documents", "doctor note", "primary"],
    blocks: [
      {
        kind: "checklist",
        heading: "Acceptable primary ID documents (any ONE)",
        items: [
          "**State-issued ID** (driver license or non-driver ID)",
          "**U.S. Passport**",
          "**Certificate of Naturalization**",
          "**Health Insurance Card**",
          "**Military ID Card**",
          "**Medical record** signed in pen ink by a doctor",
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "All documents must be **original AND unexpired**",
        text:
          "Photocopies, scans, prints, or expired documents are rejected at the SSA window. The participant must bring physical originals.",
      },
      {
        kind: "callout",
        tone: "info",
        title: "If none of the above are available - doctor's note fallback",
        text:
          "A doctor's note works if it includes ALL of: (1) participant's name, (2) DOB, (3) date AND time of visit, (4) **pen-ink** doctor's signature dated within the past 30 days. \"Patient is under our care\" is the standard phrasing.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "When in doubt, send the doctor's note version",
        text:
          "Many participants have lost or never had a state ID. Don't burn cycles trying to scrounge one - get the doctor's note (most NJRC sites have working relationships with local clinics) and submit the SS-5 with that.",
      },
    ],
  },
  {
    id: "ss-card-no-mailing-address",
    category: "social-security",
    title: "What if the participant has no usable mailing address?",
    summary:
      "NJRC's office address goes in Boxes 15 and 16 of the SS-5. Standard practice for housing-insecure participants.",
    source: "LSM 2026 · Module 7",
    estimatedTime: "2 min read",
    difficulty: "easy",
    keywords: ["ss-5", "mailing address", "homeless", "no address", "njrc address"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "Many participants are between addresses, in transitional housing, or unhoused. SSA still needs to mail the card somewhere. **Use NJRC's office address** - it's the standard workaround.",
      },
      {
        kind: "kv",
        heading: "What to enter in the SS-5",
        items: [
          { label: "Box 15 (phone)", value: "LSC's phone number." },
          { label: "Box 16 (mailing address)", value: "NJRC reentry-site address." },
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Track the mail",
        text:
          "When the card arrives at the NJRC office, the LSC writes a Salesforce Case Note immediately and contacts the participant to come pick it up. Don't let cards sit unsecured - they're a fraud target.",
      },
    ],
  },

  // ─── STATE ID · supplementary ─────────────────────────────────────
  {
    id: "renew-state-id",
    category: "state-id",
    title: "How do I renew an NJ State ID?",
    summary:
      "Eligible online up to 90 days BEFORE expiration through up to 1 year AFTER. Beyond 1 year expired → in-person at MVC. Portal walks the LSC to payment as the eligibility signal.",
    source: "LSM 2026 · Module 9",
    estimatedTime: "4 min read",
    difficulty: "medium",
    keywords: ["state id", "renew", "renewal", "non-driver id", "mvc"],
    blocks: [
      {
        kind: "keyPoints",
        items: [
          "**Online renewal window:** **up to 90 days BEFORE** the expiration date through **up to one year AFTER**.",
          "**Expired more than 1 year** → must renew **in person at an MVC site**.",
          "**Renewing online is more convenient** for the participant and the LSC - no trip to MVC required.",
          "**Eligibility signal:** if the LSC reaches the **credit-card payment page**, the renewal is available. **Close the portal without submitting payment.**",
          "**Don't pay through the LSC's session.** Email an **ID Renewal Request to SDLS** instead. **SDLS processes MVC requests, pays, emails the LSC the receipt** once paid, and uploads to Salesforce.",
          "**A duplicate State ID is ordered the same way** - see [[duplicate-state-id]].",
          "**Beware the [[mvc-portal-duplicate-trap]]** - the portal will issue whichever credential was most recently ordered if the participant has both a State ID and DL.",
        ],
      },
      {
        kind: "paragraph",
        text:
          "**When a participant's ID is approaching its expiration date - or has already expired - the LSC may check the MVC online portal to see whether the participant is eligible for an online renewal.** Renewing online is **often more convenient** for the participant and the LSC and **does not require a trip to MVC**. **IDs are eligible for online renewal up to 90 days before the expiration date and up to one year after.** **If an ID has been expired for more than one year, it must be renewed in person at an MVC site.**",
      },
      {
        kind: "paragraph",
        text:
          "**When checking whether a participant is eligible for an online renewal, the LSC will enter the required information in the appropriate MVC portal and proceed until reaching the payment page.** **If the credit-card information page loads successfully, the LSC will note that online renewal is available, close the portal without submitting payment, and email an ID Renewal Request to the SDLS.** **The SDLS will process MVC requests, email the LSC the ID Renewal Receipt once paid, and upload it to and update Salesforce.** The LSC will then **print or forward the receipt to the participant for confirmation**.",
      },
      {
        kind: "decisionTree",
        heading: "Online or in-person?",
        root: {
          kind: "decision",
          question: "Has the State ID been expired for more than 1 year?",
          yes: { kind: "result", tone: "warn", label: "In-person at MVC required (online portal blocks renewal)" },
          no: {
            kind: "decision",
            question: "Is the address on file with MVC current?",
            yes: { kind: "result", tone: "tip", label: "Online renewal eligible - see steps below" },
            no: { kind: "result", tone: "info", label: "Do Change of Address first; then online renewal works" },
          },
        },
      },
      {
        kind: "steps",
        heading: "Online renewal steps",
        items: [
          { title: "Open the MVC State ID renewal portal", body: "Use participant's NJ ID#, ZIP on file, SSN or DOB, and first 6 letters of last name." },
          { title: "Walk through to the payment page WITHOUT submitting payment", body: "If the credit-card page loads, the renewal is eligible. Close the portal - don't pay." },
          { title: "Email an ID Renewal Request to SDLS", body: "Include participant's name, NJ ID#, and confirmation that the renewal portal got to payment." },
          { title: "SDLS pays + sends you the renewal receipt", body: "Upload to Salesforce, forward to participant for confirmation." },
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "The portal trap (revisited)",
        text:
          "If the participant has both a State ID and a DL, the renewal portal will issue whichever was MOST RECENTLY issued - regardless of which one you wanted to renew. See [[mvc-portal-duplicate-trap]].",
      },
      {
        kind: "callout",
        tone: "info",
        title: "Renewal window: 90 days before to 1 year after expiration",
        text: "Outside this window? See the decision tree above - in-person is required.",
      },
    ],
  },
  {
    id: "duplicate-state-id",
    category: "state-id",
    title: "How do I order a duplicate NJ State ID?",
    summary:
      "Lost or damaged ID - duplicate via online portal + SDLS payment. Mind the address-on-file gotcha.",
    source: "LSM 2026 · Module 9",
    estimatedTime: "3 min read",
    difficulty: "easy",
    keywords: ["duplicate", "state id", "lost", "damaged", "replacement"],
    blocks: [
      {
        kind: "checklist",
        heading: "Before ordering - confirm the basics",
        items: [
          "**Address on file with MVC** is correct AND participant can receive mail there",
          "If not - do a **Change of Address first** (free, instant); see [[change-of-address-mvc]]",
          "Participant has not already received a duplicate this calendar year (NJRC pays once per year)",
        ],
      },
      {
        kind: "steps",
        heading: "Steps",
        items: [
          { title: "Run participant's info through the MVC duplicate portal", body: "Same auth fields as renewal: NJ ID#, ZIP, SSN/DOB, first 6 of last name." },
          { title: "Walk to payment page; do not pay", body: "If you reach the credit-card page, eligibility is confirmed." },
          { title: "Email Duplicate ID Request to SDLS", body: "Name, NJ ID#, confirmation of eligibility." },
          { title: "SDLS pays + sends receipt; upload to Salesforce", body: "Forward receipt to participant." },
          { title: "Wait ~20 days for ID to arrive by mail", body: "If it doesn't arrive in 30 days, call MVC - receipt has the phone number." },
        ],
      },
    ],
  },
  {
    id: "real-id-policy",
    category: "state-id",
    title: "When does NJRC pay for a REAL ID?",
    summary:
      "NJRC pays to renew or replace an existing REAL ID. NJRC does NOT pay for a participant's first REAL ID conversion.",
    source: "LSM 2026 · Module 9",
    estimatedTime: "2 min read",
    difficulty: "easy",
    keywords: ["real id", "policy", "renewal", "duplicate", "first time"],
    blocks: [
      {
        kind: "comparison",
        heading: "REAL ID payment policy",
        options: [
          {
            title: "NJRC pays",
            tone: "tip",
            items: [
              "Participant **already has** a REAL ID and needs to renew it",
              "Participant **already has** a REAL ID and needs a duplicate (lost/damaged)",
              "Subject to the standard 1-per-calendar-year policy",
            ],
          },
          {
            title: "NJRC does NOT pay",
            tone: "warn",
            items: [
              "Participant has a regular ID and wants to **upgrade to REAL ID**",
              "Participant has never had any state ID and wants their first one to be a REAL ID",
              "Participant must pay out-of-pocket for the upgrade themselves",
            ],
          },
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "Why",
        text:
          "REAL ID requires significantly more documentation (federally compliant 6-point list with stricter primary docs). NJRC's funding model assumes initial IDs are standard NJ IDs; REAL ID is treated as an optional upgrade the participant chooses.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/real-id-points-checklist.png",
        caption: "**MVC REAL ID Points Checklist** (LSM 2026 / Module 9). The exact federal-compliant document list a participant must hit to upgrade - strictly more demanding than a standard NJ ID.",
      },
    ],
  },
  {
    id: "proof-of-address-mvc",
    category: "state-id",
    title: "What counts as Proof of Address for an MVC ID?",
    summary:
      "Bank statement, utility bill, government mail. NJRC office address letter as the housing-insecure fallback.",
    source: "LSM 2026 · Module 9",
    estimatedTime: "2 min read",
    difficulty: "easy",
    keywords: ["proof of address", "mvc", "id", "documents", "housing"],
    blocks: [
      {
        kind: "checklist",
        heading: "Acceptable Proof of Address documents",
        items: [
          "**Bank statement** (recent, with name + address)",
          "**Utility bill** (gas, electric, water, cable, phone - recent)",
          "**Mail from a government agency** (Social Services, IRS, county clerk, etc.)",
          "**NJRC site address letter** - issued by the NJRC office when the participant is using the office as their mailing address",
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Use the NJRC address letter as your default fallback",
        text:
          "If the participant doesn't have a stable address or can't produce an acceptable document on short notice, the NJRC site can issue a letter confirming the office address. This is the standard workaround for housing-insecure participants - request from front desk.",
      },
    ],
  },

  // ─── EXPUNGEMENT · supplementary ──────────────────────────────────
  {
    id: "expungement-eligibility-chart",
    category: "expungement",
    title: "What does the NJ expungement eligibility chart actually say?",
    summary:
      "Every category, every waiting period, every limit. The full reference table for what a participant can (and can't) get expunged.",
    source: "LSM 2026 · Module 10 · NJ Expungement Eligibility Chart",
    estimatedTime: "8 min read",
    difficulty: "hard",
    keywords: ["expungement", "eligibility", "waiting period", "chart", "njsa"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "Use this as your reference card when triaging an expungement question. Waiting periods don't start until the *latest* of: end of incarceration, completion of probation/parole, or full payment of fines/restitution.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/expungement-eligibility-chart-page1.png",
        caption: "**Original NJ Expungement Eligibility Chart** (LSM 2026 / Module 10), page 1 - the official one-pager NJRC has used for years. The same data lives in the table below for quick scanning, but this is the artifact you'd hand a participant.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/expungement-eligibility-chart-page2.png",
        caption: "Eligibility chart, page 2 - extended notes on waiting-period start dates, crime-spree exceptions, and statute references.",
      },
      {
        kind: "table",
        heading: "Full eligibility chart",
        columns: ["Type of offense", "Eligibility limit", "Waiting period", "Statute"],
        rows: [
          ["Arrest not resulting in conviction (dismissal, acquittal, not guilty)", "No limit", "**None** - eligible immediately", "N.J.S.A. 2C:52-6"],
          ["PTI / Conditional Discharge / Conditional Dismissal", "No limit", "6 months from dismissal", "N.J.S.A. 2C:52-6"],
          ["Individual juvenile records", "Same as adult offenses", "Same as adult offenses by type", "N.J.S.A. 2C:52-4.1(a)"],
          ["Entire juvenile record", "Can expunge entire record", "**3 years**", "N.J.S.A. 2C:52-4.1(b)"],
          ["Municipal ordinance violations", "No limit if no felonies and ≤ 2 disorderly persons", "**2 years**", "N.J.S.A. 2C:52-4"],
          ["Disorderly persons (misdemeanor) convictions", "Up to 5 if no felonies; up to 3 if one felony", "**5 years** or **3 years** (early pathway)", "N.J.S.A. 2C:52-3"],
          ["Indictable (felony) conviction", "1 if ≤ 3 DP convictions; OR most recent if 2 felonies", "**5 years** or **4 years** (early pathway)", "N.J.S.A. 2C:52-2"],
          ["NJ Drug Court (Recovery Court) graduate", "No limit", "**None**", "N.J.S.A. 2C:35-14(m)"],
          ["Clean Slate Expungement", "No limit", "**10 years**", "N.J.S.A. 2C:52-5.3"],
          ["4th-degree felony marijuana + DP marijuana", "No limit", "**None** - eligible immediately", "N.J.S.A. 2C:52-5.1"],
          ["3rd-degree felony marijuana", "Treated as DP offense", "**3 years**", "N.J.S.A. 2C:52-5.1"],
          ["CDS possession/use convictions for young drug offenders (≤ 21)", "No limit if for possession/use", "**1 year**", "N.J.S.A. 2C:52-5"],
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "Waiting period clock starts at the LATEST of these",
        text:
          "Whichever happens last: completion of sentence including payment of fines/restitution, completion of probation/parole, or release from incarceration. Example: convicted April 2014, 1-year probation completed April 2015 → 5-year clock starts April 2015, not April 2014.",
      },
      {
        kind: "callout",
        tone: "info",
        title: "Crime spree exceptions",
        text:
          "Both DP and indictable categories have 'crime spree' exceptions - convictions arising from a single incident may count as one for eligibility purposes. Consult SDLS or refer to a pro bono attorney for these.",
      },
    ],
  },
  {
    id: "marijuana-decrim-expungement",
    category: "expungement",
    title: "How does Marijuana Decriminalization expungement work?",
    summary:
      "4th-degree and DP marijuana convictions are eligible *immediately* with no waiting period. NJ courts process these automatically - but verification is the LSC's job.",
    source: "LSM 2026 · Module 10 · Marijuana Decriminalization Guide",
    estimatedTime: "5 min read",
    difficulty: "medium",
    keywords: ["marijuana", "decriminalization", "expungement", "automatic", "vacate"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "Following NJ marijuana decriminalization (2021), certain past marijuana convictions and arrests are eligible for expungement **immediately** - no waiting period. The state has been processing many of these automatically, but you should verify on the participant's MCCS and PROMIS/Gavel.",
      },
      {
        kind: "checklist",
        heading: "Eligible marijuana matters",
        items: [
          "**4th-degree** indictable marijuana convictions",
          "**Disorderly persons** marijuana convictions",
          "**3rd-degree** marijuana convictions (treated as DP, 3-year wait)",
          "Certain related possession charges (under 50g, paraphernalia, etc.)",
        ],
      },
      {
        kind: "flowchart",
        heading: "Triage flow",
        nodes: [
          { tone: "start", title: "MCCS shows a marijuana case", body: "DP or 4th-degree felony charge codes around marijuana possession or distribution under thresholds." },
          { title: "Check the PROMIS/Gavel record", body: "Has the case been vacated automatically? Look for vacatur entries dated post-2021." },
          { title: "If still showing as a conviction → refer for expungement", body: "Confirm with SDLS; refer to pro bono attorney pool or LSNJ to formally file." },
          { tone: "end", title: "Confirm to the participant - and update Salesforce", body: "Many participants don't know their old marijuana charge has already been vacated. The verification + communication IS the LSC's deliverable." },
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Always check first",
        text:
          "Before pursuing a manual expungement filing for a marijuana matter, check whether the state already vacated it automatically. You'll save weeks of pro bono attorney time.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/marijuana-decrim-guide-page1.png",
        caption: "**NJRC Marijuana Decriminalization Expungement Training Guide** (Module 10, rev 03.17.23) - page 1. The internal one-pager NJRC built for triaging marijuana matters post-decrim.",
      },
    ],
  },
  {
    id: "clean-slate-expungement",
    category: "expungement",
    title: "What is Clean Slate expungement and who's eligible?",
    summary:
      "10-year window after which an entire NJ criminal record can be expunged in one petition. The biggest single tool for participants with extensive history.",
    source: "LSM 2026 · Module 10 · Clean Slate Guide · N.J.S.A. 2C:52-5.3",
    estimatedTime: "5 min read",
    difficulty: "medium",
    keywords: ["clean slate", "expungement", "10 years", "njsa 52 5.3"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "Clean Slate is NJ's answer to participants whose record is too extensive for the standard \"one indictable + 5 disorderly\" expungement formula. After **10 years** with no new convictions, the participant can petition to expunge their *entire* criminal record in one filing.",
      },
      {
        kind: "kv",
        heading: "Eligibility at a glance",
        items: [
          { label: "Statute", value: "N.J.S.A. 2C:52-5.3" },
          { label: "Waiting period", value: "**10 years** since the most recent conviction OR completion of sentence (whichever is later)" },
          { label: "Conviction-count limit", value: "**None** - that's the point of Clean Slate" },
          { label: "Excluded offenses", value: "Most violent crimes, sex offenses, public-corruption offenses are NOT eligible. Murder, kidnapping, robbery, sexual assault, etc." },
          { label: "What happens", value: "All eligible records (indictable + DP + ordinance) are expunged together in one petition" },
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "How to identify a Clean Slate candidate during a Legal Assessment",
        text:
          "If MCCS / PROMIS show a participant has multiple convictions but their **most recent conviction is 10+ years old**, flag for Clean Slate referral. This is dramatically simpler than filing multiple individual expungement petitions.",
      },
      {
        kind: "callout",
        tone: "warn",
        title: "Excluded offenses block the entire petition",
        text:
          "If the participant has *any* ineligible conviction in their record, Clean Slate is off the table - they need to use individual expungement petitions for the eligible parts. Consult SDLS to triage borderline cases.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Where to refer",
        text:
          "Clean Slate petitions are formally filed by attorneys. Refer to SDLS who will route to LSNJ, the Pro Bono Attorney Pool, or VLJ depending on case complexity.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/clean-slate-guide-page1.png",
        caption: "**NJRC Clean Slate Expungement Training Guide** (Module 10, rev 03.17.23) - the LSC-facing summary covering eligibility carve-outs, the 10-year clock, and which referrals work for which case patterns.",
      },
    ],
  },
  {
    id: "obtaining-criminal-record",
    category: "expungement",
    title: "How does the participant get a copy of their own criminal record?",
    summary:
      "Required for any expungement petition. State Police criminal-history check via fingerprinting; ~30-day turnaround.",
    source: "LSM 2026 · Module 10 · Obtaining Your Criminal Record",
    estimatedTime: "4 min read",
    difficulty: "medium",
    keywords: ["criminal record", "state police", "fingerprint", "expungement", "record list"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "Before filing any expungement petition, the participant needs an **official New Jersey State Police criminal-history record** (sometimes called a 'record list'). This is requested by the participant directly - fingerprint-based - and serves as the source-of-truth for all charges and dispositions in the petition.",
      },
      {
        kind: "steps",
        heading: "Process",
        items: [
          { title: "Visit njsp.gov/criminal-history-records", body: "Search 'NJ State Police criminal history record check' - there's an online ordering portal." },
          { title: "Choose the personal-record check option", body: "Confirm: this is the participant requesting their own record (not an employer background check)." },
          { title: "Pay the fee + schedule fingerprinting", body: "Fingerprinting is done at an IdentoGO location (state-contracted vendor). Fee runs ~$60." },
          { title: "Wait ~30 days for the record to arrive by mail", body: "Some records arrive faster; some take 6-8 weeks." },
          { title: "When it arrives, scan + upload to Salesforce", body: "Pass to the assigned pro bono attorney or LSNJ for the petition." },
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "The participant must request - not the LSC",
        text:
          "NJSP only releases criminal histories to the individual themselves. The LSC can't request a participant's record on their behalf, but the LSC CAN walk them through the portal and pay the fee from NJRC funds (with SDLS approval).",
      },
      {
        kind: "links",
        heading: "Resources & contacts",
        items: [
          { label: "NJ State Police criminal-history record check", href: "https://www.njsp.org/info/criminal-history-records.shtml", description: "Personal-record check option (not employer background check)." },
          { label: "IdentoGO fingerprint scheduling", href: "https://uenroll.identogo.com", description: "State-contracted fingerprint vendor - schedule + pay here." },
        ],
      },
    ],
  },

  {
    id: "time-served-when-databases-fail",
    category: "time-served",
    title: "What if PROMIS/Gavel + DOC + JOC all return nothing?",
    summary:
      "County jail contacts, probation/parole letters, and participant-direct records requests. The fallback chain when the public databases fail you.",
    source: "LSM 2026 · Module 4",
    estimatedTime: "3 min read",
    difficulty: "medium",
    keywords: ["time served", "fallback", "county jail", "probation officer", "records request"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "Sometimes a participant clearly did time but nothing comes up in the standard searches. Common causes: pre-2017 Superior Court conviction (JOC unavailable), released more than ~12 months ago (DOC may not show), or just old records that haven't been digitized.",
      },
      {
        kind: "flowchart",
        heading: "Fallback chain when public databases fail",
        nodes: [
          { tone: "start", title: "PROMIS/Gavel + DOC + JOC searched, nothing found" },
          { title: "Ask SDLS for the county-jail contacts spreadsheet", body: "SDLS maintains a list of county jail contacts and inmate-finder URLs (Hudson, Essex, Bergen, etc.)." },
          { title: "Try the relevant county's online inmate finder", body: "Each county jail has its own search tool - different from the state DOC." },
          { title: "If participant was on probation/parole, request a custody letter from their PO", body: "Courts will generally accept a probation officer's letter detailing custody dates." },
          { title: "Have the participant request their own records from the facility", body: "Many state prisons + federal facilities will only release records to the individual themselves. Coach the participant on the request." },
          { tone: "end", title: "Last resort: file the relief request with what you have + ask judge to consider", body: "Some judges will accept a sworn statement + corroboration." },
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "JOC search has a hard date cutoff",
        text:
          "The Judgment of Conviction Public Access Search **only returns judgments dated after June 30, 2017**. Anything earlier is invisible to JOC. If your participant's sentence is older than that, skip JOC and go straight to county jail or PO letter.",
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // META TOPICS - orientation, soft skills, edge cases, professional
  // judgment. Things every LSC needs to figure out but that aren't
  // procedural enough to live in the formal LSM. Built from the broader
  // context of what NJRC does + what real LSC work looks like.
  // ════════════════════════════════════════════════════════════════════

  // ─── ORIENTATION ──────────────────────────────────────────────────
  {
    id: "lsc-first-week",
    category: "getting-started",
    title: "What does my first week as an LSC look like?",
    summary:
      "Day-by-day map of your first week: who to meet, what to read, what to shadow, and what NOT to try yet.",
    source: "NJRC Onboarding · Field-tested",
    estimatedTime: "5 min read",
    difficulty: "easy",
    keywords: ["first week", "onboarding", "new", "orientation", "shadow", "training"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "Your first week as an NJRC Legal Services Coordinator should feel less like \"start producing\" and more like \"build the map.\" The work is broad - municipal courts, MVC, ID retrieval, expungement, working with five other staff roles per participant - and trying to do it all on day one is how new LSCs burn out by month two.",
      },
      {
        kind: "timeline",
        heading: "A reasonable first week",
        events: [
          { when: "Day 1 (Monday)", title: "Meet the team + tour the site", tone: "info", body: "Shadow your Site Director through morning operations. Meet the Intake Coordinator, Case Manager, Social Worker, and Employment Specialist. Get a quick demo of Salesforce - no need to master it yet." },
          { when: "Day 1-2", title: "Read the foundation topics", body: "[[what-does-an-lsc-do]] · [[the-njrc-program-flow]] · [[the-lsc-model-week]] · [[njrc-staff-roles]] · [[referrals-attorney-pool]] · [[what-njrc-pays-for]]. ~30 minutes total." },
          { when: "Day 2-3", title: "Shadow a senior LSC through 2-3 legal meetings", body: "Watch the rhythm: how they review the assessment with the participant, how they explain MVC results, how they set expectations on timing. Take notes on what you'd ask differently." },
          { when: "Day 3-4", title: "Run YOUR FIRST MCCS + MVC searches", tone: "tip", body: "Pick a low-stakes participant (one whose case has already been triaged by another LSC). Run their MCCS, restoration-fee, surcharge searches yourself. Compare your output to the senior LSC's. This is the single most important learning loop in week 1." },
          { when: "Day 4", title: "Prep your first Legal Assessment", body: "Pre-build for a Monday Orientation Week participant. Review with senior LSC before the legal meeting." },
          { when: "Day 5 (Friday)", title: "Run your first Legal Meeting (with senior LSC observing)", tone: "tip", body: "Walk through the assessment with the participant. The senior LSC observes - not to critique in real time, but to debrief afterward. The participant gets quality work; you get supervised practice." },
          { when: "Days 6-10", title: "Solo work, but with daily check-ins", tone: "warn", body: "By week 2 you can run legal meetings solo, but check in with SDLS daily for the first month. Cheap insurance against doing something irreversibly wrong on a participant's case." },
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "What NOT to try in week 1",
        text:
          "Don't draft court letters from scratch. Don't promise restoration timelines. Don't accept VitalChek requests independently. Don't start expungement filings. All of these have a SDLS-coordinated workflow - defer until you understand the workflow.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "By the end of month 1, you should be able to:",
        text:
          "Run a Legal Assessment end-to-end. Identify the right court letter type from MCCS. Coach a participant through the MVC Suspension Line call. Order an NJ birth certificate. Submit an SS-5. Hand off an expungement candidate to SDLS. If any of these still feel shaky after 30 days, raise it - that's a coaching moment, not a failure.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/orientation-week-worksheet.png",
        caption: "DCW LSC Orientation Week worksheet (LSM 2026 · LSC New Hire Packet) - the original framing of week-one expectations.",
      },
      {
        kind: "exhibit",
        type: "image",
        src: "screenshots/orientation-week-worksheet-page2.png",
        caption: "DCW LSC Orientation Week worksheet, page 2 - covering ongoing work + early-engagement expectations.",
      },
    ],
  },
  {
    id: "lsc-prioritization",
    category: "getting-started",
    title: "How do I prioritize my caseload when everything feels urgent?",
    summary:
      "Three buckets - Active Court Date, Blocking Employment, and Long-Tail Process. Triage by urgency, not loudness.",
    source: "NJRC Onboarding · Field-tested",
    estimatedTime: "4 min read",
    difficulty: "medium",
    keywords: ["prioritization", "caseload", "triage", "burnout", "queue"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "By month 3 every LSC has a queue that feels infinite. Active warrants, expungements, license restorations, BC orders, half-finished assessments - and a participant calling daily about a Spotify subscription they can't cancel. The job isn't to do everything; it's to do the right next thing. The triage rule below is what most senior LSCs converge on.",
      },
      {
        kind: "comparison",
        heading: "Three priority buckets",
        options: [
          {
            title: "Active Court Date",
            subtitle: "Same-week",
            tone: "warn",
            items: [
              "**Drop everything else.** Hearing this week → your work is the prep + coaching call",
              "Confirm court letter is on file (recall in 24 hours if not delivered)",
              "Call the participant, walk through what to expect",
              "Be available by phone the morning of the hearing",
            ],
          },
          {
            title: "Blocking Employment",
            subtitle: "This week",
            tone: "info",
            items: [
              "Participant has a job offer pending an ID, BC, SS card, or background check resolution",
              "Move it to the front. Restoration fees, ID requests, BC orders here",
              "Coordinate with the Employment Specialist - they may have a deadline you don't",
            ],
          },
          {
            title: "Long-Tail Process",
            subtitle: "Steady drip",
            tone: "tip",
            items: [
              "Court letters with no active hearing date",
              "Surcharge hardship payments (months-long process)",
              "Expungement referrals (years-long process)",
              "Salesforce hygiene / case-note backfill",
              "Block 1-2 hours/day for these. Don't let them eat your day, but don't let them rot either.",
            ],
          },
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Loudness ≠ urgency",
        text:
          "The participant calling you 4 times a day is rarely the most urgent case. Use Salesforce as your truth - open queue, sort by next-action-due-date, work the top. Don't let availability bias eat your week.",
      },
      {
        kind: "callout",
        tone: "warn",
        title: "When the queue is genuinely overflowing - flag it",
        text:
          "If you have more active court dates than you can prep for in a week, that's a real problem and SDLS needs to know. Either workload gets rebalanced, or SDLS helps you triage. Don't quietly drop balls - say so.",
      },
    ],
  },
  {
    id: "when-to-escalate-to-sdls",
    category: "getting-started",
    title: "When do I escalate to SDLS, and how?",
    summary:
      "Escalation is a feature of the role, not a failure. The five categories that ALWAYS go up; the friction-free way to send them.",
    source: "NJRC Onboarding · Field-tested",
    estimatedTime: "3 min read",
    difficulty: "easy",
    keywords: ["escalation", "sdls", "damon", "supervisor", "when to ask"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "Escalating to the Senior Director of Legal Services (SDLS) - Damon, in NJRC's case - isn't admitting failure. It's part of how the role works. Some categories of decision are explicitly above the LSC's pay grade, and trying to handle them solo is how things go wrong.",
      },
      {
        kind: "checklist",
        heading: "Always escalate",
        items: [
          "**Pro bono attorney referrals** - SDLS routes to LSNJ, VLJ, or NJRC's Pro Bono Pool",
          "**Restoration fee payment requests** - SDLS approves + processes through LSPM",
          "**VitalChek + PR/NYC + international BC requests** - SDLS handles the actual submission",
          "**A participant facing a Superior Court matter** - beyond LSC scope; needs attorney involvement",
          "**Anything that involves money** - surcharge waivers, fee waivers, anything beyond NJRC's standard funding",
          "**Anything you genuinely don't know** - better to ask once than guess and fix it later",
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "How to escalate well",
        text:
          "Lead with the **specific question** + **what you've already checked**. Don't email \"how do I help John?\" - email \"John has 2 active warrants in Newark + Paterson, no time-served, MCCS shows fines totaling $1,247. He has a hearing in Newark on the 15th. I can't find a precedent for whether to seek warrant recall AND fine waiver in the same letter - what's the right path?\"",
      },
      {
        kind: "callout",
        tone: "info",
        title: "When in doubt, call",
        text:
          "Damon would rather take a 90-second call than read a 4-paragraph email. Use email for documentation; use the phone for ambiguity.",
      },
    ],
  },

  // ─── EDGE-CASE PARTICIPANTS ───────────────────────────────────────
  {
    id: "participant-undocumented",
    category: "getting-started",
    title: "What if my participant is undocumented?",
    summary:
      "Scope is narrower but real. ID retrieval mostly off the table; some court work still possible. When to refer.",
    source: "NJRC Field-tested",
    estimatedTime: "4 min read",
    difficulty: "hard",
    keywords: ["undocumented", "immigration", "scope", "no ssn", "consulate"],
    blocks: [
      {
        kind: "callout",
        tone: "danger",
        title: "Immigration legal advice is OUT of scope",
        text:
          "NJRC LSCs do not provide immigration advice. If a participant asks about deportation defense, asylum, work authorization, DACA, or any immigration-status question, the answer is **referral to an immigration legal-services provider** - not advice from us. Common referrals: American Friends Service Committee, Make the Road NJ, Catholic Charities. Coordinate with SDLS.",
      },
      {
        kind: "comparison",
        heading: "What CAN we still help with?",
        options: [
          {
            title: "Yes - still in scope",
            tone: "tip",
            items: [
              "**Foreign birth certificate retrieval** - international BC picker still works",
              "**Foreign-country consulate work** - SDLS coordinates",
              "**Municipal court matters** under aliases or the participant's legal name (warrants, fines)",
              "**MVC matters** if the participant has an NJ ID/DL number on file (regardless of immigration status)",
              "**Documents like school records, baptism records, foreign-country docs** that establish identity",
            ],
          },
          {
            title: "No - limited or off-table",
            tone: "warn",
            items: [
              "**SS card** - requires SSN; cannot apply without one",
              "**NJ State ID** - requires SSN OR Individual Taxpayer ID; verify current MVC policy with SDLS",
              "**Federal background-check related work** - many federal touch-points require legal status",
              "**Anything that touches USCIS, ICE, or federal immigration courts** - outside NJRC LSC scope",
            ],
          },
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "Confidentiality matters here more than usual",
        text:
          "Don't ask about immigration status unless the participant volunteers it AND it's necessary to determine what we can help with. Salesforce should NOT contain immigration status notes - that's PII that could create real risk if subpoenaed. Document only what's strictly needed for the legal-services work.",
      },
    ],
  },
  {
    id: "participant-currently-incarcerated",
    category: "getting-started",
    title: "What if my participant is currently incarcerated?",
    summary:
      "Mail-based work only. Can do BC + SS prep + record retrieval; cannot do most MVC or hearings until release.",
    source: "NJRC Field-tested",
    estimatedTime: "3 min read",
    difficulty: "medium",
    keywords: ["incarcerated", "in custody", "mail", "pre-release", "doc"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "NJRC's primary scope is post-release reentry, but pre-release prep work happens regularly - usually for participants approaching release dates or those whose families have reached out on their behalf. Communication is by mail; turnaround is slow; the LSC role is preparing the runway, not running it.",
      },
      {
        kind: "checklist",
        heading: "What CAN you do for an incarcerated participant?",
        items: [
          "**Run their MCCS** - confirms outstanding warrants/fines so they can hit the ground knowing what's there",
          "**Run PROMIS/Gavel + DOC + JOC** - gather time-served documentation for letters once released",
          "**Mail the participant a Legal Assessment summary + checklist** - gives them something to work with on release day",
          "**Pre-prepare BC paperwork** - they sign upon release",
          "**Coordinate with the facility** for record requests (some facilities only release records to the inmate)",
          "**Connect family members** to NJRC's Case Manager for benefits + housing prep",
        ],
      },
      {
        kind: "checklist",
        heading: "What you CAN'T meaningfully do until release",
        items: [
          "MVC work (no in-person appointment possible from inside)",
          "SS card application (requires in-person SSA visit)",
          "Notarization of forms (notary requires in-person signature)",
          "Court letters (most courts won't process until participant is at liberty to appear)",
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Family + facility caseworker are your allies",
        text:
          "If the participant has family contact, route document requests through the family. Facility caseworkers (state prison, county jail) can sometimes help with record requests + verification. Coordinate via SDLS - there's protocol for these contacts.",
      },
    ],
  },
  {
    id: "participant-out-of-state-issues",
    category: "getting-started",
    title: "What if my participant has out-of-state legal or MVC issues?",
    summary:
      "Mostly out of scope. We help triage + refer; we don't litigate other states. NJ MVC's Driver License Compact is the one exception worth knowing.",
    source: "NJRC Field-tested",
    estimatedTime: "3 min read",
    difficulty: "medium",
    keywords: ["out of state", "interstate", "compact", "scope"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "Participants frequently have legal exposure in other states - old DUIs in PA, unpaid tickets in NY, child-support warrants in FL. NJRC's scope is **NJ-focused**: NJ municipal courts, NJ MVC, NJ-issued IDs. We don't litigate or correspond with out-of-state courts.",
      },
      {
        kind: "callout",
        tone: "warn",
        title: "What we DO help with",
        text:
          "**Triage** - running searches in other-state public databases when possible (some are public, some aren't). **Referral** - pointing the participant at legal-aid organizations in the relevant state (LSNJ has an interstate referral list; SDLS has it). **Documentation** - gathering NJ-side records the participant might need to bring to an out-of-state proceeding.",
      },
      {
        kind: "callout",
        tone: "info",
        title: "Driver License Compact - the one cross-state lever",
        text:
          "NJ is part of the Driver License Compact. An out-of-state DL suspension WILL block NJ license restoration. If MVC's Suspension Line tells the participant about an out-of-state hold, the path is: resolve the issue in the originating state first, then return to NJ for restoration. NJRC can help them identify what state + what type of hold; we can't make it go away.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Don't try to be a generalist 50-state lawyer",
        text:
          "Be honest with the participant: \"I can help with the NJ side. For the [state] case, you'll need [referral]. Here's what to ask them.\" That's the right answer - not improvising your way through unfamiliar law.",
      },
    ],
  },
  {
    id: "participant-disputes-records",
    category: "getting-started",
    title: "What if the participant says the records I find aren't theirs?",
    summary:
      "Maybe right (mistaken identity) or maybe denial. Verify methodically - DOB, SBI, ID#, prior aliases. Don't argue; investigate.",
    source: "NJRC Field-tested",
    estimatedTime: "3 min read",
    difficulty: "medium",
    keywords: ["disputed", "wrong person", "mistaken identity", "verify", "alias"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "Eventually, every LSC hits this: you pull a participant's MCCS / PROMIS results and they say \"that's not me.\" Two possibilities: (1) they're right (common name, mistaken identity, MVC database glitch) or (2) they're not (forgotten case, denial, or hoping you'll go away). Both possibilities deserve respect - investigate, don't accuse.",
      },
      {
        kind: "flowchart",
        heading: "Verification chain",
        nodes: [
          { tone: "start", title: "Participant says \"that's not me\"", body: "Don't argue. Don't agree. Just investigate." },
          { title: "Verify DOB matches exactly", body: "Most common cause of false hits: shared name + nearby DOB. If the DOB on the record doesn't match the participant's, they're probably right." },
          { title: "Check SBI #", body: "If a PROMIS or DOC record has an SBI, it's tied to a specific person via fingerprint. Compare to any SBI on the participant's prior NJRC records." },
          { title: "Check ID/DL #", body: "MCCS records sometimes carry the participant's DL number. If the number on the record doesn't match the participant's actual MVC DL, it's not them." },
          { title: "Check prior aliases", body: "Participants legally change names (marriage, gender confirmation, etc.). Search under prior names too." },
          { title: "If still ambiguous → talk to SDLS", body: "Sometimes the only way to resolve is to request the underlying paper file from the court. SDLS coordinates." },
          { tone: "end", title: "Document the verification in Salesforce", body: "Even if the record IS the participant's, you've now created a record of having verified it. If the question comes up again you don't have to redo." },
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Tone matters",
        text:
          "\"This says you have a 2018 disorderly persons case in Camden - the DOB matches yours. Help me understand if this is your record or someone with the same name.\" That's much better than \"are you lying to me?\" Same investigative path, different relationship.",
      },
    ],
  },
  {
    id: "participant-relapses",
    category: "getting-started",
    title: "What if my participant relapses or stops engaging?",
    summary:
      "Don't take it personally. Pause active work, document in Salesforce, leave the door open. They often come back.",
    source: "NJRC Field-tested",
    estimatedTime: "3 min read",
    difficulty: "medium",
    keywords: ["relapse", "disengaged", "no contact", "ghost", "re-engagement"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "Reentry isn't linear. Many participants disengage - sometimes for weeks, sometimes for months, sometimes after a relapse, sometimes for reasons you'll never know. The LSC's job in that moment isn't to chase or judge; it's to leave the work in a state they can resume from.",
      },
      {
        kind: "steps",
        heading: "When a participant goes quiet",
        items: [
          { title: "Try to reach them - twice, then stop", body: "One call/text within a week, one follow-up in another week. After that, stop pinging. Persistent contact at this point can feel like surveillance." },
          { title: "Pause active work that requires their input", body: "Don't submit a VitalChek request that needs them to take a security-questions call. Don't mail a court letter that needs their signature." },
          { title: "Continue work that doesn't need them", body: "Court letters already in flight can complete. Restoration fees you've already submitted can pay. Documents already received can be filed." },
          { title: "Document the disengagement in Salesforce", body: "\"[Date] - Participant disengaged after relapse. Paused open: [list]. Available items in their folder: [list]. Will resume on re-engagement.\"" },
          { title: "Coordinate with the Case Manager + Social Worker", body: "They may have re-engagement strategies. Sometimes a Case Manager hears from family before you hear from the participant." },
          { title: "Leave the door open", body: "When they come back - and they often do - you want them to feel welcome. Don't make them re-explain. Pick up where you left off." },
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "This is normal. It's not your fault.",
        text:
          "Relapse and disengagement are part of reentry, not signs that the LSC failed. The data on this is unambiguous. Take it personally and you'll burn out by year 2.",
      },
    ],
  },

  // ─── COMMUNICATION / SOFT SKILLS ──────────────────────────────────
  {
    id: "follow-up-with-court-administrator",
    category: "court-letters",
    title: "How do I follow up with a Court Administrator without becoming annoying?",
    summary:
      "21 days, then call. Polite, specific, and never demanding. Court Administrators have long memories - for both kinds of LSC.",
    source: "NJRC Field-tested",
    estimatedTime: "3 min read",
    difficulty: "medium",
    keywords: ["follow up", "court administrator", "etiquette", "phone call", "no response"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "After sending a court letter, most courts respond within 2-3 weeks. If you don't hear back in 21 days, follow up. The follow-up is what makes you effective - but the wrong kind of follow-up gets you flagged as \"that LSC.\" Court Administrators talk to each other across municipalities. They notice.",
      },
      {
        kind: "comparison",
        heading: "Good vs. bad follow-up",
        options: [
          {
            title: "Good follow-up",
            tone: "tip",
            items: [
              "**Polite + specific**: \"Hi, this is [name] from NJRC. I sent a warrant-recall request for [participant] on [date], case # [X]. I wanted to confirm you received it and check on the status.\"",
              "**Asks for status, not action**: not \"are you going to grant this?\" - just \"can you tell me where it is in the process?\"",
              "**Patient**: \"Whenever you have a chance to look - I appreciate it.\"",
              "**Short**: 90 seconds tops",
            ],
          },
          {
            title: "Bad follow-up",
            tone: "warn",
            items: [
              "**Demanding**: \"It's been 21 days, I need an answer\"",
              "**Pressuring**: \"My participant is panicking, they need this resolved\"",
              "**Long**: re-explaining the whole case from scratch",
              "**Too frequent**: more than once every 2 weeks for the same matter",
              "**Going around them**: calling the judge directly - never do this",
            ],
          },
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "Voicemail is fine - keep it crisp",
        text:
          "\"Hi, this is [name] from NJRC, calling about [participant], case # [X]. Just checking in on a warrant-recall request from [date]. My number is [X]. Thanks so much, no rush.\" That's the entire voicemail. Don't editorialize.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "When the Court Administrator helps you well - say so",
        text:
          "A 30-second \"Thank you so much, that really helped my participant\" email after a recall is granted goes a long way. Court Admins remember LSCs who treat them as humans. Your future participants benefit from your reputation today.",
      },
    ],
  },
  {
    id: "say-no-with-respect",
    category: "getting-started",
    title: "How do I tell a participant 'no, NJRC can't help with that'?",
    summary:
      "Most participants accept a clean 'no' + a useful redirect. The trap is fudging - saying maybe when you mean no.",
    source: "NJRC Field-tested",
    estimatedTime: "2 min read",
    difficulty: "medium",
    keywords: ["scope", "decline", "say no", "redirect", "out of scope"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "Participants ask for things outside NJRC scope all the time - help with a custody case, help fighting a landlord, help with a federal matter, help getting an out-of-state job. The instinct is to soften: \"let me see what I can do.\" That's the trap. The participant hears \"yes, working on it\" and waits. Three weeks later, they're frustrated and trust is damaged. Better to say no clearly + redirect well, on day one.",
      },
      {
        kind: "kv",
        heading: "Templates for honest no",
        items: [
          { label: "Family / custody", value: "\"That's a family-court matter - outside what I handle. The right resource is [Legal Services of NJ family unit / VLJ]. I can give you their intake number.\"" },
          { label: "Landlord / housing", value: "\"Housing law isn't my area. I'd send you to LSNJ's housing unit - they're free if you qualify.\"" },
          { label: "Federal case", value: "\"I work municipal-court matters in NJ. Federal cases need a federal defender or specialized attorney - let me ask SDLS for a referral.\"" },
          { label: "Out-of-state issue", value: "\"I can help with the NJ side. For the [state] case, you'll need a legal-aid org in that state. I'll text you a contact.\"" },
          { label: "Immigration", value: "\"NJRC doesn't do immigration. The folks I'd send you to are [Make the Road / AFSC / Catholic Charities] - they're set up for this.\"" },
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Always pair the no with a referral",
        text:
          "\"No\" alone feels dismissive. \"No, but here's who can\" feels like help. The participant leaves with somewhere to go, even if it's not us.",
      },
    ],
  },
  {
    id: "conduct-effective-legal-meeting",
    category: "legal-assessment",
    title: "How do I run an effective Legal Meeting?",
    summary:
      "30-45 minutes. Participant-led where possible. Confirm > teach > plan > document. Don't lecture, don't rush.",
    source: "NJRC Field-tested",
    estimatedTime: "5 min read",
    difficulty: "medium",
    keywords: ["legal meeting", "1on1", "intake", "structure", "conduct"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "The Legal Meeting is the foundation of the LSC's relationship with each participant. The first one usually happens during Orientation Week and runs 30-45 minutes. Subsequent ones are shorter - 15-20 minutes is typical for follow-ups. The structure below is what works; deviations are fine but the elements should all be there.",
      },
      {
        kind: "flowchart",
        heading: "Structure of a first Legal Meeting",
        nodes: [
          { tone: "start", title: "Open with introduction + purpose", body: "\"Hi, I'm [name], the Legal Services Coordinator here. My job is to help you with anything legal that's standing between you and reentry - court stuff, MVC stuff, getting your IDs back. Today we're going to walk through what I've already found, then make a plan.\"" },
          { title: "Confirm what you found", body: "Walk through MCCS results, restoration-fee, surcharge findings. Let the participant correct anything wrong. \"Does this match what you remember?\"" },
          { title: "Explain implications, briefly", body: "\"You have one warrant in Newark. That means if you ever get pulled over, this could come back. We'll send a letter to recall it.\" - short, plain English." },
          { title: "Make a plan together", body: "List 3-5 specific actions + who does what + by when. \"This week I'll do X. Next week I need you to call Y.\" Write it down for them." },
          { title: "Confirm contact + set follow-up", body: "Best phone, best time to call, how often you'll check in." },
          { title: "Hand them their copy of the Assessment + plan", body: "Physical paper, even in 2026. Many participants don't have stable email." },
          { tone: "end", title: "Document everything in Salesforce immediately", body: "Don't wait until end of day. Memory degrades fast." },
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "Don't lecture",
        text:
          "Many participants have heard \"this is what you need to do\" from many people. Your job is to get THEM moving on what's possible - not to deliver a lesson on legal process. Speak with them, not at them.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Take notes during, not after",
        text:
          "Write down what they tell you in real time. Shows you're listening. Saves you from misremembering. The participant sees you treating their information seriously.",
      },
    ],
  },

  // ─── SUSTAINABILITY ───────────────────────────────────────────────
  {
    id: "lsc-burnout-prevention",
    category: "getting-started",
    title: "How do I avoid LSC burnout?",
    summary:
      "Reentry work has high emotional load. Three habits that separate the LSCs who last from the ones who don't.",
    source: "NJRC Field-tested",
    estimatedTime: "4 min read",
    difficulty: "medium",
    keywords: ["burnout", "self care", "sustainability", "boundaries", "career"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "LSC turnover is a real problem in reentry organizations broadly - not just at NJRC. The work has high emotional load (you hear hard stories every day), unclear wins (most participant outcomes are partial), and a queue that never empties. Lasting in this role is a learnable skill.",
      },
      {
        kind: "comparison",
        heading: "Three habits that separate lasting LSCs from burning-out ones",
        options: [
          {
            title: "1. Hard end-of-day",
            subtitle: "Boundary",
            tone: "tip",
            items: [
              "When you leave the office, you LEAVE the office",
              "Don't check work texts after 7 PM",
              "Don't take participant calls on weekends unless it's a literal court emergency",
              "Burnout starts when work bleeds into recovery time",
            ],
          },
          {
            title: "2. Wins journal",
            subtitle: "Memory",
            tone: "tip",
            items: [
              "Keep a private list of every breakthrough - recall granted, license restored, BC delivered, expungement filed",
              "Look at it on hard days",
              "The brain weights losses 5x more than wins; the journal corrects for that",
            ],
          },
          {
            title: "3. Peer + supervisor talk",
            subtitle: "Process",
            tone: "tip",
            items: [
              "Hard cases need to go somewhere - don't carry them home alone",
              "Talk to other LSCs (peer support is high-EV)",
              "Talk to SDLS when something's stuck inside you",
              "Use NJRC's Social Worker as a resource - they know more about secondary trauma than you do",
            ],
          },
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "Warning signs to take seriously",
        text:
          "If you notice yourself: dreading specific participants, avoiding certain workflows, feeling cynical about reentry as a project, sleeping poorly, or numb during legal meetings - that's burnout signaling. Talk to SDLS. Don't power through it; that's how careers end.",
      },
      {
        kind: "callout",
        tone: "info",
        title: "It's not weakness - it's the work",
        text:
          "Other LSCs feel this too. The Social Worker on your team has formal training in vicarious trauma. The longer you stay in the role, the more you'll value protecting your own bandwidth - that's professionalism, not selfishness.",
      },
    ],
  },
  {
    id: "boundaries-with-participants",
    category: "getting-started",
    title: "How do I maintain professional boundaries with participants?",
    summary:
      "Warm, not friends. Five concrete rules to keep the relationship effective AND safe for both sides.",
    source: "NJRC Field-tested",
    estimatedTime: "3 min read",
    difficulty: "medium",
    keywords: ["boundaries", "professional", "ethics", "personal phone"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "The right relationship between LSC and participant is **warm but professional** - closer than a bureaucrat, more bounded than a friend. The wrong end (cold, transactional) makes work harder; the other wrong end (over-personal) creates risks for both you and the participant.",
      },
      {
        kind: "checklist",
        heading: "Concrete boundary rules",
        items: [
          "**Use NJRC phone/email for participant contact** - not your personal cell. If you must give a personal number temporarily, switch to office channels as soon as possible.",
          "**Don't loan the participant money**, give them rides outside official program activities, or invite them to personal events.",
          "**Don't accept gifts** beyond minor courtesies (a thank-you note, a shared snack at the office). If a participant offers anything more substantial, decline warmly.",
          "**Don't connect on personal social media**. LinkedIn for professional contacts after they leave the program is fine; everything else, no.",
          "**Don't share your home address** or specifics about your family.",
          "**If a participant develops feelings or makes you uncomfortable**, tell SDLS immediately. The boundary is yours to set; the support is from leadership.",
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "Boundaries protect THEM too",
        text:
          "An LSC who blurs the professional line creates a dynamic where the participant can't trust the relationship. Are you helping them because they're a friend? Because of who they are? The answer should always be: because that's the work. Boundaries are what makes that clear.",
      },
    ],
  },

  // ─── DOCUMENTATION ────────────────────────────────────────────────
  {
    id: "close-case-in-salesforce",
    category: "operations",
    title: "How do I close out a case in Salesforce?",
    summary:
      "Five-line case-close note. Status: resolved. Outcomes: itemized. So future LSCs reading the file know what happened.",
    source: "NJRC Field-tested",
    estimatedTime: "2 min read",
    difficulty: "easy",
    keywords: ["salesforce", "close case", "wrap up", "resolution", "case close"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "When a participant's legal-services work is complete (or the participant leaves the program), close the case in Salesforce cleanly. The next LSC who looks at this file 18 months from now will thank you.",
      },
      {
        kind: "kv",
        heading: "What goes in a case-close note",
        items: [
          { label: "Date", value: "When the case is being closed" },
          { label: "Reason", value: "Resolved · Participant disengaged · Transferred · Out of scope (referred to X)" },
          { label: "Outcomes delivered", value: "Itemized list - \"NJ DL restored, NJ BC obtained, 2 warrants recalled, 1 marijuana matter referred for expungement\"" },
          { label: "Outstanding items", value: "Anything not resolved + why - \"Surcharge balance ongoing per payment plan; no LSC action needed\"" },
          { label: "Next-step trigger", value: "What would re-open this case - \"Re-engage if participant returns + asks about expungement filing\"" },
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Don't delete or hide closed records",
        text:
          "Closed cases stay in Salesforce. Future re-engagement is common; the record is institutional memory. The only thing that should change is the status flag.",
      },
    ],
  },
  {
    id: "document-refused-service",
    category: "operations",
    title: "How do I document a service the participant refused?",
    summary:
      "Document the offer, the refusal, the reason (if given), and the date. Close the loop without judgment.",
    source: "NJRC Field-tested",
    estimatedTime: "2 min read",
    difficulty: "easy",
    keywords: ["refused", "declined", "documentation", "case note", "salesforce"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "Sometimes a participant declines a service we offer - \"I don't want to do an expungement,\" \"I don't want a court letter sent,\" \"I'd rather pay the fine myself.\" That's their right. Your job is to document it cleanly so it's not mistakenly re-offered later.",
      },
      {
        kind: "kv",
        heading: "Refusal Case Note format",
        items: [
          { label: "Date", value: "When the refusal happened" },
          { label: "Service offered", value: "Specific - \"warrant-recall letter for case # X\"" },
          { label: "Participant's response", value: "Their words if possible - \"Says he wants to handle it himself when he's back in court\"" },
          { label: "Reason (if shared)", value: "If the participant explained, capture it neutrally" },
          { label: "Status", value: "Service deferred at participant request - re-offer if circumstances change" },
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "No judgment in the note",
        text:
          "\"Participant refused to follow advice\" is a bad note. \"Participant chose to handle it independently; said he'd update LSC if circumstances change\" is a good note. Same fact, different tone - and other LSCs reading the file form their own impression based on what you wrote.",
      },
    ],
  },

  // ─── PURPOSE / WHY ────────────────────────────────────────────────
  {
    id: "why-njrc-does-legal-services",
    category: "getting-started",
    title: "Why does NJRC do legal services at all?",
    summary:
      "Because legal barriers (warrants, suspended licenses, missing IDs) block employment, housing, and stability. Reentry without legal-services support has measurably worse outcomes.",
    source: "NJRC mission · published research",
    estimatedTime: "4 min read",
    difficulty: "easy",
    keywords: ["mission", "why", "purpose", "reentry", "research", "outcomes"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "NJRC's mission is removing barriers to employment for people returning from prison, jail, addiction treatment, and combat. That mission has many fronts - workforce training, mental and behavioral health, peer support, employment placement. Legal services is one specific front: clearing the legal obstacles that, left in place, would block everything else.",
      },
      {
        kind: "paragraph",
        text:
          "If a returning participant has an outstanding municipal-court warrant, they get pulled over and they're back in custody. If their license is suspended, they can't drive to a job in suburban NJ. If they don't have a state ID, they can't be hired, open a bank account, or apply for benefits. If they don't have a birth certificate, they can't get the state ID. If they have unresolved fines, NJ MVC blocks license restoration. Each one of these is a small thing that, in combination, prevents reentry from working.",
      },
      {
        kind: "callout",
        tone: "info",
        title: "The legal-services LSC sits exactly at this bottleneck",
        text:
          "Court letters, MVC searches, ID retrieval, expungements - these are unglamorous workflows that, when done well, are the difference between a participant getting a job and a participant being incarcerated again 6 months from release. The work is the impact.",
      },
      {
        kind: "paragraph",
        text:
          "NJRC has been tracking outcomes for over a decade. Participants who fully engage with legal services (legal assessment + court matters resolved + ID retrieval) have substantially lower recidivism and higher employment retention than participants who don't. The mechanism is exactly what you'd expect: fewer reasons to be re-arrested, more eligibility to actually take the job.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "When the work feels small, look at the chain",
        text:
          "An MCCS that takes you 10 minutes can surface a warrant that, recalled with a 30-minute letter, prevents a re-arrest. The re-arrest doesn't show up in your queue because it doesn't happen. That's the impact, and it's invisible by design - but it's why this role matters.",
      },
    ],
  },

  // ─── HEARING-DAY EXTRA ─────────────────────────────────────────────
  {
    id: "leave-effective-voicemail",
    category: "court-letters",
    title: "How do I leave an effective voicemail for a Court Administrator?",
    summary:
      "Three sentences max. Identify, ask, leave callback. Don't editorialize.",
    source: "NJRC Field-tested",
    estimatedTime: "2 min read",
    difficulty: "easy",
    keywords: ["voicemail", "phone", "court administrator", "follow up"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "Court Administrators get a lot of voicemails. The ones they return are short, specific, and easy to act on.",
      },
      {
        kind: "kv",
        heading: "Template (memorize)",
        items: [
          { label: "Sentence 1", value: "\"Hi, this is [your name] from NJRC, calling about case number [X] for [participant's name].\"" },
          { label: "Sentence 2", value: "\"I sent a [letter type] on [date], and I wanted to check on the status whenever you have a chance.\"" },
          { label: "Sentence 3", value: "\"You can reach me at [phone number] - and again, that's [phone number]. Thank you so much.\"" },
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "Don't",
        text:
          "Don't restate the case from scratch. Don't apologize for calling. Don't add \"my participant is really stressed about this.\" Don't leave a number once - leave it twice (people miss it the first time).",
      },
    ],
  },

  // ─── SCOPE EXPANSIONS ─────────────────────────────────────────────
  {
    id: "work-with-public-defender",
    category: "getting-started",
    title: "How do I coordinate with a participant's Public Defender?",
    summary:
      "PDs are overworked and don't know NJRC exists. Be useful, not intrusive. Bring documents, not requests.",
    source: "NJRC Field-tested",
    estimatedTime: "3 min read",
    difficulty: "medium",
    keywords: ["public defender", "pd", "coordination", "criminal", "attorney"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "When a participant has an active criminal matter (Superior Court, indictable charges), they have a Public Defender. NJRC doesn't replace the PD - but you can make their job easier, which makes the participant's outcome better. The PD usually doesn't know who NJRC is until you reach out.",
      },
      {
        kind: "checklist",
        heading: "Reaching out - be useful",
        items: [
          "**Introduce NJRC** in one sentence: \"NJRC is a reentry nonprofit; I'm the Legal Services Coordinator working with [participant].\"",
          "**Offer documents** they might want: prior MCCS, time-served documentation (DOC, PROMIS/Gavel), drug-program completion certificates, employment-readiness verification",
          "**Ask if there's anything you can help gather** - birth certificate retrieval often comes up if the PD needs ID for a hearing",
          "**Don't ask the PD to do anything** - you're offering, not requesting",
          "**Send via email** with PDF attachments. PDs read email between hearings; phone calls are harder to fit in",
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "When the PD does engage with you - it's gold",
        text:
          "A PD who knows NJRC exists will often ask for help on their next reentry-track defendant. That's a referral pipeline that benefits future participants. Take it seriously; respond fast; deliver clean work.",
      },
      {
        kind: "callout",
        tone: "warn",
        title: "Don't substitute legal advice",
        text:
          "You don't tell the PD what plea to take. You don't tell the participant what to plead. Legal-strategy decisions on a criminal matter are between the participant and their attorney - full stop.",
      },
    ],
  },
  {
    id: "work-with-probation-officer",
    category: "getting-started",
    title: "How do I coordinate with a participant's Probation or Parole Officer?",
    summary:
      "POs are gatekeepers. Treating them as allies (not obstacles) makes the participant's life easier.",
    source: "NJRC Field-tested",
    estimatedTime: "3 min read",
    difficulty: "medium",
    keywords: ["probation", "parole", "po", "coordination", "supervision"],
    blocks: [
      {
        kind: "paragraph",
        text:
          "Many NJRC participants are under active probation or parole supervision. The Probation/Parole Officer (PO) has real authority over the participant's day-to-day - curfew, employment requirements, drug testing, travel restrictions. Coordinating with the PO can unlock things you can't do alone.",
      },
      {
        kind: "checklist",
        heading: "Common reasons to reach out",
        items: [
          "**Custody-letter request** for time-served calculations (POs can write a letter detailing custody dates)",
          "**Coordinate court appearances** - PO needs to know the participant has a court date so it's not flagged as an unauthorized absence",
          "**Verify employment** for restoration-fee waivers or hardship submissions",
          "**Notify the PO of NJRC's involvement** - at intake, NJRC's role is rarely communicated; sometimes the PO is hostile to a new player without context",
        ],
      },
      {
        kind: "kv",
        heading: "Tone of the outreach",
        items: [
          { label: "Open with role + purpose", value: "\"Hi, this is [name] from the New Jersey Reentry Corporation. I'm the Legal Services Coordinator working with [participant]. They're enrolled in our reentry program.\"" },
          { label: "State the specific ask", value: "Concrete + bounded. Not \"can you tell me about him?\" - instead \"can you confirm the dates [participant] was in custody at [facility] in 2022?\"" },
          { label: "Offer reciprocal info", value: "\"Happy to share NJRC's program engagement notes if useful for your reporting.\" Many POs will appreciate this." },
        ],
      },
      {
        kind: "callout",
        tone: "warn",
        title: "Confidentiality calibration",
        text:
          "Don't share substance-use, mental-health, or sensitive personal information with the PO unless the participant has explicitly consented. Coordinate with the Social Worker - they know what's appropriate to share + when consent is needed.",
      },
    ],
  },
];
